import { getStoredToken } from './auth';
import { getViewAs } from './viewAs';

// ── X-View-As ────────────────────────────────────────────────────────────────
// docs/product/VIEW-AS-TOGGLE.md "Client" bullet 2. The founder view-as preview
// used to be purely client-rendered: the API received a plain founder token and
// had no idea a preview was happening, so writes made during a customer demo
// executed for real. This header is how the server is TOLD — without it the
// server-side read-only gate (src/shared/viewAs.ts) can never fire.
//
// Attached HERE, in the one central wrapper, rather than at call-sites: the
// gate's guarantee is only as good as its coverage, and a per-call opt-in would
// mean the one write someone forgot to tag is the one that lands for real.
//
// Wire format matches the server's parser exactly: `role=<role>` plus an
// optional `;org=<orgId>`. The org NAME is deliberately never sent — it is free
// text (spaces, punctuation, non-ASCII) and therefore header-unsafe, and the
// server keys on the id anyway.
//
// Non-founders never have a view-as session (the switcher only mounts behind
// `me.role === 'founder'`), so for every real user this is a no-op that adds
// nothing to the request.
function viewAsHeader(): Record<string, string> {
  try {
    const viewAs = getViewAs();
    if (!viewAs?.role) return {};
    const orgId = viewAs.org?.orgId;
    return { 'X-View-As': orgId ? `role=${viewAs.role};org=${orgId}` : `role=${viewAs.role}` };
  } catch {
    // getViewAs already swallows storage faults, but a header helper must
    // never be the reason an API call fails to leave the browser.
    return {};
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(`/portal${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...viewAsHeader(),
      ...options?.headers,
    },
  });
  const contentType = res.headers.get('content-type') || '';
  const rawBody = await res.text();
  const body = rawBody
    ? contentType.includes('application/json')
      ? JSON.parse(rawBody)
      : rawBody
    : null;

  if (!res.ok) {
    const errorBody = (body && typeof body === 'object') ? body as { error?: string } : null;
    throw new Error(errorBody?.error ? `${errorBody.error} (HTTP ${res.status})` : `${res.status} ${res.statusText}`);
  }

  // An HTML body on a 2xx means CloudFront served the SPA shell instead of the API
  // (origin/session error). Surface that as the real reason rather than letting a
  // non-object body trip a downstream "incomplete response" guard.
  if (contentType.includes('text/html')) {
    throw new Error(`Server returned an HTML page (HTTP ${res.status}) instead of data — your session may have dropped. Refresh and sign in again.`);
  }

  return body as T;
}

// ── Structured error parsing ──────────────────────────────────────────────────
// Several portalApi handlers (role-requests, org-governance proposals, seat
// caps) call `fail(status, JSON.stringify({ error: code, message, ...extra }))`
// to carry a machine-readable error code alongside a human message. `fail()`
// itself wraps that string in `{ error: <string> }`, and apiFetch's throw
// appends " (HTTP <status>)" to the message — so the raw thrown message looks
// like `{"error":"sole_owner","message":"..."} (HTTP 400)`, NOT valid JSON on
// its own. Strip the HTTP suffix before parsing (a prior helper —
// RoleRequestForm's parseRateLimitError — skips this strip and silently falls
// back to 'unknown' on every real call; don't copy that). Plain-string
// fail(status, "...") errors (no JSON) fall through to `code: 'unknown'` with
// the raw string as the message, which is exactly the human-readable text.
export interface ParsedApiError {
  code: string;
  message: string;
  raw: Record<string, unknown> | null;
}

export function parseApiError(err: unknown): ParsedApiError {
  const raw = err instanceof Error ? err.message : String(err);
  const stripped = raw.replace(/\s\(HTTP \d+\)$/, '');
  try {
    const parsed = JSON.parse(stripped);
    if (parsed && typeof parsed === 'object' && typeof (parsed as Record<string, unknown>).error === 'string') {
      const obj = parsed as Record<string, unknown>;
      return {
        code: obj.error as string,
        message: typeof obj.message === 'string' ? obj.message : (obj.error as string),
        raw: obj,
      };
    }
  } catch {
    // Not a structured JSON payload — fall through.
  }
  return { code: 'unknown', message: stripped, raw: null };
}

// ── Types ─────────────────────────────────────────────────────────────────────

// Platform 'helper' ROLE retired 2026-07-24 (role-spine follow-up,
// Directive 1) — folded into admin.
export type Role = 'founder' | 'admin' | 'leader' | 'member' | 'public';
export type CompetitionStage = 'SEEDING' | 'ROUND_ROBIN' | 'PLAYOFFS';
export type GameMode = string;

// Org-configurable /ggveto defaults (getOrgVetoConfig/updateOrgVetoConfig
// below) — mirrors shared/orgVetoConfig.ts's ModePoolKey on the backend.
export type VetoModeKey = 'HARDPOINT' | 'SND' | 'OVERLOAD';

// ── Composable stage pipeline (T0 — model + lifecycle + builder; no engine
// execution wiring yet). Distinct from the legacy `CompetitionStage` above —
// that's the single mid-stage marker the old ROUND_ROBIN-only flow stamps on
// comp.stage; CompStage rows are an ADDITIVE, ordered, per-comp pipeline that
// coexists with it. A comp with no CompStage rows behaves exactly as today.
export type CompStageType =
  | 'SEEDING'
  | 'ROUND_ROBIN'
  | 'DIVISIONS'
  | 'SWISS'
  | 'ELIMINATION_SINGLE'
  | 'ELIMINATION_DOUBLE';
export const COMP_STAGE_TYPES: readonly CompStageType[] = [
  'SEEDING', 'ROUND_ROBIN', 'DIVISIONS', 'SWISS', 'ELIMINATION_SINGLE', 'ELIMINATION_DOUBLE',
];
export type CompStageStatus = 'PENDING' | 'LIVE' | 'ENDED';
// `config` stays a generic bag (validated server-side by
// src/shared/compStages.ts's validateStageConfig, not typed here) — besides
// each type's own knobs, every stage MAY carry `seriesFormat` (T3),
// `standingsType` (T4, excluded for ELIMINATION_SINGLE/ELIMINATION_DOUBLE),
// and `modes` (T5, ModeConfig[] — allow-listed on every type; absent means
// "inherit the comp's current modes", present means this stage overrides
// them — see StagePipelinePanel.tsx's StageConfigFields).
export interface CompStage {
  stageId: string;
  type: CompStageType;
  ordinal: number;
  status: CompStageStatus;
  config: Record<string, unknown>;
  createdAt: number;
  startedAt?: number;
  endedAt?: number;
  // R3 (comp-lifecycle-pipeline, divisions gate): DIVISIONS-only. Present
  // once POST .../start has been called on a DIVISIONS stage — the auto-snake
  // proposal (one string[] of bare clan ids per division). The stage stays
  // PENDING while this is set; POST .../confirm-divisions takes the
  // (possibly admin-edited) groups and actually generates + flips LIVE. See
  // StagePipelinePanel.tsx's DivisionsProposalPanel.
  proposedGroups?: string[][];
  // T5 (comp-lifecycle-pipeline, elimination gate): ELIMINATION_SINGLE/
  // ELIMINATION_DOUBLE-only, mirroring proposedGroups' EXACT idiom above —
  // present once POST .../start has been called on a bracket stage: the
  // proposed ENTRANT ORDER (bare clan names, position = seed). The stage
  // stays PENDING while this is set; POST .../confirm-bracket takes the
  // (possibly admin-reordered) entrants and actually generates + flips LIVE.
  // `proposedEntrantsSource` names which tier produced the order
  // ('divisions' | 'seeding' | 'enrollment'); `proposedEntrantsOrigins` is a
  // parallel per-seed label (e.g. "D1 #1", "Seed #3", "Entry #5") so the UI
  // can show WHY each team sits where it does. See StagePipelinePanel.tsx's
  // EliminationProposalPanel.
  proposedEntrants?: string[];
  proposedEntrantsSource?: EntrantProposalSource;
  proposedEntrantsOrigins?: string[];
  // E2 (comp-lifecycle-pipeline, Swiss->bracket seeding): a non-blocking
  // heads-up — present only when a Swiss stage exists earlier in this
  // pipeline but wasn't cleanly sourced from (not yet ended, or ended before
  // every team resolved to IN/OUT). Surface it, don't hard-block (product
  // posture — see EliminationProposalPanel.tsx).
  proposedEntrantsWarning?: string;
  // P2-c (guided-wizard Phase 2, Lisa 2026-07-16) — optional ESTIMATED
  // schedule for this stage. Unix-ms instants (mirrors signUpOpensAt/
  // signUpClosesAt's convention, NOT the comp's own bare-date startDate).
  // Purely display metadata: gates nothing, never feeds canStart/canEnd or
  // the comp's start-date lock (shared/compGates.ts) — see
  // src/shared/compStages.ts's CompStageRow doc comment. Editable while
  // PENDING or LIVE, locked once ENDED — see StagePipelinePanel.tsx.
  plannedStartAt?: number | null;
  plannedEndAt?: number | null;
}
export type EntrantProposalSource = 'divisions' | 'seeding' | 'enrollment' | 'swiss';
// ── SEEDING-stage runtime (src/shared/seedingTable.ts) — an ordered seeding
// table of a comp's enrolled clans, rendered inside StagePipelinePanel's
// SEEDING stage card (see SeedingTablePanel.tsx). A clan is auto-appended at
// the last position the instant it enrolls via any of the comp's three
// enrollment doors; an admin/org-or-comp-manager may then move any
// team to any position at any time the SEEDING stage exists and isn't ENDED.
// EVERY change writes an append-only audit-log row — the differentiator;
// `log` (?log=1 on GET) surfaces the last 50, newest first, with the actor's
// display name already resolved server-side (never show a raw discordId).
export type SeedSource = 'entry' | 'manual';
// M2 of the multi-team epic (Lisa, 2026-07-10): teamNo/teamName are present
// ONLY for a team-entry row — a clan that entered a SPECIFIC named team
// rather than (or alongside) its legacy whole-clan row. `clanName` is
// already the qualified "TeamName (ClanName)" display for those rows
// (server-side entryDisplayName) — teamNo/teamName ride along so the UI can
// disambiguate move/order calls for a multi-team clan (see
// SeedingTablePanel.tsx).
export interface SeedingTableRow {
  clanId: string;
  clanName: string;
  seed: number;
  source: SeedSource;
  teamNo?: number;
  teamName?: string;
}
export interface SeedingLogEntry {
  actorDiscordId: string;
  actorName?: string;
  clanId: string;
  clanName: string;
  fromSeed: number | null;
  toSeed: number;
  reason?: string;
  at: number;
  teamNo?: number;
  teamName?: string;
}
// seedingStageStatus is null when the comp has no SEEDING stage at all —
// StagePipelinePanel only renders SeedingTablePanel for a stage card it
// already knows is type SEEDING, but this stays nullable so the type is
// honest about what GET actually returns for any other caller.
export interface SeedingTableResponse {
  seedingStageStatus: CompStageStatus | null;
  rows: SeedingTableRow[];
  log?: SeedingLogEntry[];
}

// ── SEEDING rounds (R2, comp-lifecycle-pipeline) ────────────────────────────
// A SEEDING stage with config.rounds >= 1 plays out real rounds of matches on
// top of the seeding table above: round 1 generates at Start
// (StagePipelinePanel's existing startCompStage call — nothing new there),
// rounds 2..config.rounds generate via nextStageRound below, once every
// match in the current round is CONFIRMED. See SeedingRoundsPanel.tsx.

// One pairing within a generated round — `teamB: null` is a BYE (that team
// sat out; no match, no score impact). `matchId` is absent for a bye.
export interface SeedingRoundPairing {
  teamA: string;
  teamB: string | null;
  matchId?: string;
  status?: string;
  clan1Score?: number;
  clan2Score?: number;
  winner?: string;
}

export interface SeedingRoundSummary {
  round: number;
  pairings: SeedingRoundPairing[];
}

// ── Week-schedule overlay (T2, division fixtures & weekly schedule) ────────
// Mirrors shared/divisionSchedule.ts's FixtureScheduleState/FixtureClassification
// (backend). Only the DIVISIONS scoreboard carries these — see
// DivisionScoreboardPairing/DivisionScoreboardRound below — never the flat
// StageScoreboardResponse's own SeedingRoundPairing/SeedingRoundSummary above.
export type FixtureScheduleState = 'bye' | 'played' | 'upcoming' | 'due' | 'overdue';

// Derived standings row (shared/seedingRounds.ts's computeSeedingStandings)
// — scoped to ONE stage's own CONFIRMED matches. Tiebreak is ALWAYS points ->
// game-wins -> total score, regardless of standingsType (documented
// difference from the comp-level StandingRow/computeStandings above).
export interface SeedingStandingRow {
  name: string;
  played: number;
  wins: number;
  losses: number;
  gameWins: number;
  gameLosses: number;
  totalScore: number;
  points: number;
}

export interface StageScoreboardResponse {
  stageId: string;
  standingsType: StandingsType;
  roundsPlayed: number;
  roundsTotal: number;
  standings: SeedingStandingRow[];
  rounds: SeedingRoundSummary[];
}

// ── DIVISIONS scoreboard (R4, comp-lifecycle-pipeline) ──────────────────────
// Once R3's divisions gate confirms a DIVISIONS stage (per-division fixtures
// generated, groupIndex/groupRound stamped on each MATCH# row), GET
// .../scoreboard on THAT stage returns this shape instead of
// StageScoreboardResponse above — one entry per division, each computed with
// R2's EXACT math (reusing SeedingStandingRow/SeedingRoundSummary — same
// tiebreak, same round-pairing shape) but scoped to ONLY that division's own
// roster/matches. Non-DIVISIONS stages are completely unaffected — see
// getStageScoreboard vs getDivisionsScoreboard below (same URL, two
// separately-typed accessors so neither caller has to narrow a union it
// doesn't need). See DivisionsScoreboardPanel.tsx.
// A division pairing/round, same shape as SeedingRoundPairing/SeedingRoundSummary
// above PLUS the optional week-schedule overlay (T2, division fixtures &
// weekly schedule). All new fields are OPTIONAL and absent whenever the stage
// has no schedule anchor yet (plannedStartAt ?? startedAt both unset) — see
// DivisionsScoreboardResponse.currentWeek below, which is null in that case.
export interface DivisionScoreboardPairing extends SeedingRoundPairing {
  /** The week this pairing's round is pinned to (round N -> week N). */
  scheduledWeek?: number;
  /** 'bye' | 'played' | 'upcoming' | 'due' | 'overdue' — see classifyFixture (backend). */
  state?: FixtureScheduleState;
  /** The week this fixture should be shown against (null for bye/played). */
  dueWeek?: number | null;
}

export interface DivisionScoreboardRound {
  round: number;
  pairings: DivisionScoreboardPairing[];
  /** The preferred [start, end) epoch-ms window for this round's week. */
  weekDateRange?: { start: number; end: number };
}

export interface DivisionScoreboardEntry {
  groupIndex: number;
  name: string;
  standings: SeedingStandingRow[];
  rounds: DivisionScoreboardRound[];
}

export interface DivisionsScoreboardResponse {
  stageId: string;
  standingsType: StandingsType;
  divisions: DivisionScoreboardEntry[];
  /** Null when the stage has no schedule anchor yet (no plannedStartAt/startedAt). */
  currentWeek?: number | null;
}

// ── SWISS scoreboard (E1, comp-lifecycle-pipeline — Swiss rounds panel) ─────
// Same URL as StageScoreboardResponse/DivisionsScoreboardResponse above (the
// server branches on stage.type — see getStageScoreboard's doc comment), a
// THIRD separately-typed accessor for a SWISS stage. Standings carry the
// engine's own Buchholz/state (src/shared/swiss.ts's SwissStanding) — not
// the flat W/L/points table every other stage type gets, because Swiss has
// no such table, it has a record + a cut.
export type SwissTeamState = 'ACTIVE' | 'IN' | 'OUT';

export interface SwissScoreboardStanding {
  id: string;
  seed: number;
  wins: number;
  losses: number;
  buchholz: number;
  state: SwissTeamState;
}

export interface SwissScoreboardPairing {
  round: number;
  bucket: string;
  teamA: string;
  /** null = bye (a free win — no match row, no live status). */
  teamB: string | null;
  matchId?: string;
  /** The underlying match's live status (e.g. PENDING/CONFIRMED); absent for a bye. */
  status?: string;
  winner?: string;
}

export interface SwissScoreboardRound {
  round: number;
  status: 'PAIRED' | 'COMPLETE';
  pairings: SwissScoreboardPairing[];
}

export interface SwissScoreboardSummary {
  advanced: string[];
  eliminated: string[];
  active: string[];
  complete: boolean;
}

export interface SwissScoreboardResponse {
  stageId: string;
  /** False before /start has ever generated round 1 — every other field is empty/null. */
  initialised: boolean;
  config: { winThreshold: number; lossThreshold: number } | null;
  currentRound: number;
  standings: SwissScoreboardStanding[];
  rounds: SwissScoreboardRound[];
  /** True once every pairing in the CURRENT round has a result (byes always count). */
  roundComplete: boolean;
  summary: SwissScoreboardSummary | null;
}

// ── Clan rollup (M3, comp-lifecycle-pipeline — "bring the stars back to the
// clan", Lisa 2026-07-10) ────────────────────────────────────────────────────
// GET /competitions/:compId/clan-rollup — sums every enrolled ENTRY's (team-
// entry or legacy whole-clan, M1/M2) results across EVERY stage of the comp's
// pipeline it played in, then groups the entry totals back up under their
// real clan so a clan that fielded more than one team sees the COMBINED
// honours of all of them. Mirrors shared/clanRollup.ts's shape byte-for-byte
// — see that module's header (backend) for the aggregation-scope + bestResult
// ordering (champion > division rank > points > none) this response encodes.
export interface ClanRollupEntry {
  entryName: string;
  teamNo?: number;
  played: number;
  wins: number;
  losses: number;
  points: number;
  /** Present only when this entry played a DIVISIONS stage. */
  divisionName?: string;
  /** 1-based rank within that division (1 = best). */
  divisionRank?: number;
  /** True only for the entry that won an elimination stage outright. */
  isChampion?: boolean;
}

export interface ClanRollupTotals {
  entries: number;
  played: number;
  wins: number;
  losses: number;
  points: number;
}

export interface ClanRollupBestResult {
  kind: 'champion' | 'division' | 'points' | 'none';
  label: string;
}

export interface ClanRollupClan {
  clanId: string;
  clanName: string;
  entries: ClanRollupEntry[];
  totals: ClanRollupTotals;
  bestResult: ClanRollupBestResult;
}

export interface ClanRollupResponse {
  compId: string;
  clans: ClanRollupClan[];
}

export interface ApplyStandingsMove {
  clanId: string;
  clanName: string;
  fromSeed: number;
  toSeed: number;
}

export interface ApplyStandingsResponse {
  ok: boolean;
  moved: number;
  roundNumber: number;
  moves: ApplyStandingsMove[];
}
// ── Per-clan terms/rules acceptance (src/shared/compTerms.ts) — teams must
// agree to a comp's rules before the leader can self-register via
// register-my-clan; admin-driven enrollment is not gated but stays visible.
// Acceptance is DURABLE (Lisa, 2026-07-08 amendment): ANY accepted version
// counts as 'accepted' — there is no 'stale' warning state. rulesVersion
// still rides along on 'accepted' rows as audit metadata (which version the
// clan actually agreed to), which may be older than the comp's current rules.
export type TermsAcceptanceStatusValue = 'accepted' | 'missing';
export interface ClanTermsAcceptance {
  clanId: string;
  status: TermsAcceptanceStatusValue;
  acceptedByDiscordId?: string;
  acceptedAt?: number;
  rulesVersion?: number;
}

// GET /competitions/:compId/terms-acceptance response — the comp's dynamic
// rules text + current version ride alongside the per-clan status list, so
// the accept-terms interstitial can render the ACTUAL rules being agreed to
// (not just a link to the static CompRules.tsx manual), and so
// CompetitionCreateForm's edit-mode textarea can prefill from the same call.
// `rules` is '' when the comp has never had its rules text written.
export interface CompTermsAcceptanceResponse {
  rules: string;
  rulesVersion: number;
  clans: ClanTermsAcceptance[];
}

export type DivisionTier =
  | 'PROS'
  | 'AMATEUR_PROS'
  | 'AMATEURS'
  | 'CHALLENGERS'
  | 'PRO_AMS_DIV_1'
  | 'PRO_AMS_DIV_2'
  | 'AMS_DIV_1'
  | 'AMS_DIV_2'
  | 'AMS_DIV_3';
export type FlagType = 'COD_ID_CHANGED' | 'COD_ID_DUPLICATE' | 'SUBMISSION_ANOMALY' | 'OTHER';
export type FlagStatus = 'OPEN' | 'APPROVED' | 'REJECTED';
export type OrganizationRole = 'ORG_OWNER' | 'ORG_ADMIN' | 'ORG_MEMBER' | 'ORG_VIEWER';
export type CompetitionRole = 'COMP_OWNER' | 'COMP_ADMIN' | 'COMP_MEMBER';
export type ClanScopedRole = 'CLAN_OWNER' | 'CLAN_LEADER' | 'CLAN_MEMBER';
export type BillingPlan = 'STARTER' | 'GROWTH' | 'PREMIER' | 'CUSTOM';
export type BillingInterval = 'MONTHLY' | 'ANNUAL' | 'PER_COMPETITION';
export type EntitlementStatus = 'INACTIVE' | 'TRIALING' | 'ACTIVE' | 'GRACE_PERIOD' | 'PAST_DUE' | 'CANCELED';
export type CompetitionRequestStatus = 'DRAFT' | 'QUOTED' | 'CHECKOUT_PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED';
export type CompetitionVisibility = 'PUBLIC' | 'ORG_ONLY' | 'PRIVATE';
export type RolePolicyScope = 'ORG' | 'COMP' | 'CLAN';

export interface OrgMembershipSummary {
  orgId: string;
  role: OrganizationRole;
  joinedAt?: number;
}

export interface CompetitionMembershipSummary {
  compId: string;
  orgId?: string;
  role: CompetitionRole;
  joinedAt?: number;
}

export interface PopiaConsent {
  consentedAt: number;
  version: string;
}

export interface DiscordProfile {
  globalName: string | null;
  avatar: string | null;
  accentColor: number | null;
  bannerColor: string | null;
  banner: string | null;
  discordClanTag: string | null;
  discordClanGuildId: string | null;
  discordClanBadge: string | null;
  accountCreatedAt: number | null;
  /** User-picked theme accent (#RRGGBB) — overrides Discord's accentColor. */
  accentOverride: string | null;
}

export interface Me {
  discordId: string;
  displayName: string;
  role: Role;
  clanName?: string;
  popiaConsent?: PopiaConsent | null;
  discordProfile?: DiscordProfile | null;
  orgMemberships?: OrgMembershipSummary[];
  competitionMemberships?: CompetitionMembershipSummary[];
  orgCreditBalances?: OrgCreditBalanceSummary[];
  platformCredits?: PlatformCreditsSummary;
  userCredits?: { balance: number; totalGranted: number; totalUsed: number } | null;
  // T07 (duplicate-org warning): the org bound to the Discord guild this
  // session's JWT was minted from — present only when that org still exists.
  // Drives the "you already have an org for this server" notice on the
  // org-create form (MyOrg.tsx's CreateOrgInline) so a portal-created org
  // doesn't silently duplicate a bot-install org for the same server.
  entryOrgId?: string;
  entryOrgName?: string;
  // Active TEAM captaincies (role-visibility audit F1, 2026-07-22). A captain
  // grant is an overlay that never touches the base membership role, so a
  // captain's `role` above is plain 'member' — this is the ONLY signal on /me
  // that distinguishes them. Drives the GATE's "Competition as a clan" door
  // (see pages/gate/gateIdentity.ts's canEnterComp). Additive and optional:
  // absent on any response cached from before the field shipped, which every
  // consumer must read as "no captaincies", the same fail-closed answer the
  // gates already assumed.
  captaincies?: TeamCaptaincy[];
  // SIMULATION-ONLY (2026-08-02 role-accuracy fix). Written EXCLUSIVELY by
  // lib/viewAs.ts's applyViewAs when a founder is previewing a clan-scoped
  // persona; GET /me never returns it and no real session ever carries it.
  // It exists because a clan placement is genuinely part of an identity —
  // roleSpine.ts resolves rank AT a placement, and CLAN_CAPTAIN is pinned to
  // ONE team — so a persona without it cannot represent a captain at all.
  // Consumers must treat absence as "no simulated clan placement", which is
  // the answer for every real user.
  simulatedClanScope?: SimulatedClanScope | null;
}

/** A simulated clan placement — mirrors roleSpine.ts's ActorContext
 *  clanMemberships row (scopedRole + teamNo), which is what the backend
 *  resolves a clan rank against. `teamNo` is null for CLAN_LEADER: a leader
 *  runs every team in their clan, so pinning one would misrepresent them. */
export interface SimulatedClanScope {
  clanId: string;
  clanName: string;
  scopedRole: 'CLAN_LEADER' | 'CLAN_CAPTAIN';
  teamNo: number | null;
}

/**
 * An OPEN submission window for the caller — the answer GET /me/live-match
 * gives (2026-07-26). Non-null means: a match this person is on is inside its
 * submission window (from 1h before kickoff until the card closes) AND at
 * least one screenshot they are responsible for is still outstanding.
 *
 * `reason` says WHY they are responsible, which the copy has to reflect:
 *   'own'   — their own slot is empty.
 *   'proxy' — they are the clan leader covering GHOST seats (nobody behind
 *             those seats; the leader uploads on their behalf).
 *   'both'  — at least one of each.
 * Same three-way distinction the backend's NudgeReason draws for its DMs.
 */
export interface LiveMatchWindow {
  matchId: string;
  compId: string;
  /** Server-composed one-liner, e.g. "Acme Cup · vs Night Cats". */
  label: string;
  opponentClanName: string;
  pendingSlotCount: number;
  reason: 'own' | 'proxy' | 'both';
}

/** One live team captaincy, as projected by shared/delegation.ts's
 *  activeTeamCaptaincies. Only TEAM-scoped grants appear — a legacy clan-wide
 *  grant opens no team entry, so it is never surfaced here. */
export interface TeamCaptaincy {
  clanName: string;
  teamNo: number;
  teamName?: string;
  kind: 'WINDOWED' | 'PERMANENT' | 'COMP_SCOPED';
}

// A single other viewer currently "present" on the same resource (see
// presence.ts's header — awareness-only face-pile, no locking). Backend has
// already applied the read-time freshness filter and excluded the caller
// themselves before this ever reaches the frontend.
export interface PresenceViewer {
  discordId: string;
  displayName: string;
  role: Role;
  lastSeenAt: number;
}

export type CompetitionFormat = 'ROUND_ROBIN' | 'ELIMINATION_SINGLE' | 'ELIMINATION_DOUBLE' | 'SWISS' | 'GROUP_STAGE';

export interface Competition {
  id: string;
  name: string;
  status: 'PENDING' | 'LIVE' | 'CLOSED';
  stage?: CompetitionStage;
  format?: CompetitionFormat;
  publicPageEnabled?: boolean;
  teamSize: number;
  seedingDeadline?: number;
  createdAt: number;
  activatedAt?: number;
  orgId?: string;
  modes?: import('./modes').ModeConfig[];
  seriesFormat?: import('./modes').SeriesFormat;
  // Calendar/lifecycle — optional
  signUpOpensAt?: number;
  signUpClosesAt?: number;
  // Start-date lifecycle gates (Lisa, 2026-07-09) — startDate is the
  // "YYYY-MM-DD" date CompetitionCreateForm's <input type="date"> writes;
  // null/undefined means the comp never opted into the gates (legacy,
  // always open). registrationOpen is server-derived so the frontend never
  // re-implements the date math (see shared/compGates.ts isRegistrationClosed).
  startDate?: string | null;
  registrationOpen?: boolean;
}

// T14 — org-level scoreboard
export interface OrgStatsResponse {
  orgId: string;
  orgName: string;
  comps: { total: number; byStatus: Record<string, number> };
  clanCount: number;
  memberCount: number;
  confirmedMatches: number;
  topClans: Array<{ rank: number; clanName: string; totalScore: number; wins: number; losses: number; compsSeen: number }>;
  perComp: Array<{ id: string; name: string; status: string; format: string; matchCount: number }>;
}

export type StandingsType = 'WIN_LOSS' | 'SCORE';

export interface StandingRow {
  name: string;
  wins: number;
  losses: number;
  draws: number;
  gamesWon: number;
  gamesLost: number;
  matchesPlayed: number;
  points: number;
  totalScore?: number;
  avgScore?: number;
  // Present when the comp is divisional — the tier this row was ranked
  // within. Absent/undefined on non-divisional comps.
  division?: string;
}

// One division's scoped, pre-sorted ranking (e.g. tier 'CHALLENGERS' or the
// legacy 'PROS'/'AMATEUR_PROS'/'AMATEURS'). Clans that played a match but
// aren't listed in any division land under tier 'UNASSIGNED' rather than
// disappearing.
export interface DivisionStandingsGroup {
  tier: string;
  standings: StandingRow[];
}

export interface StandingsResponse {
  compId: string;
  compName: string;
  standingsType?: StandingsType;
  // Flat list — back-compat. For a divisional comp this is the concatenation
  // of every division's standings (tier order), each row stamped with
  // `division`; prefer `divisions` for display when it's non-null.
  standings: StandingRow[];
  // null for non-divisional comps (today's single flat ranking). Non-null =
  // this comp has DIVISION# rows; render one table per division.
  divisions: DivisionStandingsGroup[] | null;
  matchCount: number;
}

export interface ProgressionRoundPoint {
  round: number;
  score: number;
  cumScore: number;
  cumWins: number;
  cumLosses: number;
}

export interface ProgressionClan {
  clan: string;
  display: string;
  division: string;
  perRound: ProgressionRoundPoint[];
}

export interface CompProgressionResponse {
  compId: string;
  rounds: number;
  clans: ProgressionClan[];
}

// ── Elimination bracket ───────────────────────────────────────────────────────

export type BracketSide = 'WINNERS' | 'LOSERS' | 'GRAND_FINAL';
export type BracketSlotStatus = 'WAITING' | 'READY' | 'PLAYED' | 'BYE';

export interface BracketSourceRef {
  kind: 'SEED' | 'WINNER_OF' | 'LOSER_OF';
  slot?: number;
  entrant?: string;
}

export interface BracketProposal {
  scoreA: number;
  scoreB: number;
  shots: number;
  orientationConfident: boolean;
  perShot: Array<{ shotSk: string; gameMode?: string | null; scoreA?: number; scoreB?: number }>;
  updatedAt: number;
}

export type BracketShotStatus = 'UPLOADED' | 'OCR_PROCESSING' | 'OCR_DONE' | 'FAILED';

export interface BracketShot {
  sk: string;
  compId: string;
  slot: number;
  status: BracketShotStatus;
  uploadedBy: string;
  createdAt: number;
  leftTeamName?: string | null;
  rightTeamName?: string | null;
  leftTeamScore?: number | null;
  rightTeamScore?: number | null;
  gameMode?: string | null;
  ocrConfidence?: number | null;
  ocrError?: string;
}

export interface BracketSlot {
  compId: string;
  slot: number;
  side: BracketSide;
  round: number;
  roundLabel: string;
  status: BracketSlotStatus;
  sourceA: BracketSourceRef;
  sourceB: BracketSourceRef;
  teamA?: string;
  teamB?: string;
  byeA?: boolean;
  byeB?: boolean;
  scoreA?: number;
  scoreB?: number;
  winner?: string;
  loser?: string;
  winnerTo?: number;
  loserTo?: number;
  playedAt?: number;
  proposed?: BracketProposal;
}

export interface BracketResponse {
  compId: string;
  format: CompetitionFormat | null;
  slots: BracketSlot[];
  shots?: BracketShot[];
  champion?: string;
}

// Public spectator payload (/public/comps/:id — no auth, opt-in per comp)
export interface PublicCompResponse {
  compId: string;
  name: string;
  status: string;
  format: CompetitionFormat | null;
  orgName?: string;
  accentColor?: string;
  slots?: BracketSlot[];
  champion?: string;
  standings?: StandingRow[];
  divisions?: DivisionStandingsGroup[] | null;
  standingsType?: StandingsType;
  matchCount?: number;
  // GROUP_STAGE only: per-group standings + phase (the playoff bracket arrives in `slots`).
  groups?: GroupStageGroup[];
  phase?: 'GROUPS' | 'PLAYOFF';
}

// Public rules/CoC payload (/public/comp/:id/rules — NO auth, opt-in per comp).
// Strict allowlist from the backend: only these two fields ever come back.
export interface CompPublicRulesResponse {
  compName: string;
  rulesText: string;
}

// ── Group stage (format === GROUP_STAGE) ──────────────────────────────────────

export interface GroupStandingRow {
  id: string;
  seed: number;
  group: number;
  wins: number;
  losses: number;
  ties: number;
  byes: number;
  score: number;
  pointsFor: number;
  pointsAgainst: number;
  buchholz: number;
  rank: number;
  advanced: boolean;
}

export interface GroupStageGroup {
  group: number;
  standings: GroupStandingRow[];
}

export interface GroupStageFixture {
  group: number;
  round: number;
  teamA: string;
  teamB: string | null;
  matchId?: string | null;
  winner?: string;   // present once the match is decided
  scoreA?: number;   // maps won by teamA
  scoreB?: number;   // maps won by teamB
}

export interface GroupStageResponse {
  compId: string;
  initialised: boolean;
  phase: 'GROUPS' | 'PLAYOFF' | null;
  config?: { numGroups: number; advancePerGroup: number };
  groups: GroupStageGroup[];
  fixtures: GroupStageFixture[];
}

// ── Seeding ───────────────────────────────────────────────────────────────────

export interface SeedingRoundEntry {
  roundNumber: number;
  opponent: string;
  modeScores: Record<string, number>;
  pointsEarned: number;
  submittedAt: number;
}

export interface SeedingStandingRow {
  clanName: string;
  totalPoints: number;
  modePoints: Record<string, number>;
  roundsPlayed: number;
  roundBreakdown: SeedingRoundEntry[];
}

export interface SeedingResponse {
  compId: string;
  compName: string;
  stage?: CompetitionStage;
  seedingDeadline?: number;
  seedingRoundsTotal: number;
  modes: import('./modes').ModeConfig[];
  standings: SeedingStandingRow[];
}

export interface SubmitSeedingInput {
  compId: string;
  opponentClan: string;
  roundNumber: number;
  modeScores: Record<string, number>;
}

// ── Divisions ─────────────────────────────────────────────────────────────────

// A team's ranked score line inside a division (SCORE-type comps). `rounds` is
// keyed by round number as a string ("1".."9"); `total` is the season total.
export interface DivisionTeamScore {
  clanName: string;
  displayName?: string;
  logoUrl?: string;
  rounds: Record<string, number>;
  roundsPlayed: number;
  total: number;
}

// A player's aggregated Final Stats within a division (SCORE comps).
export interface DivisionPlayerStat {
  team: string;
  player: string;
  score: number;
  objScore: number;
  time: number;
  plants: number;
  defuses: number;
  overloads: number;
  kills: number;
  deaths: number;
  kd: number;
}

export interface Division {
  pk: string;
  sk: string;
  tier: DivisionTier;
  clans: string[];
  createdAt: number;
  // Present when the division carries a populated score table (e.g. EGL S3).
  standings?: DivisionTeamScore[];
  players?: DivisionPlayerStat[];
  roundNumbers?: number[];
  standingsType?: string;
}

export interface CreateDivisionsInput {
  compId: string;
  prosClans?: string[];
  amateurProsClans?: string[];
  amateursClans?: string[];
}

// ── Clan ──────────────────────────────────────────────────────────────────────

export interface ClanMember {
  discordId: string;
  gamertag?: string;
  displayName?: string;
  role: 'LEADER' | 'MEMBER';
  scopedRole?: ClanScopedRole;
  teamNo?: number | null;
}

export interface ClanSeat {
  gamertag: string;
  status: 'GHOST' | 'PENDING_CLAIM' | 'CLAIMED';
  claimedBy?: string;
  claimedByName?: string;
  pendingClaimant?: string;
  pendingClaimantName?: string;
  teamNo?: number | null;
  // G3a/G3b: canonical ghost identity (resolveSeatPlayerId, shared/ghostId.ts)
  // — the backend (GET /clan/:name, index.ts ~12130) always stamps this,
  // null-safe even for legacy seats with no stored playerId. This is the SAME
  // id a claim links to (GHOST_LINK) and PLAYER_CARD/slot rows key on once a
  // match is inited with this seat — never a display-only value.
  playerId: string;
}

export interface Delegation {
  grantId: string;
  granteeId: string;
  teamNo: number | null;
  teamName?: string;
  capabilities: string[];
  status: 'ACTIVE';
  /** 'PERMANENT' = the minted Team Captain (no expiry); 'WINDOWED' = the time-boxed stand-in; 'COMP_SCOPED' = captain for the duration of one competition. Absent (legacy rows) reads as 'WINDOWED'. */
  kind?: 'WINDOWED' | 'PERMANENT' | 'COMP_SCOPED';
  startsAt: number;
  /** Present for WINDOWED grants only — PERMANENT and COMP_SCOPED captains never carry an expiry timestamp. */
  expiresAt?: number;
  /** Present for COMP_SCOPED grants only — the competition this captaincy is scoped to. */
  compId?: string;
  grantedAt: number;
}

export interface BannedMember {
  discordId: string;
  bannedBy: string;
  bannedAt: number;
  reason?: string;
  gamertag?: string;
  displayName?: string;
}

export interface ClanDetail {
  id: string;
  name: string;
  leaderId: string;
  description?: string;
  publicApplicationsOpen?: boolean;
  members: ClanMember[];
  seats: ClanSeat[];
  activeDelegations?: Delegation[];
  teamNames?: Record<string, string>;
  bannedMembers?: BannedMember[];
  orgId?: string;
}

// ── Match ─────────────────────────────────────────────────────────────────────

export interface GameResult {
  clan1Score: number;
  clan2Score: number;
  winner: string;
}

export interface Screenshot {
  userId: string;
  url: string;
}

export interface GameEntry {
  mode: string;
  slotIndex: number;
  clan1Score: number;
  clan2Score: number;
  winner: string;
}

export interface RosterEntry {
  mode: string;
  playerIds: string[];
  captainId: string;
  setAt: number;
}

export interface ModeMatchStats {
  score: number;
  objScore: number;
  kills: number;
  deaths: number;
  time?: number;
  plants?: number;
  defuses?: number;
  overloads?: number;
}

export interface PlayerMatchTotals {
  score: number;
  objScore: number;
  kills: number;
  deaths: number;
  kd: number;
  time: number;
  plants: number;
  defuses: number;
  overloads: number;
}

export interface PlayerMatchStats {
  playerId: string;
  clanName: string;
  gamertag?: string;
  roundNumber: number;
  modeStats: Record<string, ModeMatchStats>;
  totals: PlayerMatchTotals;
  submittedAt?: number;
  submittedBy?: string;
}

export interface MatchReadiness {
  clan1Available: number;
  clan1Needed: number;
  clan1Pending: number;
  clan2Available: number;
  clan2Needed: number;
  clan2Pending: number;
  vetoDone: boolean;
  ready: boolean;
  updatedAt: number;
}

export type MatchLifecycleState =
  | 'NOTIFY' | 'AWAIT_AVAILABILITY' | 'VETO_WINDOW' | 'LIVE'
  | 'AWAIT_EVIDENCE' | 'CONFIRM' | 'ARCHIVED';

export interface MatchAvailabilityRow {
  matchId: string;
  compId: string;
  discordId: string;
  clanName: string;
  gamertag?: string;
  response: 'YES' | 'NO' | 'PENDING';
  respondedAt?: number;
  source?: 'PORTAL' | 'DISCORD';
}

export interface MatchTimelineRow {
  matchId: string;
  compId: string;
  ts: number;
  kind: string;
  actorDiscordId?: string;
  actorDisplayName?: string;
  detail?: string;
}

export interface MatchDetail {
  pk: string;
  sk: string;
  clan1: string;
  clan2: string;
  // Denormalized display-name stamp (Lisa, 2026-07-13, clan display-name-
  // preservation fix): clan1/clan2 above stay the canonical CLAN#-derived
  // identity; these carry each clan's METADATA original-casing name (so
  // e.g. "$kare" survives instead of collapsing to "KARE"). Absent on
  // matches created before this fix — every render site falls back to
  // stripping/deriving from clan1/clan2 as before.
  clan1DisplayName?: string;
  clan2DisplayName?: string;
  // M2 of the multi-team epic (Lisa, 2026-07-10): the REAL clan pk backing a
  // TEAM-entry side (clan1/clan2 may be a qualified "Beta (Alpha)" name that
  // can't be re-derived into a clan pk by string transform) — see
  // matchClanIdFields' doc comment in index.ts. Absent for a legacy side.
  clan1Id?: string;
  clan2Id?: string;
  // Match-team-autofill (Lisa, 2026-07-13): which of that clan's named teams
  // is playing — the explicit pointer MatchRosterInitPanel's squad pre-fill
  // keys off (see deriveDefaultSelection). Absent = legacy/whole-clan side.
  clan1TeamNo?: number;
  clan2TeamNo?: number;
  leader1Id?: string;
  leader2Id?: string;
  status: string;
  nightWinner?: string;
  // FORFEIT thread (division fixtures & weekly schedule) — POST
  // /matches/:matchId/forfeit stamps these onto an admin-resolved walkover
  // (status CONFIRMED, nightWinner = the non-forfeited clan, no real
  // scores/games). Absent on every match resolved a normal way. The
  // frontend forfeit button/badge is a follow-up thread — these fields are
  // mirrored here now so that thread doesn't also need a backend PR.
  forfeit?: boolean;
  forfeitedClan?: string;
  leader1Confirmed?: boolean;
  leader2Confirmed?: boolean;
  scheduledAt?: number;
  // T4 (division fixtures & weekly schedule) — see MatchSummary's identical
  // fields' doc comment; same shared/matchDateAgreement.ts authority.
  proposedScheduledAt?: number;
  dateProposedBy?: string;
  dateAgreementStatus?: 'AWAITING';
  submissionDeadline?: number;
  matchEndTime?: number;
  roundNumber?: number;
  roster?: RosterEntry[];
  games: GameEntry[];
  screenshots: Screenshot[];
  playerStats?: PlayerMatchStats[];
  modes?: import('./modes').ModeConfig[];
  lifecycleState?: MatchLifecycleState;
  readiness?: MatchReadiness;
  vetoId?: string;
  // Engine Wiring Arc 1 (comp-lifecycle-pipeline): present when the match was
  // created while the comp had exactly one composed stage LIVE (see
  // shared/compStages.ts's findLiveStage on the backend). Absent for legacy
  // matches / comps with no stage pipeline.
  stageId?: string;
  stageType?: string;
}

// R1 (comp-lifecycle-pipeline, manual result entry): the series a match
// should be scored against, resolved server-side (stage config -> comp
// seriesFormat -> legacy CDL preset — shared/compStages.ts's
// resolveMatchSeries). Present on GET /matches rows (the legacy tier always
// resolves via a BO3 anchor even with no stage link and no comp-level
// seriesFormat) — absent on other MatchSummary-shaped endpoints (e.g.
// GET /admin/disputes) that don't attach it.
export interface ResolvedMatchSeries {
  length: number;
  modeOrder: string[];
  winsNeeded: number;
}

export interface MatchSummary {
  pk: string;
  sk: string;
  clan1: string;
  clan2: string;
  // See MatchDetail.clan1DisplayName's doc comment — same denormalized
  // display-name stamp, same fallback rule.
  clan1DisplayName?: string;
  clan2DisplayName?: string;
  status: string;
  nightWinner?: string;
  // See MatchDetail.forfeit/forfeitedClan's doc comment — same walkover
  // marker, same absent-unless-forfeited rule.
  forfeit?: boolean;
  forfeitedClan?: string;
  createdAt?: number;
  scheduledAt?: number;
  // Engine Wiring Arc 1 — see MatchDetail.stageId's doc comment above.
  stageId?: string;
  stageType?: string;
  resolvedSeries?: ResolvedMatchSeries;
  // Typing-gap fill (Matches step / MatchScheduleBoard) — these ride on the
  // raw MATCH# row already (GET /matches spreads the DDB item verbatim, see
  // index.ts's `...m`) but were never declared here. One field per
  // stage-generation engine; a given match only ever carries the one(s) its
  // own engine stamps — see index.ts's writeSeedingRound (roundNumber),
  // generateDivisionsPlay/legacy groupstage-init (groupIndex + groupRound),
  // generateSwissPlay/legacy swiss-pair (swissRound). ROUND_ROBIN and
  // ELIMINATION_SINGLE/ELIMINATION_DOUBLE stamp none of these — round-robin
  // has no rounds and elimination bracket play lives entirely in BRACKET#
  // rows, never a MATCH# row (see groupMatchesByStage.ts's header comment).
  roundNumber?: number;
  groupIndex?: number;
  groupRound?: number;
  swissRound?: number;
  // Same typing-gap fill as above — a CONFIRMED match's per-game scores
  // (arc 3 scoring unification: POST /match/:matchId/result and
  // .../confirm-scores both stamp this same MatchCardGameResult[] shape onto
  // the MATCH# row, see matchResultEntry.test.ts). Rides GET /matches
  // verbatim; was never declared here, which is why "Edit result" on a
  // CONFIRMED match opened ResultEntryForm blank instead of prefilled.
  games?: MatchCardGameResult[];
  // T4 (division fixtures & weekly schedule, 2026-07-18): the two clan
  // leaders' discordIds — stamped by generateDivisionsPlay for a
  // stage-generated division fixture (may be null if no leader could be
  // resolved for that entrant), or by the captain-initiated POST /matches
  // path for a manually-created match. Same typing-gap fill as above: rides
  // GET /matches verbatim, was just never declared. DivisionsScoreboardPanel's
  // date-agreement control is the first reader — reuses the SAME
  // discordId === leader1Id/leader2Id check MatchCardLive.tsx's
  // AgreementBanner already established.
  leader1Id?: string | null;
  leader2Id?: string | null;
  // The pending-proposal sub-state for POST .../propose-date and
  // .../date/agree|decline (shared/matchDateAgreement.ts, the backend
  // authority). All three arrive/leave together — see that module's
  // MatchDateAgreementRow doc comment. scheduledAt above doubles as the
  // AGREED date once one exists (same field an admin PATCH would set).
  proposedScheduledAt?: number;
  dateProposedBy?: string;
  dateAgreementStatus?: 'AWAITING';
  // Typing-gap fill (Forfeit thread, DisciplinePage, 2026-07-18): M2
  // multi-team's clan1Id/clan2Id (see MatchDetail.clan1Id's doc comment
  // above — the REAL clan pk backing a side, needed when clan1/clan2 is a
  // qualified team-entry name like "Beta (Alpha)" that can't be re-derived
  // by string transform) ride GET /matches verbatim same as every other
  // field in this "typing-gap fill" run but were never declared here.
  // ForfeitFixturePanel is the first reader — it needs the real pk to match
  // a qualified side back to the org's clan list.
  clan1Id?: string;
  clan2Id?: string;
}

// ── Match Card v2 ─────────────────────────────────────────────────────────────

export type MatchFormat = 'BO3' | 'BO5' | 'BO7' | 'LEGACY';
export type MatchCardStatus =
  | 'DRAFT' | 'SCHEDULED' | 'VETO' | 'LIVE' | 'CLOSED'
  | 'OCR' | 'VERIFIED' | 'FORGED' | 'CANCELLED' | 'DISPUTED'
  // W-A: hard-failure terminal state (uncaught batch exception, daily OCR
  // cap, missing actor) — see MatchCardStatus.FAILED in shared/types.ts.
  | 'FAILED'
  // Org token pool depleted (or no parent org) — the batch runner routes the
  // match to manual score entry and writes cardStatus: 'MANUAL'. This member
  // must stay in lockstep with MatchCardStatus.MANUAL in shared/types.ts —
  // omitting it left STATUS_THEMES lookups undefined and crashed the live
  // card the first time a real match hit the depleted-tokens fork.
  | 'MANUAL';
export type SlotStatus =
  | 'PENDING' | 'UPLOADED' | 'OCR_PROCESSING' | 'OCR_DONE' | 'MISSED' | 'FLAGGED';

/** One side's sign-off on its own result rows. */
export interface SideSignoff {
  status: 'SIGNED' | 'WITHDRAWN';
  signedBy: string;
  signedAt: number;
  /** True when an OPERATOR signed for an unresponsive side rather than that
   *  side signing its own work — materially different, so it is recorded. */
  onBehalf?: boolean;
}

export interface MatchSignoffState {
  side1?: SideSignoff;
  side2?: SideSignoff;
  state: 'AWAITING_BOTH' | 'AWAITING_ONE' | 'READY';
  /** Which side is still outstanding — set only for AWAITING_ONE. */
  awaitingSide?: 1 | 2;
}

export interface MapVetoState {
  status: 'OPEN' | 'LOCKED';
  turnClanId?: string;
  picks: Array<{ gameNum: number; mode: string; mapName: string; pickedByClanId: string; pickedAt: number }>;
  bans: Array<{ mapName: string; mode: string; bannedByClanId: string; bannedAt: number }>;
  lockedAt?: number;
  lockedBy?: 'BOTH_CLANS' | 'AUTO_DEADLINE' | 'ADMIN';
  // In-game faction per side (Lisa, 2026-07-24) — the caller's own side picks,
  // the other side auto-inherits the opposite, then it's locked. The veto MOVE
  // endpoint 409s until this is set, so the frontend gates the ban/pick board
  // on its presence (see VetoBoard's faction picker).
  factions?: { clan1: 'JSOC' | 'GUILD'; clan2: 'JSOC' | 'GUILD' };
}

// Arc 3 (scoring unification) — one confirmed game, the SAME shape both
// POST /match/:matchId/result and POST /matchcard/:matchId/confirm-scores write.
export interface MatchCardGameResult {
  mode: string;
  slotIndex: number;
  clan1Score: number;
  clan2Score: number;
  winner: string;
}

export interface SubmissionSlotRow {
  pk: string;
  sk: string;
  matchId: string;
  compId: string;
  gameNum: number;
  gameMode: string;
  mapName?: string;
  playerId: string;
  clanId: string;
  status: SlotStatus;
  s3Key?: string;
  uploadedAt?: number;
  ocrCompletedAt?: number;
  ocrConfidence?: number;
  ocrTier?: string;
  ocrTokens?: { input: number; output: number };
  parsedScoreboard?: unknown;
  // Uploader's own bottom-strip stats — lifted from parsedScoreboard.selectedPlayer
  // by OcrBatchRunner so the consensus aggregator can attribute the photographer's
  // K/D/extras to their snapped roster player without re-parsing.
  uploaderStats?: {
    gamertag?: string;
    title?: string;
    eliminations?: number;
    deaths?: number;
    elimDeathRatio?: number;
    objectiveScore?: number;
    extras?: Array<{ label: string; value: number }>;
  };
  flaggedReason?: string;
  // GOD-policy L7b — "poke + nothing to submit" provenance on a MISSED slot.
  // 'timeout' = matchLifecycle's MATCH_CLOSE sweep (offset owned by
  // shared/matchSchedules.ts); the other three are a human declare-nothing
  // (POST .../declare-nothing). declarationOverriddenAt is
  // stamped when the holder uploads for real before close, overriding a
  // prior declaration — missedVia/declaredBy/declaredAt/reason stay as
  // history rather than being cleared.
  missedVia?: 'self-declared' | 'captain-declared' | 'admin-declared' | 'timeout';
  declaredBy?: string;
  declaredAt?: number;
  // Roster-integrity flag (Lisa product ruling, 2026-07-21) — set when the
  // submitter (and/or slot owner, for a proxy submission) no longer held
  // clan membership at SUBMISSION time. Observational only — never changes
  // who was allowed to submit.
  notRostered?: boolean;
  notRosteredWho?: 'submitter' | 'slotOwner' | 'both';
  reason?: string;
  declarationOverriddenAt?: number;
}

export interface VetoMoveRow {
  pk: string;
  sk: string;
  matchId: string;
  compId: string;
  seq: number;
  actorClanId: string;
  actorDiscordId: string;
  action: 'BAN' | 'PICK';
  mapName: string;
  mode: string;
  ts: number;
}

export interface MatchCommentRow {
  pk: string;
  sk: string;
  matchId: string;
  compId: string;
  actorDiscordId: string;
  actorDisplayName?: string;
  body: string;
  ts: number;
  pinned?: boolean;
  pinnedBy?: string;
  pinnedAt?: number;
  editedAt?: number;
  deletedAt?: number;
}

export interface CalendarEntryRow {
  pk: string;
  sk: string;
  discordId: string;
  matchId: string;
  compId: string;
  compName?: string;
  scheduledAt: number;
  format: MatchFormat;
  cardStatus: MatchCardStatus;
  role: 'PLAYER' | 'LEADER' | 'ADMIN_OBSERVER';
  myClanId?: string;
  opponentClanId: string;
  opponentClanName: string;
  createdAt: number;
  // Demo-data tag (2026-07-21): a personal calendar entry pulled from a
  // demo/throwaway match (see index.ts's isDemo convention on MATCH# rows).
  // Unlike GET /competitions (which excludes demo comps outright), the
  // per-user calendar does NOT filter these out server-side — they're
  // tagged instead so a demo match a tester is genuinely part of still
  // shows up, just visibly marked. Absent (old payloads / real matches)
  // renders exactly as before — see Calendar.tsx's CalendarTile.
  isDemo?: boolean;
}

// ── Match card agreement (Lisa, 2026-07-23) ─────────────────────────────────
// The three-party agreement on a card's DATE AND TIME. Completing it is what
// builds the card. Mirrors shared/matchCardAgreement.ts — see that module for
// why the party SET varies by who created the match (an organiser is a
// signatory only when the organiser scheduled it).
export type AgreementParty = 'admin' | 'clan1' | 'clan2';

export interface CardAgreementResult {
  ok: boolean;
  party: AgreementParty;
  /** True when this party had already answered — a no-op, not an error. */
  alreadyAgreed?: boolean;
  allAgreed?: boolean;
  /** Only ever true on the write that landed the LAST signature. */
  cardBuilt?: boolean;
  outstanding: AgreementParty[];
  disagreeing?: AgreementParty[];
}

/** One party's line on the card, derived from the raw match row. */
export interface AgreementLine {
  party: AgreementParty;
  label: string;
  state: 'agreed' | 'objected' | 'waiting';
  by?: string;
  auto?: boolean;
}

export interface MatchCardV2Response {
  match: Record<string, unknown> & {
    pk: string; sk: string;
    clan1: string; clan2: string;
    // See MatchDetail.clan1DisplayName's doc comment — same denormalized
    // display-name stamp threaded through matchCardV2.ts's raw-row spread.
    clan1DisplayName?: string;
    clan2DisplayName?: string;
    // See MatchDetail.clan1Id/clan1TeamNo's doc comments — same fields,
    // same raw-row spread (handleGet does `{ ...match, clan1Display, ... }`,
    // so these ride through with zero extra backend code).
    clan1Id?: string;
    clan2Id?: string;
    clan1TeamNo?: number;
    clan2TeamNo?: number;
    leader1Id?: string; leader2Id?: string;
    cardStatus?: MatchCardStatus;
    format?: MatchFormat;
    gameModes?: string[];
    slotsExpected?: number;
    slotsSubmitted?: number;
    teamSize?: 4 | 6 | 8;
    clan1RosterSnapshot?: string[];
    clan2RosterSnapshot?: string[];
    // Parallel arrays (same index as the RosterSnapshot above) resolving each
    // Discord id to its best display name — undefined/null entries mean no
    // usable profile name was found; callers fall back to a truncated id.
    clan1Display?: Array<string | undefined>;
    clan2Display?: Array<string | undefined>;
    rosterLockedAt?: number;
    mapVeto?: MapVetoState;
    forgedCardUrl?: string;
    cancelledReason?: string;
    scheduledAt?: number;
    // T4 (division fixtures & weekly schedule) — see MatchSummary's identical
    // fields' doc comment; same shared/matchDateAgreement.ts authority. Rides
    // this raw-row spread the same way leader1Id/leader2Id above already do.
    proposedScheduledAt?: number;
    dateProposedBy?: string;
    dateAgreementStatus?: 'AWAITING';
    streamerUrl?: string;
    // Engine Wiring Arc 1 — see MatchDetail.stageId's doc comment above.
    stageId?: string;
    stageType?: string;
    // Stashed by ocrBatchRunner at the end of every digestion run (see
    // src/lambda/ocrBatchRunner/index.ts) — server-canonical, no round-trip
    // needed to fetch it separately. Was read via an unsafe inline cast in
    // MatchCardDemo.tsx (only `elapsedMs`/`cost.estimatedUsd`); typed
    // properly here as of Wave 6 so MatchCardLive.tsx can also render the
    // per-cell consensus. Mirrors src/shared/ocrPipelineV2.ts's own
    // `CellConsensus`/`PipelineResult['costSummary']` shapes — NOT the
    // differently-shaped (fuller, but unused-by-this-pipeline) `CellConsensus`
    // in src/shared/types.ts, which has flags/hasOverride/computedAt fields
    // this runner never actually populates.
    ocrBatchSummary?: {
      completedAt: number;
      elapsedMs: number;
      shotsProcessed: number;
      consensus: ServerCellConsensusRow[];
      cost: {
        stage1TokensIn: number;
        stage1TokensOut: number;
        stage1Calls: number;
        stage2TokensIn: number;
        stage2TokensOut: number;
        stage2Calls: number;
        estimatedUsd: number;
      };
    };
    // Arc 3 (scoring unification) — stamped by POST .../confirm-scores, the
    // SAME shape POST /match/:matchId/result writes: games[] + nightWinner +
    // status CONFIRMED is what computeStandings/R2/R4 actually read. A match
    // confirmed by the OLD (pre-Arc-3) confirm-scores has scoresConfirmedAt/By
    // but no games/nightWinner — still a valid historical row, just invisible
    // to standings (nothing migrates it).
    games?: MatchCardGameResult[];
    nightWinner?: string;
    scoresConfirmedAt?: number;
    scoresConfirmedBy?: string;
    // The match's top-level MatchStatus — independent of cardStatus above
    // (the card's OCR/publish lifecycle). PENDING until POST .../confirm-
    // scores (or manual result entry) sets it to CONFIRMED — that write is
    // what makes a match reach STANDINGS. Was already riding through this
    // raw row spread (handleGet in matchCardV2.ts does `{ ...match, ... }`)
    // but ungated: a match could reach cardStatus FORGED (terminal) while
    // this stayed PENDING forever, invisible in standings with no confirm
    // affordance left. Read by ForgedPanel/isForgeBlocked below to gate the
    // forge action on it — see matchCardV2.ts's isResultConfirmed guard,
    // the backend authority for this same check.
    // AWAITING_AGREEMENT/CANCELLED added (match-agreement gate, 2026-07-17)
    // — see shared/types.ts's MatchStatus enum, the backend authority this
    // union mirrors; AgreementBanner below reads this same field.
    status?: 'PENDING' | 'IN_PROGRESS' | 'AWAITING_CONFIRMATION' | 'CONFIRMED' | 'DISPUTED' | 'AWAITING_AGREEMENT' | 'CANCELLED';
  };
  slots: SubmissionSlotRow[];
  slotsByGame: Record<number, SubmissionSlotRow[]>;
  vetoMoves: VetoMoveRow[];
  comments: MatchCommentRow[];
  vetoGuide?: VetoGuide;
  // Co-branding for the downloadable veto card (Lisa, 2026-07-23). Resolved
  // server-side ONLY once the veto is LOCKED, from the comp's parent org, via
  // the same shared read-only resolvers the site's /ggveto card uses: the org's
  // own uploaded logo (absolute CDN URL) + accent colour, plus any hardcoded
  // client skin ('egl'). Absent when the veto is unlocked or the org has no
  // branding — the card then renders GG-Anvil-only. Drives the portal's own
  // canvas renderer; the site veto is untouched.
  vetoCardBranding?: { logoUrl?: string; color?: string; brand?: string };
  // Delegation-visibility audit (T3, 2026-07-21) — teamNos where the viewing
  // user holds an ACTIVE captain grant, keyed by the RAW clan identity
  // matchCardV2's sideClanId() emits (match.clan1/match.clan2 verbatim —
  // the SAME value already threaded into SlotGrid as clan1Id/clan2Id).
  // Optional/absent (older fixtures, or the delegation overlay off) degrades
  // to today's leader/admin-only gating — see lib/matchCaptainAccess.ts.
  viewerCaptainOf?: Record<string, number[]>;
  // Team-scoped result sign-off (stage 2, 2026-07-22). Each side signs off its
  // OWN rows; the operator's verify then confirms two sign-offs rather than
  // being a lone judgement call. `state` is derived server-side
  // (shared/matchSignoff.ts) so the card and its verification checklist read
  // ONE authority instead of each working out "are both sides in" separately.
  // Optional — absent on a response cached from before this shipped, which
  // correctly reads as "nobody has signed".
  signoff?: MatchSignoffState;
  summary: {
    /** F6: ALREADY REDUCED by slots for games the series never reached — read
     *  it as "how many are actually owed", which is what every consumer
     *  already assumed it meant. */
    slotsExpected: number;
    /** F6 (Lisa, 2026-07-23): slots for games that were never played because
     *  the series was already won. Reported so the UI can EXPLAIN a smaller
     *  expectation ("game 3 wasn't needed") rather than silently showing a
     *  different number than the one planned at init. */
    slotsVoid?: number;
    slotsSubmitted: number;
    slotsDone: number;
    slotsMissed: number;
    slotsFlagged: number;
  };
}

// T12: where the CDL veto sequence stands — derived server-side, never stored.
export interface VetoGuide {
  stepIndex: number;
  totalSteps: number;
  complete: boolean;
  action?: 'BAN' | 'PICK';
  mode?: string;
  gameNum?: number;
  actorClanId?: string;
  pool?: string[];
}

export interface MatchCardInitInput {
  compId: string;
  format: 'BO3' | 'BO5' | 'BO7';
  scheduledAt: number;
  teamSize?: 4 | 6 | 8;
  clan1Roster: string[];
  clan2Roster: string[];
  compName?: string;
  adminObservers?: string[];
}

export interface VetoMoveInput {
  compId: string;
  action: 'BAN' | 'PICK';
  mapName: string;
  mode: string;
  gameNum?: number;
}

// ── Cleanup / pool / override types ───────────────────────────────────────────

// 2026-07-14 (Lisa — Hardpoint objective columns): 'objectiveKills'/'captures'
// are the Hardpoint equivalent of SND's 'plants'/'defuses' and Overload's
// 'overloads' — a personal per-player stat, sourced ONLY from the uploader's
// own bottom-strip (see consensus.ts's applyUploaderStripPrecedence), never
// from cross-shot table votes. Deliberately NOT added to matchCardV2.ts's
// backend VALID_FIELDS/VALID_FLAG_FIELDS (admin cell-override / member
// score-flag) yet — see MatchCardLive.tsx's read-only gate on these two
// fields for why a click-to-edit/flag affordance would silently 400 today.
export type ScoreCellField = 'score' | 'objScore' | 'kills' | 'deaths' | 'time' | 'plants' | 'defuses' | 'overloads' | 'objectiveKills' | 'captures';

export type ShotQualityFlag =
  | 'OK' | 'BLURRY' | 'TILTED' | 'GLARE' | 'INCOMPLETE' | 'LOW_CONFIDENCE'
  | 'DISPUTED' | 'ADMIN_FLAGGED' | 'STAGE2_REJECTED' | 'STAGE1_ERROR' | 'OFF_TOPIC'
  // W-C: a scoreboard name matched neither clan roster (a "stranger") /
  // a submitting-clan roster member never appeared anywhere on the board.
  | 'UNEXPECTED_NAME' | 'MISSING_ROSTER_MEMBER'
  // Mode-match enforcement (Lisa, 2026-07-13): the shot's OCR'd gameMode
  // contradicts the match's planned mode for that gameNum (e.g. a hardpoint
  // screenshot in a S&D slot). Excluded from consensus, same as an
  // admin-excluded shot — see ocrPipelineV2.ts's ShotQualityFlag doc comment.
  | 'MODE_MISMATCH'
  // Strip-owner mismatch (Lisa, 2026-07-14): this shot's own uploader strip
  // (parsed.selectedPlayer.gamertag) snaps to a KNOWN roster player who is
  // DIFFERENT from the roster player this slot's owner resolves to — e.g.
  // Punjabi Pan's screenshot landed in zoey's slot. UNLIKE MODE_MISMATCH,
  // NOT excluded from consensus (the table cells may still be a useful
  // cross-vote) — only the strip's own K/D is refused, see consensus.ts's
  // applyUploaderStripPrecedence/isStripOwnerMismatch. See
  // ocrPipelineV2.ts's ShotQualityFlag doc comment for the full rationale.
  | 'STRIP_OWNER_MISMATCH'
  // Activision ID mismatch (Lisa, 2026-07-14): the keystone that hardens
  // STRIP_OWNER_MISMATCH above — the gamertag's #number (Activision ID
  // discriminator) is a UNIQUE, EXACT identity anchor, unlike a fuzzy-
  // matched display name. This shot's own uploader strip carries a #number
  // that confidently disagrees with the slot owner's own REGISTERED
  // activisionIdNumber (the PUT /me/card write-once lock). A SEPARATE flag
  // from STRIP_OWNER_MISMATCH (not folded into it) because it can catch a
  // swap the name-based check would miss (two players sharing a display
  // name but never a discriminator) — routed through the identical
  // downstream wiring (never excluded from consensus, only this shot's own
  // strip K/D is refused; see matchCardV2.ts's standings-strip-guard). See
  // shared/activisionIdMismatch.ts + ocrPipelineV2.ts's ShotQualityFlag doc
  // comment for the full rationale. PHASE-IN: only ever fires when the slot
  // owner has a registered Activision ID — never when they haven't.
  | 'ACTIVISION_ID_MISMATCH';

// Mirrors src/shared/ocrPipelineV2.ts's `CellConsensus` — the shape actually
// written into match.ocrBatchSummary.consensus by ocrBatchRunner. `playerKey`
// is the raw OCR'd player string lowercased/trimmed (NOT roster-snapped) —
// see frontend/src/lib/consensus.ts's mergeServerConsensus for how this gets
// resolved to a roster display name before rendering.
export interface ServerCellConsensusRow {
  gameNum: number;
  playerKey: string;
  field: ScoreCellField;
  value?: number;
  agreement: number;
  totalReadings: number;
  source: 'stage1' | 'stage2';
}

export type PoolEntrySource =
  | 'STAGE1_OCR' | 'STAGE2_DISAMBIG'
  | 'ADMIN_OVERRIDE' | 'LEADER_OVERRIDE' | 'FOUNDER_OVERRIDE';

export interface ScorePoolEntryRow {
  pk: string;
  sk: string;
  matchId: string;
  compId: string;
  gameNum: number;
  playerKey: string;
  field: ScoreCellField;
  source: PoolEntrySource;
  value: number;
  sourceShotSk?: string;
  actorDiscordId?: string;
  ocrModel?: string;
  ocrConfidence?: number;
  notes?: string;
  createdAt: number;
}

export interface ScoreFlagRow {
  pk: string;
  sk: string;
  matchId: string;
  compId: string;
  gameNum: number;
  playerKey: string;
  field: ScoreCellField | '*';
  flaggerId: string;
  flaggerRole: 'member' | 'leader' | 'admin' | 'founder' | 'org-manager';
  flaggerName?: string;
  note?: string;
  status: 'OPEN' | 'RESOLVED' | 'DISMISSED';
  createdAt: number;
  resolvedAt?: number;
  resolvedBy?: string;
  resolution?: 'EDITED' | 'CONFIRMED_OCR' | 'DUPLICATE';
}

export interface ManualOverrideRow {
  pk: string;
  sk: string;
  matchId: string;
  compId: string;
  actorDiscordId: string;
  actorRole: 'admin' | 'founder' | 'leader';
  kind: 'CELL_VALUE' | 'SHOT_FLAG' | 'SHOT_UNFLAG' | 'CELL_RESET_TO_OCR';
  gameNum?: number;
  playerKey?: string;
  field?: ScoreCellField;
  oldValue?: number | string;
  newValue?: number | string;
  shotSk?: string;
  reason?: string;
  ts: number;
}

// ── Submissions ───────────────────────────────────────────────────────────────

export interface PendingSubmission {
  matchId: string;
  compId: string;
  clan1: string;
  clan2: string;
  missingModes: GameMode[];
  deadline?: number;
  hasPartialSubmission: boolean;
}

export interface PendingSubmissionsResponse {
  pending: PendingSubmission[];
}

export interface PlayerSubmitInput {
  compId: string;
  gameMode: GameMode;
  s3Key: string;
  gamertag?: string;
  enhanced?: boolean;
  targetDiscordId?: string;
  targetGamertag?: string;
  ocrTier?: OcrTier;
}

// ── Match Card Submissions ──────────────────────────────────────────────────

export interface ScoreboardSubmitInput {
  compId: string;
  gameMode: GameMode;
  s3Key: string;
  ocrTier?: OcrTier;
}

export type PipelineStatus = 'QUEUED' | 'LAYOUT_GATE' | 'PREPROCESS' | 'TESSERACT' | 'AI_OCR' | 'CLEANING' | 'VALIDATING' | 'POST_PROCESS' | 'DONE' | 'FAILED' | 'REJECTED';

export interface ScoreboardSubmitResponse {
  ok: boolean;
  submissionId?: string;
  gameMode: GameMode;
  clanId?: string;
  pipelineStatus?: PipelineStatus;
  executionId?: string;
  parsedScoreboard?: ParsedScoreboard;
  ocrStats?: { totalBlocks: number; avgConfidence: number; grade: string };
  crossVerification?: { scoresMatch: boolean; discrepancies: string[] };
  ocrTier?: OcrTier;
  creditsUsed?: number;
  orgCreditBalance?: number;
  platformCreditBalance?: number;
  platformMonthSpend?: number;
}

export interface ScoreboardSubmission {
  discordId: string;
  clanId: string;
  gameMode: GameMode;
  submittedAt: number;
  locked: boolean;
  lockedAt?: number;
  parsedScoreboard?: ParsedScoreboard;
  ocrTier?: OcrTier;
  ocrStats?: { totalBlocks: number; avgConfidence: number; grade: string };
  pipelineStatus?: PipelineStatus;
  pipelineError?: string;
}

export interface PlayerDetailSubmission {
  discordId: string;
  targetDiscordId: string;
  gameMode: GameMode;
  submittedAt: number;
  locked: boolean;
  lockedAt?: number;
  parsedScoreboard?: ParsedScoreboard;
  selectedPlayer?: ParsedScoreboard['selectedPlayer'];
  ocrTier?: OcrTier;
  proxySubmission: boolean;
  ocrStats?: { totalBlocks: number; avgConfidence: number; grade: string };
  pipelineStatus?: PipelineStatus;
  pipelineError?: string;
}

export interface MatchSubmissions {
  match: {
    clan1: string;
    clan2: string;
    clan1Name: string;
    clan2Name: string;
    status: string;
    leader1Id: string;
    leader2Id: string;
    submissionDeadline?: number;
  };
  scoreboards: {
    clan1: { clanName: string; modes: Partial<Record<GameMode, ScoreboardSubmission>> };
    clan2: { clanName: string; modes: Partial<Record<GameMode, ScoreboardSubmission>> };
  };
  crossVerification: Partial<Record<GameMode, { scoresMatch: boolean; discrepancies: string[] }>>;
  playerDetails: Record<string, {
    gamertag: string;
    displayName?: string;
    modes: Partial<Record<GameMode, PlayerDetailSubmission>>;
  }>;
  completeness: {
    clan1: { completedModes: number; totalRequired: number };
    clan2: { completedModes: number; totalRequired: number };
  };
  rosters: {
    clan1: Array<{ gamertag: string; discordId?: string; clanId: string; status: string }>;
    clan2: Array<{ gamertag: string; discordId?: string; clanId: string; status: string }>;
  };
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export interface AdminClan {
  id: string;
  name: string;
  leaderId: string;
  leaderName?: string;
  description?: string;
  // Seat-badge honesty fix (2026-07-21): a server-computed snapshot of this
  // clan's seat mix, so the /admin/clans list can render real numbers
  // without a per-row detail fetch. Absent on old payloads (pre-rollout) or
  // when the server genuinely couldn't count — NEVER treat absence as zero;
  // see lib/seatBadge.ts's resolveSeatBadge, which is the one place that
  // decides what a missing value means for display.
  seatCounts?: { claimed: number; pending: number; ghost: number };
}

export interface AdminFlag {
  pk: string;
  type: FlagType;
  status: FlagStatus;
  discordId: string;
  compId: string;
  matchId?: string;
  detail: string;
  oldValue?: string;
  newValue?: string;
  resolvedBy?: string;
  resolvedAt?: number;
  resolutionNote?: string;
  createdAt: number;
}

export interface AdminRecord {
  pk: string;
  discordId: string;
  // 'HELPER' kept in this UNION (unlike the platform Role type above) even
  // though the tier is retired (2026-07-24, role-spine follow-up Directive
  // 1) — GET /admin/admins can still return a legacy pre-retirement HELPER
  // row (see AdminTiers.tsx's "Legacy Helper Rows" cleanup lever), and typing
  // it out here would make that real API response unrepresentable.
  tier: 'FOUNDER' | 'ADMIN' | 'HELPER';
  assignedBy: string;
  assignedAt: number;
  // Resolved server-side (resolveProfileNames) — never render discordId raw
  // when this is present. Absent when no PROFILE row could be resolved.
  displayName?: string;
}

// ── Discipline flags (v1) ─────────────────────────────────────────────────────
// Private, admin-only disciplinary severity cards against a PLAYER or a
// CLAN. Labels only — no penalty rulebook, no score effects, no player
// notification. NOT the moderation/dispute AdminFlag/FlagStatus above (that's
// a separate, unrelated system) — named `DisciplineFlag...` throughout, and
// exposed under `api.admin.disciplineFlags` (not `api.admin.flags`, which
// already means the dispute-flag list), to avoid colliding with it.
export type FlagSeverity = 'blue' | 'yellow' | 'red' | 'black';
export type DisciplineFlagStatus = 'active' | 'pending' | 'overturned';

export interface DisciplineFlag {
  flagId: string;
  orgId: string;
  targetType: 'player' | 'clan';
  targetId: string;
  targetName?: string;
  severity: FlagSeverity;
  reason: string;
  evidence?: string;
  status: DisciplineFlagStatus;
  issuedBy: string;
  issuedByName?: string;
  createdAt: number;
  sk: string;
  cosignedBy?: string;
  cosignedByName?: string;
  cosignedAt?: number;
  overturnedBy?: string;
  overturnedByName?: string;
  overturnReason?: string;
  overturnedAt?: number;
}

export interface TextractLine {
  text: string;
  confidence: number;
}

export interface TextractTableCell {
  text: string;
  confidence: number;
}

export interface TextractTable {
  tableIndex: number;
  rows: TextractTableCell[][];
}

export interface CodIdCandidate {
  raw: string;
  codId: string;
  confidence: number;
  warning: string | null;
}

export type TeamSide = 'left' | 'right';

export interface ParsedScoreboardColumn {
  key: 'rank' | 'level' | 'player' | 'score' | 'objectiveScore' | 'overloads' | 'time' | 'unknown';
  label: string;
}

export interface ParsedScoreboardRow {
  rank?: number;
  level?: number;
  player?: string;
  score?: number;
  objectiveScore?: number;
  overloads?: number;
  time?: number;
  rawValues: Record<string, string>;
}

export interface ParsedTeamTable {
  side: TeamSide;
  teamName?: string;
  teamScore?: number;
  columns: ParsedScoreboardColumn[];
  rows: ParsedScoreboardRow[];
}

export interface ParsedSelectedPlayerExtraStat {
  label: string;
  value: number;
}

export interface ParsedSelectedPlayer {
  gamertag?: string;
  playerName?: string;
  title?: string;
  teamSide?: TeamSide;
  matchedRowIndex?: number;
  eliminations?: number;
  deaths?: number;
  elimDeathRatio?: number;
  objectiveScore?: number;
  extras: ParsedSelectedPlayerExtraStat[];
}

export interface SystemStats {
  fps?: number;
  latency?: number;
  packetLoss?: number;
  gpuTemp?: number;
}

export interface ParsedScoreboard {
  parserVersion: string;
  completeness: 'high' | 'medium' | 'low';
  rawModeLabel?: string;
  gameMode?: GameMode;
  mapName?: string;
  leftTeamName?: string;
  rightTeamName?: string;
  leftTeamScore?: number;
  rightTeamScore?: number;
  winnerSide?: TeamSide | 'tie';
  teams: ParsedTeamTable[];
  selectedPlayer?: ParsedSelectedPlayer;
  systemStats?: SystemStats;
  warnings: string[];
}

export interface PipelineValidationCheck {
  name: string;
  passed: boolean;
  detail: string;
}

export interface PipelineMetadata {
  gate: { passed: boolean; confidence: number };
  preprocessing: { rotationDeg: number; originalSize: { w: number; h: number } };
  ocr: { engine: string; grade: string; avgConfidence: number };
  corrections: number;
  validation: { passed: boolean; overallConfidence: number; checks: PipelineValidationCheck[] };
}

export interface PipelineResult {
  ready: boolean;
  pk?: string;
  sk?: string;
  status?: string;
  rejectionReason?: string;
  evidenceUrl?: string;
  processedUrl?: string;
  parsedScoreboard?: ParsedScoreboard;
  rawParsedScoreboard?: ParsedScoreboard;
  extractedValues?: Record<string, string>;
  pipeline?: PipelineMetadata;
  previewUrls?: { original?: string; processed?: string };
}

export interface TextractAnalysis {
  s3Key: string;
  previewUrl?: string;
  lines: TextractLine[];
  tables: TextractTable[];
  codIdCandidates: CodIdCandidate[];
  stats: {
    totalBlocks: number;
    avgConfidence: number;
    high: number;
    medium: number;
    low: number;
    grade: 'excellent' | 'good' | 'poor' | 'unreadable';
  };
  parsedScoreboard: ParsedScoreboard;
}

// ── Forms ─────────────────────────────────────────────────────────────────────

export interface CompClan {
  // Clan display-name preservation (Lisa, 2026-07-13): `name` now prefers
  // the enrollment row's denormalized clanName stamp (the clan METADATA's
  // original-casing name — survives special characters like "$kare") over
  // the canonical CLAN#-derived bare form; legacy rows enrolled before the
  // stamp existed still fall back to the derived form server-side. Never
  // compare `name` against a canonical id (e.g. getRole()'s clanName) —
  // use `clanId` for that (see ClanWorkbench.tsx's restrictToClan).
  name: string;
  status?: string;
  seatCount?: number;
  // M1 of the multi-team epic (Lisa, 2026-07-10): teamNo/teamName present
  // only for a team-entry row — `name` is already the team-qualified
  // "TeamName (ClanName)" display in that case; a legacy (whole-clan) entry
  // omits these and `name` stays the bare/display clan name.
  teamNo?: number;
  teamName?: string;
  // clanId is always the canonical CLAN#-prefixed id, regardless of row
  // kind (previously team-rows-only; now always populated server-side).
  clanId?: string;
}

// ── Setup → Seats-step roster overview (Lisa, 2026-07-09) ────────────────────
// GET /competitions/:compId/roster-overview — comp-wide, READ-ONLY glance at
// every enrolled clan's seats, so admin can see who is on which team
// without opening each clan's own workbench.

export interface RosterOverviewSeat {
  gamertag: string;
  status: 'GHOST' | 'PENDING_CLAIM' | 'CLAIMED';
  teamNo: number | null;
  /** Resolved team label (named team, or "Team N" fallback) — absent when teamNo is null (unassigned). */
  teamName?: string;
  /** Resolved display name — only present for CLAIMED seats (house rule: never show raw ids). */
  playerName?: string;
}

export interface RosterOverviewSeatCounts {
  total: number;
  claimed: number;
  pending: number;
  ghost: number;
}

export interface RosterOverviewClan {
  clanId: string;
  clanName: string;
  seats: RosterOverviewSeat[];
  counts: RosterOverviewSeatCounts;
}

export interface RosterOverviewRollup {
  teams: number;
  totalSeats: number;
  claimed: number;
  unclaimed: number;
}

export interface RosterOverview {
  compId: string;
  compName: string;
  startDate: string | null;
  /** True once the comp's startDate has passed — rosters are locked (see shared/compGates.ts). */
  rosterLocked: boolean;
  /** Days until rosters lock; null when startDate is absent or already passed. */
  daysUntilLock: number | null;
  rollup: RosterOverviewRollup;
  clans: RosterOverviewClan[];
}

export interface CreateCompetitionInput {
  name: string;
  teamSize?: number;
  startDate?: string;
  endDate?: string;
  seedingRoundsTotal?: number;
  standingsType?: StandingsType;
  modes?: import('./modes').ModeConfig[];
  seriesFormat?: import('./modes').SeriesFormat;
  format?: CompetitionFormat;
  // Swiss thresholds (format === 'SWISS'); default 3/3 server-side.
  swissWinThreshold?: number;
  swissLossThreshold?: number;
  // Group-stage config (format === 'GROUP_STAGE'); defaults derived server-side.
  groupCount?: number;
  advancePerGroup?: number;
  // W3 (server-as-org): owning org for the bare POST /competitions create.
  // Optional during the soft-stamp transition; pass the workspace entry org.
  orgId?: string;
  // T1 sign-up window (Lisa, 2026-07-16) — unix-ms instants, set at creation
  // ONLY (never editable via UpdateCompetitionInput/PATCH). Additive gate on
  // top of startDate — see shared/compGates.ts on the backend.
  signUpOpensAt?: number;
  signUpClosesAt?: number;
  // Stage-first creation (T2) — an ordered pipeline composed on the create
  // form itself (CompetitionCreateForm.tsx). When present (non-empty), the
  // backend DERIVES `format`/`seedingRoundsTotal` from this list server-side
  // and ignores the flat fields above for those two — see
  // shared/compStages.ts's deriveFormatFromStages/seedingRoundsFromStages.
  stages?: Array<{ type: CompStageType; config?: Record<string, unknown> }>;
}

// PATCH /competitions/:compId — progressive, stage-aware lock (see
// updateCompetition below): fully open while PENDING; while LIVE, name/
// format/modes/seriesFormat stay editable until stage moves past 'SEEDING',
// but teamSize locks the instant status goes LIVE (send it only when NOT
// editing a LIVE comp — the backend rejects the WHOLE request if teamSize is
// present at all on a LIVE comp, even unchanged). orgId/status/stage are NOT
// editable via this endpoint — the backend rejects them outright.
export interface UpdateCompetitionInput {
  name?: string;
  teamSize?: number;
  format?: CompetitionFormat;
  modes?: import('./modes').ModeConfig[];
  seriesFormat?: import('./modes').SeriesFormat;
  // Rules/terms text — writing this bumps comp.rulesVersion server-side
  // (src/shared/compTerms.ts), which is what makes prior clan acceptances
  // stale. Same progressive lock as every other field on this endpoint.
  rules?: string;
}

export interface StartMatchInput {
  compId: string;
  opponentClan: string;
  scheduledAt?: number;
  // Match-team-autofill (Lisa, 2026-07-13): an ADDITIVE explicit pointer to
  // the opponent's enrollment row — set these (from the SAME CompClan row
  // opponentClan's name came from) when the chosen opponent is a specific
  // entered TEAM, so the backend can stamp clan2TeamNo without ever having
  // to parse opponentClan's display string back apart. Omit for a legacy
  // whole-clan opponent — byte-identical to pre-feature.
  opponentClanId?: string;
  opponentTeamNo?: number;
  // T7 (2026-07-24 — closes the Route-2 leak): the proposer's OWN ticked
  // squad, required whenever a captain (not an admin/founder) proposes —
  // the backend stamps this side's card-agreement line at creation
  // (creationAgreementFields), and T6 already forbids that stamp without a
  // valid squad on the agree path; this closes the same gap on the propose
  // path. Validated server-side against the caller's own registered seats
  // and the comp's teamSize ceiling — see FIELDED_SQUAD_REQUIRED/EMPTY/
  // OVER_CEILING/STRANGER on parseApiError's error surface. Meaningless (and
  // ignored) when an admin/founder proposes through this same door — they
  // sign the admin line, which never fields a squad.
  fieldedSquad?: string[];
}

export interface StartMatchResult {
  matchId: string;
  clan1: string;
  clan2: string;
  dmsSent: boolean;
}

export interface CreateClanInput {
  name: string;
  leaderDiscordId?: string;   // optional — defaults to caller unless admin specifies someone else
  description?: string;
  gamertag?: string;
  // W3 (server-as-org): owning org for the clan. Optional during the
  // soft-stamp transition; pass the workspace entry org.
  orgId?: string;
}

export interface OrganizationBilling {
  plan: BillingPlan;
  interval: BillingInterval;
  entitlementStatus: EntitlementStatus;
  currentPeriodStart?: number;
  currentPeriodEnd?: number;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

export type OrgPaymentStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED';

// ── Observability (org-scoped audit log) ──────────────────────────────────────
// Read-side shape for GET /admin/org-logs. Mirrors the write engine's
// BuiltLogItem (src/shared/observability.ts) — only the fields the
// Observability dashboard actually renders are declared here.
export type LogSeverity = 'info' | 'warn' | 'error';

export interface OrgLogRecord {
  sk: string;
  action: string;
  actorId?: string;
  target?: string;
  targetType?: string;
  severity: LogSeverity;
  category?: string;
  source: string;
  details?: Record<string, unknown>;
  createdAt: number;
  correlationId?: string;
}

// ── Observatory (ob-A5: founder trace view) ─────────────────────────────────
// Read-side shapes for the trace-view endpoints. actor/timeline share
// ActivityRecord (above) — same ACTIVITY#/TIMELINE spine as Activity Log,
// just with date-range + cursor pagination on top.
export interface ObservatoryActorPage {
  items: ActivityRecord[];
  nextToken?: string;
}

export interface ObservatoryTracePage {
  items: ActivityRecord[];
  note?: string;
}

export interface ObservatoryTimelinePage {
  items: ActivityRecord[];
}

// Mirrors src/lambda/costMonitor/index.ts's buildSnapshotItem output — the
// COST_SNAPSHOT row (pk 'COST_SNAPSHOT', sk 'DAY#<yyyy-mm-dd>').
export type CostBandStatus = 'ok' | 'warn' | 'alert';

export interface CostAccountRow {
  id: string;
  name: string;
  mtdUsd: number;
  yesterdayUsd: number;
}

export interface CostServiceRow {
  service: string;
  usd: number;
}

export interface CostSnapshot {
  sk: string; // 'DAY#<yyyy-mm-dd>'
  estateMtdUsd: number;
  forecastUsd: number;
  byAccount: CostAccountRow[];
  topServices: CostServiceRow[];
  bandStatus: CostBandStatus;
  reasons: string[];
  createdAt: number;
}

export interface ObservatoryCostsPage {
  items: CostSnapshot[];
}

// ── Clickstream dashboard (GET /admin/clickstream) ──────────────────────────
// Read-side shape for the founder clickstream VIEW. Mirrors the backend's
// server-side aggregation exactly (src/lambda/portalApi/index.ts) — this is
// aggregates ONLY, no raw event rows ever leave the API, so the shape here
// is deliberately small regardless of how busy the requested day was.
export type ClickKind = 'button' | 'link' | 'other';

export interface ClickstreamTopLabel {
  label: string;
  kind: ClickKind;
  clicks: number;
}

export interface ClickstreamTopRoute {
  route: string;
  clicks: number;
}

export interface ClickstreamDaySummary {
  day: string;
  batches: number;
  totalClicks: number;
  /** Events skipped from every tally above (non-finite ts) — surfaced so a
   *  data-quality problem is visible instead of silently depressing the
   *  charts while totalClicks still ticks up (2026-07-21 audit fix). */
  malformedEvents: number;
  rejectedTotal: number;
  actors: { authed: number; anonBatches: number };
  topLabels: ClickstreamTopLabel[];
  topRoutes: ClickstreamTopRoute[];
  /** 24 entries, index = UTC hour. */
  byHour: number[];
}

// ── Founder analytics ────────────────────────────────────────────────────────

/** One day on the acquisition axis. Dense: quiet days are present with 0. */
export interface AcquisitionDay {
  day: string;
  arrivals: number;
}

/**
 * GET /admin/observatory/acquisition — "who came in when", derived from the
 * POPIA consent rows via GSI1. Counts only; the API never emits an identity.
 */
export interface AcquisitionSeries {
  windowDays: number;
  from: string;
  to: string;
  /** True when the page cap was hit — the series is an UNDERCOUNT, say so in the UI. */
  truncated: boolean;
  days: AcquisitionDay[];
  /** Every consent row ever, not just the window. */
  totalUsers: number;
  inWindow: number;
  malformed: number;
  firstArrival: string | null;
  lastArrival: string | null;
}

/**
 * One permanent daily audit rollup (pk='ROLLUP'), written by src/lambda/dailyRollup.
 *
 * `uniqueActors` is a COUNT and has no id roster — the rollup deliberately does
 * not retain one, and the API strips it from any legacy row that still carries
 * it. topActors DOES name people and is purge-covered (ROLLUP_DEIDENTIFY);
 * a purged user appears as 'FORMER_PLAYER'.
 */
export interface RollupDay {
  sk: string;
  day: string;
  totalEvents: number;
  byAction: Record<string, number>;
  bySource: Record<string, number>;
  uniqueActors: { count: number };
  /** 24 entries, index = UTC hour. */
  byHour: number[];
  topActors: Array<{ actorId: string; count: number }>;
  errorCount: number;
  truncated: boolean;
  computedAt: number;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  ownerDiscordId: string;
  billingEmail?: string;
  primaryGuildId?: string;
  accentColor?: string | null;
  enabledFormats?: CompetitionFormat[];
  createdAt: number;
  /** How this org came into existence. 'discord_install' means the bot minted
   *  it for itself when it was added to a server — nobody provisioned it.
   *  null for every org created through a normal provisioning door. */
  createdVia?: string | null;
  membershipRole?: OrganizationRole | null;
  billing?: OrganizationBilling | null;
  // Calendar/lifecycle — optional
  goLiveAt?: number;
  trialExpiresAt?: number;
  paymentStatus?: OrgPaymentStatus;
  nextBillingAt?: number;
}

// CreateOrganizationInput removed in Phase 3.5 alongside POST /orgs.
// Use createMyOrg's inline param shape: { name, billingEmail?, primaryGuildId?, slug?, reason? }.

export interface CompetitionRequest {
  pk: string;
  sk: string;
  orgId: string;
  requestedByDiscordId: string;
  name: string;
  status: CompetitionRequestStatus;
  visibility: CompetitionVisibility;
  teamCountEstimate: number;
  teamSize: number;
  projectedPlayerCount?: number;
  projectedAdminCount?: number;
  quotedAmountUsdCents: number;
  quotedCurrency: 'usd';
  plan: BillingPlan;
  interval: BillingInterval;
  desiredStartAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface CreateCompetitionRequestInput {
  name: string;
  teamCountEstimate: number;
  teamSize: number;
  projectedPlayerCount?: number;
  projectedAdminCount?: number;
  desiredStartAt?: number;
  visibility?: CompetitionVisibility;
}

export interface CheckoutSessionResponse {
  ok: boolean;
  provider: 'stripe-placeholder';
  url: string;
}

// ── Credits ──────────────────────────────────────────────────────────────────

// ── Asset types ──────────────────────────────────────────────────────────────
export type AssetOwnerType = 'org' | 'clan' | 'user' | 'platform';
export type AssetType = 'logo' | 'banner' | 'avatar';

export interface Asset {
  assetId: string;
  pk: string;
  sk: string;
  assetType: AssetType;
  s3Key: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string;
  createdAt: number;
  url: string;
  allocatedFrom?: string;
  /** CSS object-position ("x% y%") — the focal point to display on crop. Absent = centre. */
  objectPosition?: string;
}

export interface AssetConfig {
  storageCap: number;
  storageUsed: number;
}

export type CreditPack = 'STARTER_50' | 'VALUE_200' | 'PRO_500';
export type CreditTransactionType = 'PURCHASE' | 'DEBIT' | 'REFUND' | 'GRANT';
export type OcrTier = 'haiku' | 'haiku-player' | 'tesseract';

export interface OrgCreditsResponse {
  orgId: string;
  balance: number;
  totalPurchased: number;
  totalUsed: number;
  lastPurchaseAt?: number;
}

export interface CreditTransaction {
  sk: string;
  type: CreditTransactionType;
  amount: number;
  balanceAfter: number;
  reason: string;
  purchasedBy?: string;
  consumedBy?: string;
  matchId?: string;
  createdAt: number;
}

export interface CreditCheckoutResponse {
  ok: boolean;
  authorization_url: string;
  reference: string;
}

export interface OrgCreditBalanceSummary {
  orgId: string;
  balance: number;
}

export interface PlayerSubmitResponse {
  ok: boolean;
  submissionId?: string;
  gameMode: GameMode;
  locked?: boolean;
  pipelineStatus?: PipelineStatus;
  executionId?: string;
  parsedScoreboard?: ParsedScoreboard;
  playerStats?: {
    gamertag?: string;
    eliminations?: number;
    deaths?: number;
    elimDeathRatio?: number;
    objectiveScore?: number;
    extras?: Array<{ label: string; value: number }>;
  };
  ocrTier?: OcrTier;
  creditsUsed?: number;
  orgCreditBalance?: number;
  platformCreditBalance?: number;
  platformMonthSpend?: number;
  proxySubmission?: boolean;
  targetDiscordId?: string;
  antiCheat?: {
    detectedPlayer: string | null;
    expectedPlayer: string | null;
    matched: boolean;
  };
  submitter?: {
    discordId: string;
    displayName?: string;
    gamertag?: string;
    role?: string;
    clanName?: string;
  };
  ocrStats?: { totalBlocks: number; avgConfidence: number; grade: string };
}

// ── Platform Credits (Founders) ─────────────────────────────────────────────

export interface PlatformCreditsResponse {
  balance: number;
  totalUsed: number;
  totalGranted: number;
  monthlySpendCap: number;
  currentMonthSpend: number;
  currentMonthKey: string;
  updatedAt: number;
}

export interface PlatformCreditTransaction {
  sk: string;
  type: 'GRANT' | 'DEBIT';
  amount: number;
  balanceAfter: number;
  reason: string;
  consumedBy?: string;
  matchId?: string;
  createdAt: number;
}

export interface AllOrgCreditsRow {
  orgId: string;
  orgName: string;
  balance: number;
  totalPurchased: number;
  totalUsed: number;
}

export interface PlatformCreditsSummary {
  balance: number;
  monthlySpendCap: number;
  currentMonthSpend: number;
}

export interface ScopedOrgMember {
  discordId: string;
  role: OrganizationRole;
  joinedAt?: number;
  invitedBy?: string;
  clanName?: string;
  gamertag?: string;
  displayName?: string;
}

// ── Server import — GET /orgs/:orgId/members pagination + background import ────
// The endpoint is migrating from a bare array to a cursor-paginated envelope
// with an inline import-progress projection. Until the backend half lands,
// it may still return the bare array — both shapes are handled by
// lib/orgMembersPaging.ts's normalizeOrgMembersResponse, never assumed here.

export interface OrgMemberImportStatus {
  state: 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';
  pagesDone: number;
  membersImported: number;
  updatedAt: number;
  finishedAt?: number;
  error?: string;
}

export interface OrgMembersResponse {
  members: ScopedOrgMember[];
  nextCursor?: string;
  importStatus?: OrgMemberImportStatus;
}

export interface OrgBan {
  discordId: string;
  gamertag?: string;
  displayName?: string;
  reason?: string;
  bannedAt: number;
  bannedBy: string;
}

// ── Org governance — voting engine (PR2 backend / PR4 frontend) ─────────────────

export type OrgProposalType = 'REMOVE_OWNER';
export type OrgProposalQuorumRule = 'majority' | 'unanimous';
export type OrgVoteChoice = 'yes' | 'no';
export type OrgProposalStatus = 'OPEN' | 'EXECUTED' | 'REJECTED' | 'EXPIRED' | 'WITHDRAWN';

export interface OrgProposalVoteRecord {
  voterDiscordId: string;
  vote: OrgVoteChoice;
  votedAt: number;
}

export interface OrgProposalTally {
  ownerCount: number;
  yes: number;
  no: number;
  totalVotes: number;
}

export interface OrgProposal {
  proposalId: string;
  orgId: string;
  type: OrgProposalType;
  proposerDiscordId: string;
  targetDiscordId: string;
  quorumRule: OrgProposalQuorumRule;
  status: OrgProposalStatus;
  createdAt: number;
  expiresAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  resolutionReason: string | null;
  tally: OrgProposalTally;
  votes: OrgProposalVoteRecord[];
}

// vote/withdraw/resolve return bare `{ error: 'proposal_expired', status }` or
// `{ error: 'not_open', status }` with NO `message` field (unlike create's
// sole_owner/target_not_owner/etc, which do carry one) — parseApiError's
// generic fallback would otherwise surface the raw code. Map those two here
// so every governance action shows a human sentence, never a bare enum value.
export function describeOrgProposalError(err: unknown): string {
  const parsed = parseApiError(err);
  if (parsed.code === 'proposal_expired') {
    return 'This vote expired before your action landed. Refresh to see its final status.';
  }
  if (parsed.code === 'not_open') {
    const status = typeof parsed.raw?.status === 'string' ? parsed.raw.status : 'resolved';
    return `This vote is no longer open (now ${status}). Refresh to see the outcome.`;
  }
  return parsed.message;
}

// ── Activity / Audit Types ────────────────────────────────────────────────────

export interface ActivityRecord {
  sk: string;
  actorId?: string;
  // Resolved display name for actorId, stamped server-side by the
  // /admin/activity/* handlers via resolveProfileNames. Absent whenever the
  // actor has no usable PROFILE row (deleted user, POPIA-purged
  // 'FORMER_PLAYER' tombstone) — always render `actorName ?? actorId`, never
  // assume it is present.
  actorName?: string;
  action: string;
  target: string;
  targetType: string;
  details: Record<string, unknown>;
  source: 'portal' | 'discord' | 'system';
  createdAt: number;
  // Top-level correlationId — populated only once the (parallel, in-flight)
  // correlation-id-spine PR lands; older rows keep it nested under
  // details.correlationId instead. The trace view checks both — see
  // frontend/src/pages/TraceView.tsx's recordCorrelationId().
  correlationId?: string;
}

export interface UserLookupResponse {
  discordId: string;
  profile: {
    gamertag?: string;
    discordUsername?: string;
    discordAvatar?: string;
    discordFlags?: number;
    isSpammer?: boolean;
    accountCreatedAt?: number;
    enrichedAt?: number;
  } | null;
  clanMemberships: Array<{ clanId: string; role: string; gamertag?: string; joinedAt?: number }>;
  orgMemberships: Array<{ orgId: string; role: string; joinedAt?: number }>;
  compMemberships: Array<{ compId: string; role: string; joinedAt?: number }>;
  recentActivity: ActivityRecord[];
}

export interface PlatformMetrics {
  totalClans: number;
  actionsThisMonth: number;
  platformCredits: { balance: number; totalUsed: number; totalGranted: number; currentMonthSpend: number };
  computedAt: number;
}

export interface SimulateTournamentResponse {
  ok: boolean;
  compId: string;
  clans: string[];
  playerCount: number;
}

export interface ScopedCompetitionMember {
  discordId: string;
  displayName?: string;
  role: CompetitionRole;
  joinedAt?: number;
  assignedBy?: string;
  assignedByName?: string;
  orgId?: string;
}

export interface ScopedClanMember {
  discordId: string;
  gamertag?: string;
  displayName?: string;
  role: 'LEADER' | 'MEMBER';
  scopedRole: ClanScopedRole;
  joinedAt?: number;
  orgId?: string;
  compId?: string;
}

export interface RolePolicyRecord {
  scope: RolePolicyScope;
  role: string;
  disabledCapabilities: string[];
  updatedAt?: number;
  updatedBy?: string;
}

export interface RolePolicyBundle {
  defaults: Record<RolePolicyScope, string[]>;
  records: RolePolicyRecord[];
}

export interface SubmitScoresInput {
  compId: string;
  s3Key?: string;
  scores?: Array<{ mode: string; score: string }>;
}

// ── Player Stats ─────────────────────────────────────────────────────────────

export interface ModeStats {
  gamesPlayed: number;
  totalEliminations: number;
  totalDeaths: number;
  kd: number;
  totalScore: number;
  totalObjectiveScore: number;
  totalOverloads?: number;
  totalTime?: number;
}

export interface PlayerStats {
  discordId: string;
  scope: 'competition' | 'lifetime';
  compId?: string;
  matchesPlayed: number;
  gamesPlayed: number;
  totalEliminations: number;
  totalDeaths: number;
  overallKd: number;
  modeStats: Partial<Record<GameMode, ModeStats>>;
  bestKd?: number;
  bestEliminations?: number;
  updatedAt: number;
}

// ── My Card (Phase 2) ────────────────────────────────────────────────────────

export interface MyCardSocialLinks {
  tiktok?: string | null;
  youtube?: string | null;
  discord?: string | null;
  twitter?: string | null;
  twitch?: string | null;
}

export interface MyCardModeStats {
  gamesPlayed: number;
  totalScore: number;
  totalObjectiveScore: number;
  totalEliminations: number;
  totalDeaths: number;
  kd: number;
  totalOverloads?: number;
  totalTime?: number;
}

export interface MyCardLifetimeStats {
  matchesPlayed: number;
  gamesPlayed: number;
  totalEliminations: number;
  totalDeaths: number;
  overallKd: number;
  bestKd?: number | null;
  bestEliminations?: number | null;
  totalScore?: number;
  totalObjScore?: number;
  totalOverloads?: number;
  totalTime?: number;
  modeStats?: Record<string, MyCardModeStats>;
}

export interface MyCardResponse {
  discordId: string;
  initialised: boolean;
  displayName: string | null;
  gamertag: string | null;
  /** Set the FIRST time a gamertag is successfully saved — write-once lock (#294).
   *  Once set, only an admin can change the gamertag (player PUTs 403). */
  gamertagLockedAt?: number | null;
  /** discordId of whoever performed the locking set (player themself, or an
   *  admin acting on the player's behalf via PATCH /admin/users/:id/gamertag). */
  gamertagSetBy?: string | null;
  description: string | null;
  avatarAssetId: string | null;
  bannerAssetId: string | null;
  /** Focal point ("x% y%") for the Discord banner strip crop. null = centre. */
  bannerObjectPosition: string | null;
  cardColor: string | null;
  discordUsername: string | null;
  discordAvatar: string | null;
  socialLinks?: MyCardSocialLinks | null;
  updatedAt: number | null;
  stats: MyCardLifetimeStats | null;
}

// All keys are optional; only submitted keys are written. `null` clears.
export interface MyCardUpdateInput {
  gamertag?: string | null;
  displayName?: string | null;
  description?: string | null;
  cardColor?: string | null;
  /** User-picked theme accent (#RRGGBB) — drives the whole-app accent palette. */
  accentOverride?: string | null;
  socialLinks?: MyCardSocialLinks | null;
  avatarAssetId?: string | null;
  bannerAssetId?: string | null;
  /** Focal point ("x% y%") for the Discord banner strip crop. null = centre. */
  bannerObjectPosition?: string | null;
}

// ── Org Clan Affiliation ──────────────────────────────────────────────────────

export interface OrgClanMemberEntry {
  clanId: string;
  name: string;
  leaderId: string | null;
  leaderName?: string;
}

export interface OrgClanRequestEntry {
  clanId: string;
  orgId: string;
  requestedBy: string;
  requestedByName?: string;
  createdAt: number | null;
  status: string;
}

export interface OrgClanInviteEntry {
  clanId: string;
  orgId: string;
  createdBy: string;
  createdAt: number | null;
  status: string;
}

export interface OrgClansResponse {
  memberClans: OrgClanMemberEntry[];
  pendingRequests: OrgClanRequestEntry[];
  pendingInvites: OrgClanInviteEntry[];
}

// ── My Org / Discovery (Phase 3.5) ────────────────────────────────────────────

export interface CreateMyOrgImmediateResponse {
  id: string;
  name: string;
  role: OrganizationRole;
}

export interface CreateMyOrgRequestPendingResponse {
  status: 'request_pending';
  requestId: string;
  createdAt: number;
  proposedOrgName: string;
  proposedOrgSlug: string;
  pendingCount: number;
  pendingLimit: number;
  message: string;
}

export type CreateMyOrgResponse =
  | CreateMyOrgImmediateResponse
  | CreateMyOrgRequestPendingResponse;

export type OrgCreationRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'DENIED'
  | 'WITHDRAWN';

export interface MyOrgCreationRequestSummary {
  requestId: string;
  requesterDiscordId: string;
  proposedOrgName: string;
  proposedOrgSlug: string | null;
  reason: string;
  status: OrgCreationRequestStatus;
  createdAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  resolverNote: string | null;
}

export interface OrgCreationRequestInboxRow extends MyOrgCreationRequestSummary {
  requesterDisplayName: string | null;
}

export interface RoleRequestInboxRow {
  requestId: string;
  orgId: string;
  requesterDiscordId: string;
  requesterDisplayName: string | null;
  currentRole: string;
  requestedRole: string;
  scope: 'ORG' | 'COMP';
  scopeCompId: string | null;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED' | 'WITHDRAWN';
  createdAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  resolverNote: string | null;
}

export interface DiscoverableOrgSummary {
  orgId: string;
  name: string;
  slug: string | null;
  description: string | null;
  memberCount: number;
}

export interface DiscoverableClanSummary {
  clanId: string;
  name: string;
  description: string | null;
  leaderId: string | null;
  memberCount: number;
}

// ── Role-requests ─────────────────────────────────────────────────────────────
// Phase 4. The shared payload between GET /me/role-requests, POST submit, and
// POST withdraw. Mirrors the backend RoleRequestUserIndex projection.

export type RoleRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'DENIED'
  | 'WITHDRAWN';

export type RoleRequestScope = 'ORG' | 'COMP';

export interface MyRoleRequestSummary {
  requestId: string;
  orgId: string;
  requesterDiscordId: string;
  currentRole: string;
  requestedRole: string;
  scope: RoleRequestScope;
  scopeCompId: string | null;
  status: RoleRequestStatus;
  reason: string;
  createdAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  resolverNote: string | null;
}

export interface SubmitRoleRequestInput {
  currentRole: string;        // user's claimed current role; server re-checks
  requestedRole: string;      // 'COMP_ADMIN' | 'ORG_ADMIN' | 'ORG_OWNER'
  scopeCompId?: string;       // required iff requestedRole === 'COMP_ADMIN'
  reason: string;
}

export interface SubmitRoleRequestResponse {
  requestId: string;
  orgId: string;
  status: RoleRequestStatus;
  currentRole: string;
  requestedRole: string;
  scope: RoleRequestScope;
  scopeCompId: string | null;
  createdAt: number;
  pendingCount: number;
  pendingLimit: number;
}

// ── Clan-create requests (T3 — server-is-the-org) ─────────────────────────────
// Member-gated clan creation: an org member applies via
// POST /orgs/<orgId>/clan-create-requests instead of the direct POST /me/clan
// (which now 403s once an org context resolves — see BuildClanScreen.tsx).
// Org owners/admins review via GET/approve/deny; the requester tracks their
// own submissions via GET /me/clan-create-requests. Mirrors the
// role-requests shapes above almost exactly (same PENDING/APPROVED/DENIED
// lifecycle, same resolverNote-on-deny convention).

export type ClanCreateRequestStatus = 'PENDING' | 'APPROVED' | 'DENIED';

export interface ClanCreateRequestInboxRow {
  requestId: string;
  orgId: string;
  requesterDiscordId: string;
  requesterDisplayName: string | null;
  name: string;
  description: string | null;
  gamertag: string | null;
  status: ClanCreateRequestStatus;
  createdAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  resolverNote: string | null;
  clanId: string | null;
}

export interface MyClanCreateRequestSummary {
  requestId: string;
  orgId: string;
  name: string;
  description: string | null;
  gamertag: string | null;
  status: ClanCreateRequestStatus;
  createdAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  resolverNote: string | null;
  clanId: string | null;
}

export interface SubmitClanCreateRequestInput {
  name: string;
  description?: string;
  gamertag?: string;
}

// ── Org public page ───────────────────────────────────────────────────────────

// Season Results — manual-entry standings editor. An admin either hand-builds
// a division's finishing order or imports it from a live competition's
// divisions/standings (see OrgPublicPageEditor's "Import from a competition").
// logoUrl is the ONLY source for a team's logo on the public page — it comes
// straight from the division standings row (DivisionTeamScore.logoUrl) at
// import time, never uploaded/entered separately here.
export interface OrgSeasonTeam {
  clanName: string;                 // required — canonical id
  displayName?: string;
  logoUrl?: string;
  score?: number;
  place?: number;                   // 1-based finishing place
}

export interface OrgSeasonDivision {
  tier: string;                     // display label, e.g. "Challengers"
  teams: OrgSeasonTeam[];
}

export interface OrgSeason {
  title: string;                    // e.g. "EGL Season 3"
  divisions: OrgSeasonDivision[];
}

export interface OrgPublicPageContent {
  published: boolean;
  hero?: {
    logoAssetUrl?: string;
    logoObjectPosition?: string;
    bannerAssetUrl?: string;
    bannerObjectPosition?: string;
    tagline?: string;
  };
  about?: { whoWeAre?: string; howWeWork?: string };
  contact?: { email?: string; discordInviteUrl?: string; website?: string; socials?: { label: string; url: string }[]; note?: string };
  partners?: { logoAssetUrl?: string; logoObjectPosition?: string; name: string; url?: string }[];
  testimonials?: { quote: string; author: string; role?: string }[];
  faq?: { q: string; a: string }[];
  staff?: { name: string; role?: string; discord?: string }[];
  // Org rules, markdown-lite. Shown on the members-only org info page.
  rules?: string;
  featuredCompId?: string;
  seasons?: OrgSeason[];
}

export interface OrgPublicPageResponse {
  // logoUrl is the org's own uploaded branding logo (OrgBranding.tsx's
  // "Organisation Logo" AssetUploader) — distinct from page.hero.logoAssetUrl
  // (an explicit page-editor pick). null when the org has never uploaded one.
  org: { name: string; slug: string; accentColor?: string | null; createdAt?: number | null; logoUrl?: string | null };
  // Rides through OrgPublicPageContent — the public read exposes the same
  // `seasons` field here at `page.seasons` (OrgPublicPageView reads `data.page`).
  page: OrgPublicPageContent;
  comps: { id: string; name: string; status: string; format?: string | null; signUpOpensAt?: number; signUpClosesAt?: number }[];
  stats: { totalComps: number; totalTeams: number; totalPlayers: number; yearsActive: number };
  history: { compId: string; name: string; teamCount: number; playerCount: number; podium: { place: number; clanName: string }[] }[];
  hallOfChampions: { clanName: string; wins: number; podiums: number }[];
  memberClans?: Array<{ clanId: string; name: string }>;
  featuredComp?: { id: string; name: string; status: string; format?: string | null; signUpOpensAt?: number; signUpClosesAt?: number };
}

// ── Clan public page ──────────────────────────────────────────────────────────
// GET /public/clan/:name — org-scoped (NOT the /public/org live-Discord-guild
// gate; see index.ts's isOrgVisible). Public-safe projection: names/gamertags/
// roles/teams only — NEVER a discordId, seat, ban, or delegation internal.

export interface ClanPublicPageMember {
  displayName: string;
  gamertag?: string;
  role: string;
  teamNo: number | null;
}

export interface ClanPublicPageLeader {
  displayName: string;
  gamertag?: string;
  /** 'Owner' | 'Leader' | 'Captain' */
  role: string;
}

export interface ClanPublicPageTeam {
  teamNo: number;
  name: string;
  members: { displayName: string; gamertag?: string }[];
}

export interface ClanPublicPageComp {
  compId: string;
  name: string;
  status: string;
}

export interface ClanPublicPageHistoryEntry {
  compId: string;
  name: string;
  placement: number | null;
  result?: string;
}

export interface ClanPublicPageResponse {
  clan: {
    name: string;
    description?: string | null;
    logoUrl?: string | null;
    bannerUrl?: string | null;
    applicationsOpen: boolean;
  };
  leadership: ClanPublicPageLeader[];
  members: ClanPublicPageMember[];
  teams: ClanPublicPageTeam[];
  stats: { memberCount: number; teamCount: number; compsEntered: number };
  comps: ClanPublicPageComp[];
  history: ClanPublicPageHistoryEntry[];
}

// ── Comp public page ─────────────────────────────────────────────────────────

export interface CompPublicPageHero {
  logoAssetUrl?: string;
  logoObjectPosition?: string;
  bannerAssetUrl?: string;
  bannerObjectPosition?: string;
  seasonTag?: string;
  title?: string;
  subtitle?: string;
}

export interface CompPublicPageScheduleRow {
  label: string;
  date: string;
}

export interface CompPublicPagePrize {
  place: string;
  prize: string;
}

export interface CompPublicPageSponsor {
  logoAssetUrl?: string;
  logoObjectPosition?: string;
  name: string;
  url?: string;
}

export interface CompPublicPageContact {
  email?: string;
  discordInviteUrl?: string;
  note?: string;
}

export interface CompPublicPageBlob {
  published: boolean;
  hero?: CompPublicPageHero;
  about?: string;
  schedule?: CompPublicPageScheduleRow[];
  prizes?: CompPublicPagePrize[];
  sponsors?: CompPublicPageSponsor[];
  modes?: string[];
  rules?: string;
  contact?: CompPublicPageContact;
  registerCtaText?: string;
  keyDates?: Record<string, string | number | undefined>;
}

export interface CompPublicPageComp {
  id: string;
  name: string;
  status: string | null;
  format: CompetitionFormat | null;
  game?: unknown;
  prizePool?: unknown;
  signUpOpensAt?: number;
  signUpClosesAt?: number;
}

export interface CompPublicPageResponse {
  comp: CompPublicPageComp;
  page: CompPublicPageBlob;
}

// Draft shape returned by GET /comps/:id/public-page — blob fields plus
// top-level game/prizePool and metadata written by the backend.
export type CompPublicPageDraft = CompPublicPageBlob & {
  game?: string | null;
  prizePool?: string | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
};

// ── API ───────────────────────────────────────────────────────────────────────

// W8: the one message shown for every way a handshake redeem can fail.
// Handshake links are single-use and short-lived by design — from the
// user's side "expired" and "already used" and "the CDN mangled the
// response" all resolve to the same action: go get a fresh link.
export const HANDSHAKE_EXPIRED_MESSAGE =
  'This portal link has expired or was already used — handshake links are single-use.';

export const api = {
  me: () => apiFetch<Me>('/me'),

  /** Is a submission window open for the caller RIGHT NOW? Drives the GATE's
   *  match-night mode (see LiveMatchWindow). Deliberately a separate call from
   *  me() rather than a field on it — /me is the bootstrap every page load
   *  makes, and this answer is only ever needed by the GATE. */
  myLiveMatch: () => apiFetch<{ liveMatch: LiveMatchWindow | null }>('/me/live-match'),

  competitions: () => apiFetch<Competition[]>('/competitions'),

  standings: (compId: string) =>
    apiFetch<StandingsResponse>(`/standings?compId=${encodeURIComponent(compId)}`),

  compProgression: (compId: string) =>
    apiFetch<CompProgressionResponse>(`/comps/${encodeURIComponent(compId)}/progression`),

  // Raw CSV text (template-shaped end-of-season stats). Admin/founder only.
  exportCompStatsCsv: (compId: string) =>
    apiFetch<string>(`/comps/${encodeURIComponent(compId)}/export`),

  // Elimination bracket
  bracket: (compId: string) =>
    apiFetch<BracketResponse>(`/comps/${encodeURIComponent(compId)}/bracket`),

  generateBracket: (compId: string, data?: { entrants?: string[]; format?: CompetitionFormat; force?: boolean }) =>
    apiFetch<BracketResponse>(`/comps/${encodeURIComponent(compId)}/bracket/generate`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),

  submitBracketResult: (compId: string, slot: number, scoreA: number, scoreB: number) =>
    apiFetch<{ compId: string; changed: BracketSlot[]; bracketComplete: boolean; champion?: string }>(
      `/comps/${encodeURIComponent(compId)}/bracket/${slot}/result`,
      { method: 'POST', body: JSON.stringify({ scoreA, scoreB }) }
    ),

  bracketShotUploadUrl: (compId: string, slot: number, contentType: string) =>
    apiFetch<{ url: string; s3Key: string }>(
      `/comps/${encodeURIComponent(compId)}/bracket/${slot}/shot/upload-url`,
      { method: 'POST', body: JSON.stringify({ contentType }) }
    ),

  bracketShotConfirm: (compId: string, slot: number, s3Key: string) =>
    apiFetch<{ ok: boolean; shotSk: string; status: BracketShotStatus; ocrKicked: boolean }>(
      `/comps/${encodeURIComponent(compId)}/bracket/${slot}/shot/confirm`,
      { method: 'POST', body: JSON.stringify({ s3Key }) }
    ),

  setCompPublicPage: (compId: string, enabled: boolean) =>
    apiFetch<{ ok: boolean; compId: string; publicPageEnabled: boolean }>(
      `/comps/${encodeURIComponent(compId)}/public-page`,
      { method: 'PATCH', body: JSON.stringify({ enabled }) }
    ),

  // Public spectator fetch — plain fetch, NO auth header: this must work for
  // someone who has never logged in.
  publicComp: (compId: string) =>
    fetch(`/portal/public/comps/${encodeURIComponent(compId)}`).then(async r => {
      if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : `Failed (${r.status})`);
      return (await r.json()) as PublicCompResponse;
    }),

  // Public rules/CoC fetch — plain fetch, NO auth: a share link must open for
  // anyone, logged in or not. The backend is opt-in + fail-closed, so a
  // disabled / no-rules / missing comp all come back as 'not_found' (rendered
  // as a neutral empty state). Defensive content-type check
  // handles CloudFront's SPA 404→200+HTML rewrite the same as not_found.
  publicCompRules: (compId: string) =>
    fetch(`/portal/public/comp/${encodeURIComponent(compId)}/rules`).then(async r => {
      const ct = r.headers.get('content-type') ?? '';
      if (!r.ok || !ct.includes('application/json')) {
        if (r.status === 404 || ct.includes('text/html')) throw new Error('not_found');
        throw new Error(`Failed (${r.status})`);
      }
      return (await r.json()) as CompPublicRulesResponse;
    }),

  // Org public profile page — sends session cookie + Bearer token so the backend
  // can gate on Discord-server membership. Status map: 401 = not logged in,
  // 403 = logged in but not a member, 404 / HTML-body = not published / missing.
  publicOrgPage: (slug: string) => {
    const token = getStoredToken();
    return fetch(`/portal/public/org/${encodeURIComponent(slug)}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async r => {
      // Status-first: auth errors take priority before content-type inspection.
      if (r.status === 401) throw new Error('unauthorized');
      if (r.status === 403) throw new Error('forbidden');
      // CloudFront rewrites API 404 to 200 + index.html (SPA fallback), so an
      // unpublished/missing page comes back as HTML — treat any non-JSON body as
      // not-found rather than letting r.json() throw a confusing parse error.
      const ct = r.headers.get('content-type') ?? '';
      if (!r.ok || !ct.includes('application/json')) {
        if (r.status === 404 || ct.includes('text/html')) throw new Error('not_found');
        throw new Error(`Failed (${r.status})`);
      }
      return (await r.json()) as OrgPublicPageResponse;
    });
  },

  // Comp public page — sends session cookie + Bearer token (same membership gate
  // as publicOrgPage). Status map: 401 = not logged in, 403 = not a member,
  // 404 / HTML-body = unpublished / missing comp (falls back to the neutral competition page).
  publicCompPage: (compId: string) => {
    const token = getStoredToken();
    return fetch(`/portal/public/comp/${encodeURIComponent(compId)}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async r => {
      if (r.status === 401) throw new Error('unauthorized');
      if (r.status === 403) throw new Error('forbidden');
      const ct = r.headers.get('content-type') ?? '';
      if (!r.ok || !ct.includes('application/json')) {
        if (r.status === 404 || ct.includes('text/html')) throw new Error('not_found');
        throw new Error(`Failed (${r.status})`);
      }
      return (await r.json()) as CompPublicPageResponse;
    });
  },

  // Clan public profile page — sends session cookie + Bearer token; gated on
  // the ordinary org-scoped visibility boundary (myVisibleOrgIds), NOT a live
  // Discord-guild-membership check. Status map: 401 = not logged in,
  // 403 = clan's org not in the viewer's visible set, 404 = missing clan.
  publicClanPage: (name: string) => {
    const token = getStoredToken();
    return fetch(`/portal/public/clan/${encodeURIComponent(name)}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async r => {
      // Status-first: auth errors take priority before content-type inspection.
      if (r.status === 401) throw new Error('unauthorized');
      if (r.status === 403) throw new Error('forbidden');
      // CloudFront rewrites API 404 to 200 + index.html (SPA fallback), so a
      // missing clan comes back as HTML — treat any non-JSON body as
      // not-found rather than letting r.json() throw a confusing parse error.
      const ct = r.headers.get('content-type') ?? '';
      if (!r.ok || !ct.includes('application/json')) {
        if (r.status === 404 || ct.includes('text/html')) throw new Error('not_found');
        throw new Error(`Failed (${r.status})`);
      }
      return (await r.json()) as ClanPublicPageResponse;
    });
  },

  // Draft CRUD — authed (org owner / admin only). ─────────────────────────────
  getOrgPublicPage: (orgId: string) =>
    apiFetch<OrgPublicPageContent>(`/orgs/${encodeURIComponent(orgId)}/public-page`),

  putOrgPublicPage: (orgId: string, blob: OrgPublicPageContent) =>
    apiFetch<{ ok: boolean; orgId: string }>(`/orgs/${encodeURIComponent(orgId)}/public-page`, {
      method: 'PUT',
      body: JSON.stringify(blob),
    }),

  // Returns { uploadUrl, s3Key, assetId, publicUrl } — caller PUTs the file to
  // uploadUrl then stores publicUrl into the relevant blob image field.
  orgPublicPageUploadUrl: (orgId: string, contentType: string, fileSize: number) =>
    apiFetch<{ uploadUrl: string; s3Key: string; assetId: string; publicUrl: string }>(
      `/orgs/${encodeURIComponent(orgId)}/public-page/upload-url`,
      { method: 'POST', body: JSON.stringify({ contentType, fileSize }) },
    ),

  // Comp public page CRUD — authed (comp.manage_structure capability). ──────────
  getCompPublicPage: (compId: string) =>
    apiFetch<CompPublicPageDraft>(`/comps/${encodeURIComponent(compId)}/public-page`),

  putCompPublicPage: (compId: string, body: CompPublicPageBlob & { game?: string; prizePool?: string }) =>
    apiFetch<{ ok: boolean; compId: string }>(`/comps/${encodeURIComponent(compId)}/public-page`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // Returns { uploadUrl, s3Key, assetId, publicUrl } — same flow as org upload.
  compPublicPageUploadUrl: (compId: string, contentType: string, fileSize: number) =>
    apiFetch<{ uploadUrl: string; s3Key: string; assetId: string; publicUrl: string }>(
      `/comps/${encodeURIComponent(compId)}/public-page/upload-url`,
      { method: 'POST', body: JSON.stringify({ contentType, fileSize }) },
    ),

  // T14 — org-level scoreboard
  orgStats: (orgId: string) =>
    apiFetch<OrgStatsResponse>(`/orgs/${encodeURIComponent(orgId)}/stats`),

  // T10 — notification delegation routing
  getOrgNotifyConfig: (orgId: string) =>
    apiFetch<{ orgId: string; ownerCopyAll: boolean; delegates: Partial<Record<'onboarding' | 'competitions', string[]>> }>(
      `/orgs/${encodeURIComponent(orgId)}/notify-config`),

  setOrgNotifyConfig: (orgId: string, config: { ownerCopyAll: boolean; delegates: Partial<Record<'onboarding' | 'competitions', string[]>> }) =>
    apiFetch<{ ok: boolean; orgId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/notify-config`,
      { method: 'PATCH', body: JSON.stringify(config) }),

  orgGuild: (orgId: string) =>
    apiFetch<{ orgId: string; primaryGuildId: string | null }>(`/orgs/${encodeURIComponent(orgId)}/guild`),

  // memberImport is display-only (state: 'QUEUED' at bind time) — never gate
  // logic on its presence, the backend may not send it on every deploy yet.
  bindOrgGuild: (orgId: string, primaryGuildId: string | null) =>
    apiFetch<{
      ok: boolean;
      orgId: string;
      primaryGuildId: string | null;
      memberImport?: { state: 'QUEUED' };
    }>(`/orgs/${encodeURIComponent(orgId)}/guild`, {
      method: 'PATCH',
      body: JSON.stringify({ primaryGuildId }),
    }),

  // Seeding
  seeding: (compId: string) =>
    apiFetch<SeedingResponse>(`/seeding?compId=${encodeURIComponent(compId)}`),

  submitSeedingRound: (data: SubmitSeedingInput) =>
    apiFetch<{ ok: boolean; pointsEarned: number; clanName: string }>('/seeding/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Divisions
  divisions: (compId: string) =>
    apiFetch<Division[]>(`/competitions/${encodeURIComponent(compId)}/divisions`),

  createDivisions: (data: CreateDivisionsInput) =>
    apiFetch<{ ok: boolean; divisions: Record<string, string[]> }>('/competitions/create-divisions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Clan
  clan: (name: string) =>
    apiFetch<ClanDetail>(`/clan/${encodeURIComponent(name)}`),

  // M2 of the multi-team epic (Lisa, 2026-07-10; M1 flag #3 follow-up) — the
  // caller's OWN active captain grant in a clan, teamNo:null when they have
  // none (not an error — see the backend route's doc comment). Used by
  // JoinCompScreen to highlight "your team" in the team-pick step.
  myClanGrant: (clanName: string) =>
    apiFetch<{ teamNo: number | null }>(`/clans/${encodeURIComponent(clanName)}/my-grant`),

  // Match
  match: (matchId: string, compId: string) =>
    apiFetch<MatchDetail>(
      `/match/${encodeURIComponent(matchId)}?compId=${encodeURIComponent(compId)}`
    ),

  matches: (compId: string) =>
    apiFetch<MatchSummary[]>(`/matches?compId=${encodeURIComponent(compId)}`),

  // ── Match Card (Phase 3) ─────────────────────────────────────────────────
  matchAvailability: (matchId: string, compId: string) =>
    apiFetch<MatchAvailabilityRow[]>(
      `/match/${encodeURIComponent(matchId)}/availability?compId=${encodeURIComponent(compId)}`
    ),

  respondAvailability: (matchId: string, compId: string, response: 'YES' | 'NO') =>
    apiFetch<{ ok: boolean }>(
      `/match/${encodeURIComponent(matchId)}/availability`,
      { method: 'POST', body: JSON.stringify({ compId, response }) }
    ),

  matchTimeline: (matchId: string, compId: string) =>
    apiFetch<MatchTimelineRow[]>(
      `/match/${encodeURIComponent(matchId)}/timeline?compId=${encodeURIComponent(compId)}`
    ),

  openDispute: (matchId: string, compId: string, reason: string) =>
    apiFetch<{ ok: boolean }>(
      `/match/${encodeURIComponent(matchId)}/dispute`,
      { method: 'POST', body: JSON.stringify({ compId, reason }) }
    ),

  // Pending submissions check
  pendingSubmissions: () =>
    apiFetch<PendingSubmissionsResponse>('/me/pending-submissions'),

  // Per-player screenshot upload
  matchPlayerUploadUrl: (matchId: string, compId: string, gameMode: GameMode, contentType: string, opts?: { uploadType?: 'player' | 'scoreboard'; targetDiscordId?: string }) =>
    apiFetch<{ uploadUrl: string; s3Key: string }>(
      `/matches/${encodeURIComponent(matchId)}/upload-url?compId=${encodeURIComponent(compId)}&gameMode=${gameMode}&contentType=${encodeURIComponent(contentType)}${opts?.uploadType ? `&uploadType=${opts.uploadType}` : ''}${opts?.targetDiscordId ? `&targetDiscordId=${opts.targetDiscordId}` : ''}`
    ),

  // Scoreboard submission (team-level evidence per mode)
  scoreboardSubmit: (matchId: string, data: ScoreboardSubmitInput) =>
    apiFetch<ScoreboardSubmitResponse>(
      `/matches/${encodeURIComponent(matchId)}/scoreboard-submit`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  // Player detail submission (individual player evidence)
  playerSubmit: (matchId: string, data: PlayerSubmitInput) =>
    apiFetch<PlayerSubmitResponse>(
      `/matches/${encodeURIComponent(matchId)}/player-submit`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  playerFinalSubmit: (matchId: string, compId: string) =>
    apiFetch<{ ok: boolean; lockedModes: string[]; lockedAt: number; clanId?: string }>(
      `/matches/${encodeURIComponent(matchId)}/player-final-submit`,
      { method: 'POST', body: JSON.stringify({ compId }) }
    ),

  // Full match card state
  matchSubmissions: (matchId: string, compId: string) =>
    apiFetch<MatchSubmissions>(
      `/matches/${encodeURIComponent(matchId)}/submissions?compId=${encodeURIComponent(compId)}`
    ),

  playerClans: (discordId: string) =>
    apiFetch<{
      discordId: string;
      clans: Array<{
        clanName: string;
        role: string;
        joinedAt: number;
        gamertag?: string;
        orgId?: string;
        compId?: string;
      }>;
    }>(`/players/${encodeURIComponent(discordId)}/clans`),

  playerStats: (discordId: string, compId?: string) =>
    apiFetch<PlayerStats>(
      `/players/${encodeURIComponent(discordId)}/stats${compId ? `?compId=${encodeURIComponent(compId)}` : ''}`
    ),

  // Public Discord-derived identity for a player's card (name, avatar, accent).
  playerProfile: (discordId: string) =>
    apiFetch<{
      discordId: string;
      displayName: string | null;
      globalName: string | null;
      discordUsername: string | null;
      gamertag: string | null;
      avatarUrl: string | null;
      bannerUrl: string | null;
      accentColor: number | null;
      accentHex: string | null;
      bannerColor: string | null;
      cardColor: string | null;
      accentOverride: string | null;
      discordClanTag: string | null;
    }>(`/players/${encodeURIComponent(discordId)}/profile`),

  playerMatchHistory: (discordId: string, compId?: string) =>
    apiFetch<Array<{
      compId: string;
      matchId: string;
      clanName: string;
      gamertag?: string;
      roundNumber: number;
      modeStats: Record<string, ModeMatchStats>;
      totals: PlayerMatchTotals;
      submittedAt: number;
    }>>(
      `/players/${encodeURIComponent(discordId)}/match-history${compId ? `?compId=${encodeURIComponent(compId)}` : ''}`
    ),

  // Leader/admin match upload (existing flow)
  matchUploadUrl: (matchId: string, compId: string, contentType: string) =>
    apiFetch<{ uploadUrl: string; s3Key: string }>(
      `/matches/${encodeURIComponent(matchId)}/upload-url?compId=${encodeURIComponent(compId)}&contentType=${encodeURIComponent(contentType)}`
    ),

  submitScores: (matchId: string, data: SubmitScoresInput) =>
    apiFetch<{ ok: boolean; games: unknown }>(
      `/matches/${encodeURIComponent(matchId)}/scores`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  setRoster: (matchId: string, compId: string, data: { roster: Array<{ mode: string; playerIds: string[] }> }) =>
    apiFetch<{ ok: boolean; roster: unknown }>(
      `/matches/${encodeURIComponent(matchId)}/set-roster`,
      { method: 'POST', body: JSON.stringify({ compId, ...data }) }
    ),

  submitMatchStats: (
    matchId: string,
    data: {
      compId: string;
      games: Array<{ mode: string; clan1Score: number; clan2Score: number }>;
      players: Array<{
        playerId: string;
        clanName: string;
        gamertag?: string;
        modeStats: Record<string, { score: number; objScore: number; kills: number; deaths: number; time?: number; plants?: number; defuses?: number; overloads?: number }>;
      }>;
    },
  ) =>
    apiFetch<{ matchId: string; status: string; nightWinner: string; playerStatsWritten: number; message: string }>(
      `/match/${encodeURIComponent(matchId)}/stats`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  competitionClans: (compId: string) =>
    apiFetch<CompClan[]>(`/competitions/clans?compId=${encodeURIComponent(compId)}`),

  rosterOverview: (compId: string) =>
    apiFetch<RosterOverview>(`/competitions/${encodeURIComponent(compId)}/roster-overview`),

  clanRollup: (compId: string) =>
    apiFetch<ClanRollupResponse>(`/competitions/${encodeURIComponent(compId)}/clan-rollup`),

  groupStage: (compId: string) =>
    apiFetch<GroupStageResponse>(`/comps/${encodeURIComponent(compId.replace(/^COMP#/, ''))}/groupstage`),

  groupStageInit: (compId: string) =>
    apiFetch<{ compId: string; teams: number; numGroups: number; advancePerGroup: number; matches: number; notified: number }>(
      `/comps/${encodeURIComponent(compId.replace(/^COMP#/, ''))}/groupstage/init`,
      { method: 'POST' },
    ),

  groupStageFinalize: (compId: string, force?: boolean) =>
    apiFetch<{ compId: string; phase: string; advancers: string[]; bracketSlots: number; notified: number }>(
      `/comps/${encodeURIComponent(compId.replace(/^COMP#/, ''))}/groupstage/finalize`,
      { method: 'POST', body: JSON.stringify({ force: force ?? false }) },
    ),

  createCompetition: (data: CreateCompetitionInput) =>
    apiFetch<{ id: string; name: string }>('/competitions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Edit-while-PENDING: 409 comp_locked once the comp is LIVE/CLOSED (hard
  // gate, no exceptions — see PATCH /competitions/:compId in portalApi).
  updateCompetition: (compId: string, patch: UpdateCompetitionInput) =>
    apiFetch<{ id: string; updated: string[] }>(`/competitions/${encodeURIComponent(compId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // SOFT delete — mirrors deleteClan below. LIVE comps 409 with
  // { error: 'comp_live_confirm_required' } unless opts.confirm is true —
  // parseApiError surfaces that code so the caller can re-prompt.
  deleteCompetition: (compId: string, opts?: { confirm?: boolean }) =>
    apiFetch<{ ok: boolean; compId: string; softDelete: boolean; recoverableUntil: number }>(
      `/competitions/${encodeURIComponent(compId)}`,
      { method: 'DELETE', body: JSON.stringify(opts ?? {}) },
    ),

  endCompetition: (compId: string) =>
    apiFetch<{ ok: boolean }>('/competitions/end', {
      method: 'POST',
      body: JSON.stringify({ compId }),
    }),

  activateCompetition: (compId: string) =>
    apiFetch<{ ok: boolean }>('/competitions/activate', {
      method: 'POST',
      body: JSON.stringify({ compId }),
    }),

  advanceStage: (compId: string, seedingDeadline?: number) =>
    apiFetch<{ ok: boolean; stage: string }>('/competitions/advance-stage', {
      method: 'POST',
      body: JSON.stringify({ compId, seedingDeadline }),
    }),

  // ── Composable stage pipeline (T0) — see CompStage above. Structured
  // errors (stage_locked/stage_order/stage_live/stage_not_live/stage_not_found)
  // come back as `{ error, message }` — use parseApiError to surface them.
  listCompStages: (compId: string) =>
    apiFetch<CompStage[]>(`/competitions/${encodeURIComponent(compId)}/stages`),

  addCompStage: (compId: string, type: CompStageType, config?: Record<string, unknown>) =>
    apiFetch<CompStage>(`/competitions/${encodeURIComponent(compId)}/stages`, {
      method: 'POST',
      body: JSON.stringify({ type, config }),
    }),

  updateCompStage: (compId: string, stageId: string, patch: {
    config?: Record<string, unknown>;
    ordinal?: number;
    // P2-c — see CompStage's doc comment above. Independent of `config`;
    // either may be sent alone or alongside the other.
    plannedStartAt?: number | null;
    plannedEndAt?: number | null;
  }) =>
    apiFetch<{ stageId: string; updated: string[] }>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  removeCompStage: (compId: string, stageId: string) =>
    apiFetch<{ removed: string }>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}`,
      { method: 'DELETE' },
    ),

  startCompStage: (compId: string, stageId: string) =>
    apiFetch<{ stageId: string; status: CompStageStatus }>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/start`,
      { method: 'POST' },
    ),

  endCompStage: (compId: string, stageId: string) =>
    apiFetch<{ stageId: string; status: CompStageStatus }>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/end`,
      { method: 'POST' },
    ),

  // R3 (comp-lifecycle-pipeline, divisions gate): the DIVISIONS confirm step.
  // `groups` is the (possibly admin-edited) division list — [[clanId,...],...]
  // — off the stage's own `proposedGroups`. Structured 400s: bad_groups,
  // missing_team, unknown_team, duplicate_team, group_too_small; structured
  // 409s: no_proposal (call start first), plus canStart's own codes
  // (stage_locked/stage_order/stage_live) if the stage isn't eligible to go
  // LIVE right now. `moved` is how many teams differ from the proposal (each
  // wrote a DIVLOG audit row server-side).
  confirmDivisions: (compId: string, stageId: string, groups: string[][]) =>
    apiFetch<{ stageId: string; status: CompStageStatus; matchesGenerated: number; moved: number }>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/confirm-divisions`,
      { method: 'POST', body: JSON.stringify({ groups }) },
    ),

  // T5 (comp-lifecycle-pipeline, elimination gate): the ELIMINATION_SINGLE/
  // ELIMINATION_DOUBLE confirm step — same shape as confirmDivisions above.
  // `entrants` is the (possibly admin-reordered) proposed entrant list — off
  // the stage's own `proposedEntrants`. Structured 400s: bad_entrants,
  // missing_entrant, unknown_entrant, duplicate_entrant; structured 409s:
  // no_proposal (call start first), plus canStart's own codes if the stage
  // isn't eligible to go LIVE right now. `moved` is how many entrants differ
  // from the proposal (each wrote a BRACKETLOG audit row server-side).
  confirmBracket: (compId: string, stageId: string, entrants: string[]) =>
    apiFetch<{ stageId: string; status: CompStageStatus; matchesGenerated: number; moved: number }>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/confirm-bracket`,
      { method: 'POST', body: JSON.stringify({ entrants }) },
    ),

  // ── SEEDING-stage runtime (src/shared/seedingTable.ts) — see
  // SeedingTableResponse above. Mutations 409 with { error: 'seeding_locked' }
  // once the SEEDING stage has ENDED (or if the comp never had one) —
  // parseApiError surfaces that code so the caller can show the right copy.
  getSeedingTable: (compId: string, opts?: { log?: boolean }) =>
    apiFetch<SeedingTableResponse>(
      `/competitions/${encodeURIComponent(compId)}/seeding-table${opts?.log ? '?log=1' : ''}`,
    ),

  // M2 of the multi-team epic (Lisa, 2026-07-10; M1 flag #1 fix): `teamNo`
  // disambiguates WHICH of a multi-team clan's rows to move — omit it for a
  // legacy/unambiguous row (every pre-M2 call site keeps working
  // unmodified); a multi-team clan's row REQUIRES it or the server 400s
  // ambiguous_entry.
  moveSeedingClan: (compId: string, clanId: string, toSeed: number, reason?: string, teamNo?: number) =>
    apiFetch<{ ok: boolean; clanId: string; teamNo?: number; fromSeed: number; toSeed: number; shifted: number }>(
      `/competitions/${encodeURIComponent(compId)}/seeding-table/move`,
      { method: 'POST', body: JSON.stringify({ clanId, toSeed, ...(teamNo != null ? { teamNo } : {}), ...(reason ? { reason } : {}) }) },
    ),

  // `clanIds` entries may be a bare clanId string (legacy/unambiguous row) or
  // { clanId, teamNo } to name a specific team-entry row — see
  // moveSeedingClan's doc comment.
  orderSeedingTable: (compId: string, clanIds: Array<string | { clanId: string; teamNo?: number }>, reason?: string) =>
    apiFetch<{ ok: boolean; moved: number }>(
      `/competitions/${encodeURIComponent(compId)}/seeding-table/order`,
      { method: 'POST', body: JSON.stringify({ clanIds, ...(reason ? { reason } : {}) }) },
    ),

  // ── SEEDING rounds (R2) — see StageScoreboardResponse above. next-round
  // structured errors: round_incomplete (names how many unplayed), rounds_exhausted,
  // stage_not_live, not_seeding_stage — parseApiError surfaces the code+message.
  nextStageRound: (compId: string, stageId: string) =>
    apiFetch<{ stageId: string; round: number; matchesGenerated: number }>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/next-round`,
      { method: 'POST' },
    ),

  getStageScoreboard: (compId: string, stageId: string) =>
    apiFetch<StageScoreboardResponse>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/scoreboard`,
    ),

  // R4 (comp-lifecycle-pipeline, division scoreboards): SAME route as
  // getStageScoreboard above — the server branches on stage.type internally
  // (see index.ts's GET .../scoreboard handler) — but only ever called for a
  // DIVISIONS stage (DivisionsScoreboardPanel.tsx), so it's typed against
  // DivisionsScoreboardResponse rather than the flat R2 shape.
  getDivisionsScoreboard: (compId: string, stageId: string) =>
    apiFetch<DivisionsScoreboardResponse>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/scoreboard`,
    ),

  // E1 (comp-lifecycle-pipeline, Swiss rounds panel): SAME route as
  // getStageScoreboard/getDivisionsScoreboard above — the server branches on
  // stage.type internally (see index.ts's GET .../scoreboard handler) — but
  // only ever called for a SWISS stage (SwissRoundsPanel.tsx), typed against
  // SwissScoreboardResponse.
  getSwissScoreboard: (compId: string, stageId: string) =>
    apiFetch<SwissScoreboardResponse>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/scoreboard`,
    ),

  // E1 — the two LEGACY (non-stage-scoped, `/comps/` prefix — see
  // shared/swiss.ts's own header comment) round-progression actions
  // SwissRoundsPanel.tsx drives: complete the just-finished round (folds
  // results into standings, flips teams IN/OUT) and pair the next one. The
  // stage pipeline's own /start only ever generates ROUND 1 — every
  // subsequent round runs through these same routes an admin could
  // previously only reach directly, unchanged by this thread. Structured
  // errors (e.g. "Round N is not complete — call /swiss/complete-round
  // first", "Swiss stage is already complete") come back as a plain string
  // message — parseApiError surfaces it verbatim, which is the point: the
  // route's own refusal IS the guard SwissRoundsPanel shows.
  completeSwissRound: (compId: string) =>
    apiFetch<{ round: number; summary: SwissScoreboardSummary; standings: SwissScoreboardStanding[] }>(
      `/comps/${encodeURIComponent(compId.replace(/^COMP#/, ''))}/swiss/complete-round`,
      { method: 'POST' },
    ),
  pairSwissRound: (compId: string) =>
    apiFetch<{ round: number; pairings: SwissScoreboardPairing[] }>(
      `/comps/${encodeURIComponent(compId.replace(/^COMP#/, ''))}/swiss/pair`,
      { method: 'POST' },
    ),

  applyStageStandings: (compId: string, stageId: string) =>
    apiFetch<ApplyStandingsResponse>(
      `/competitions/${encodeURIComponent(compId)}/stages/${encodeURIComponent(stageId)}/apply-standings`,
      { method: 'POST' },
    ),

  startMatch: (data: StartMatchInput) =>
    apiFetch<StartMatchResult>('/matches', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Match-agreement gate (2026-07-17): a captain-proposed match lands
  // AWAITING_AGREEMENT instead of live; the opponent captain (match.leader2Id)
  // — or a founder/admin override — must Agree or Decline before it goes
  // PENDING. Single authority: shared/matchAgreement.ts's executeAgree/
  // executeDecline (portalApi's POST /matches/:matchId/agree|decline), the
  // SAME module the Discord bot's match_agree:/match_decline: button
  // handlers call. `match` in the response is the updated raw row — same
  // shape MatchCardV2Response['match'] carries.
  agreeMatch: (matchId: string, compId: string) =>
    apiFetch<{ ok: boolean; match: MatchCardV2Response['match'] }>(
      `/matches/${encodeURIComponent(matchId)}/agree`,
      { method: 'POST', body: JSON.stringify({ compId }) },
    ),
  declineMatch: (matchId: string, compId: string) =>
    apiFetch<{ ok: boolean; match: MatchCardV2Response['match'] }>(
      `/matches/${encodeURIComponent(matchId)}/decline`,
      { method: 'POST', body: JSON.stringify({ compId }) },
    ),

  // Match CARD agreement (Lisa, 2026-07-23) — a DIFFERENT question from
  // agreeMatch/declineMatch above, which is consent to the FIXTURE EXISTING on
  // a captain-proposed match. THIS is the agreement on the card's DATE AND
  // TIME, asked of every match whoever created it, and completing it is what
  // BUILDS the card. Single authority shared/matchCardAgreement.ts — the same
  // module the Discord card_agree:/card_disagree: buttons call.
  //
  // `party` is optional and only honoured for founder/admin, who may sign on
  // an unresponsive side's behalf; everyone else signs the line the server
  // derives for them from the match row. Never trust the client for this.
  //
  // `fieldedSquad` (T1b, 2026-07-24): the TICKED subset of that side's
  // registered players, sent alongside a clan1/clan2 self-signature —
  // CardAgreementPanel is the only caller that ever supplies one (the
  // "agree for X" operator-override path omits it, same as any pre-T1b
  // client). Meaningless on the admin party; validated server-side against
  // that side's registered seats and the comp's teamSize ceiling before the
  // stamp lands — see FIELDED_SQUAD_EMPTY/OVER_CEILING/STRANGER on
  // parseApiError's error surface. See shared/matchCardAgreement.ts.
  cardAgree: (matchId: string, compId: string, party?: 'admin' | 'clan1' | 'clan2', fieldedSquad?: string[]) =>
    apiFetch<CardAgreementResult>(
      `/matches/${encodeURIComponent(matchId)}/card-agree`,
      {
        method: 'POST',
        body: JSON.stringify({
          compId,
          ...(party ? { party } : {}),
          ...(fieldedSquad ? { fieldedSquad } : {}),
        }),
      },
    ),
  cardDisagree: (matchId: string, compId: string, party?: 'admin' | 'clan1' | 'clan2') =>
    apiFetch<CardAgreementResult>(
      `/matches/${encodeURIComponent(matchId)}/card-disagree`,
      { method: 'POST', body: JSON.stringify({ compId, ...(party ? { party } : {}) }) },
    ),

  // FORFEIT thread (division fixtures & weekly schedule, 2026-07-18) —
  // resolves a fixture as a walkover in ONE action: the forfeited clan
  // loses, the opponent wins by forfeit, the match goes CONFIRMED with
  // forfeit/forfeitedClan markers (see MatchSummary.forfeit's doc comment)
  // — AND a discipline flag is logged against the forfeited clan in the
  // SAME request server-side (index.ts's POST /matches/:matchId/forfeit),
  // which is why DisciplinePage owns this control rather than a match-
  // management surface. AUTH is the established canManageMatchResults trio
  // (admin/org-manager/comp-manager) — NOT a captain/leader action,
  // same as POST /match/:matchId/result. `forfeitedClan` is compared
  // VERBATIM against the match's own clan1/clan2 server-side — always pass
  // whichever of those two exact strings identifies the forfeiting side,
  // never a re-derived/normalized clan name.
  forfeitMatch: (matchId: string, compId: string, forfeitedClan: string) =>
    apiFetch<{ matchId: string; compId: string; status: string; nightWinner: string; forfeit: true; forfeitedClan: string }>(
      `/matches/${encodeURIComponent(matchId)}/forfeit`,
      { method: 'POST', body: JSON.stringify({ compId, forfeitedClan }) },
    ),

  // Division-fixture date agreement (T4, division fixtures & weekly
  // schedule, 2026-07-18) — a SEPARATE captain-to-captain gate from
  // agreeMatch/declineMatch above: those settle whether a captain-INITIATED
  // match happens at all (AWAITING_AGREEMENT -> PENDING/CANCELLED); these
  // settle WHEN an already-fixed division pairing plays. Either clan leader
  // may propose a date/time; the OTHER leader Agrees or Declines. Declining
  // does NOT cancel the match — it just clears the proposal so a new one can
  // be made (shared/matchDateAgreement.ts is the backend authority; no
  // founder/admin override exists on these three routes — a date is a
  // captain courtesy, not a privileged action). Single authority: portalApi's
  // POST /matches/:matchId/propose-date|date/agree|date/decline.
  proposeMatchDate: (matchId: string, compId: string, scheduledAt: number) =>
    apiFetch<{ ok: boolean; matchId: string; proposedScheduledAt: number }>(
      `/matches/${encodeURIComponent(matchId)}/propose-date`,
      { method: 'POST', body: JSON.stringify({ compId, scheduledAt }) },
    ),
  agreeMatchDate: (matchId: string, compId: string) =>
    apiFetch<{ ok: boolean; matchId: string; scheduledAt: number }>(
      `/matches/${encodeURIComponent(matchId)}/date/agree`,
      { method: 'POST', body: JSON.stringify({ compId }) },
    ),
  declineMatchDate: (matchId: string, compId: string) =>
    apiFetch<{ ok: boolean; matchId: string }>(
      `/matches/${encodeURIComponent(matchId)}/date/decline`,
      { method: 'POST', body: JSON.stringify({ compId }) },
    ),

  inviteMember: (discordId: string) =>
    apiFetch<{ ok: boolean; dmSent: boolean; joinCommand: string; clanUrl?: string }>('/clans/invite', {
      method: 'POST',
      body: JSON.stringify({ discordId }),
    }),

  createClan: (data: CreateClanInput) =>
    apiFetch<{ id: string; name: string; leaderId: string }>('/clans', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  orgs: () => apiFetch<OrganizationSummary[]>('/orgs'),

  // POST /orgs was deleted in Phase 3.5 (it was ungated). Use createMyOrg
  // (POST /me/orgs/create) instead — that route enforces the founder gate
  // and writes an OrgCreationRequest row for non-founders.

  orgCompetitionRequests: (orgId: string) =>
    apiFetch<CompetitionRequest[]>(`/orgs/${encodeURIComponent(orgId)}/competition-requests`),

  createOrgCompetitionRequest: (orgId: string, data: CreateCompetitionRequestInput) =>
    apiFetch<{ ok: boolean; id: string; quotedAmountUsdCents: number; plan: BillingPlan }>(
      `/orgs/${encodeURIComponent(orgId)}/competition-requests`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  orgCompetitions: (orgId: string) =>
    apiFetch<Competition[]>(`/orgs/${encodeURIComponent(orgId)}/competitions`),

  setOrgEnabledFormats: (orgId: string, enabledFormats: CompetitionFormat[]) =>
    apiFetch<{ ok: boolean; orgId: string; enabledFormats: CompetitionFormat[] }>(
      `/orgs/${encodeURIComponent(orgId)}/enabled-formats`,
      { method: 'PATCH', body: JSON.stringify({ enabledFormats }) },
    ),

  createOrgCompetition: (orgId: string, data: CreateCompetitionInput) =>
    apiFetch<{ id: string; name: string; orgId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/competitions`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  createCheckoutSession: (orgId: string, requestId?: string) =>
    apiFetch<CheckoutSessionResponse>(`/orgs/${encodeURIComponent(orgId)}/billing/checkout-session`, {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    }),

  addOrgMember: (orgId: string, targetDiscordId: string, role: OrganizationRole) =>
    apiFetch<{ ok: boolean; orgId: string; discordId: string; role: OrganizationRole }>(
      `/orgs/${encodeURIComponent(orgId)}/members`,
      {
        method: 'POST',
        body: JSON.stringify({ targetDiscordId, role }),
      }
    ),

  // Bare array = legacy (pre-pagination) shape; envelope = new shape with
  // nextCursor/importStatus. Callers must run this through
  // lib/orgMembersPaging.ts's normalizeOrgMembersResponse — never assume
  // one shape here, the backend half of this contract may not have landed
  // yet on any given deploy.
  orgMembers: (orgId: string, opts?: { cursor?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.cursor) params.set('cursor', opts.cursor);
    if (opts?.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return apiFetch<ScopedOrgMember[] | OrgMembersResponse>(
      `/orgs/${encodeURIComponent(orgId)}/members${qs ? `?${qs}` : ''}`
    );
  },

  updateOrgMemberRole: (orgId: string, targetDiscordId: string, role: OrganizationRole) =>
    apiFetch<{ ok: boolean; orgId: string; discordId: string; role: OrganizationRole }>(
      `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(targetDiscordId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }
    ),

  removeOrgMember: (orgId: string, targetDiscordId: string) =>
    apiFetch<{ ok: boolean; orgId: string; discordId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(targetDiscordId)}`,
      { method: 'DELETE' }
    ),

  transferOrgOwnership: (orgId: string, newOwnerDiscordId: string) =>
    apiFetch<{ ok: boolean; orgId: string; newOwnerId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/transfer-ownership`,
      { method: 'POST', body: JSON.stringify({ newOwnerDiscordId }) }
    ),

  // PR1 multi-owner: moves the add-power (METADATA.ownerDiscordId) to a
  // DIFFERENT existing co-owner without demoting anyone — contrast with
  // transferOrgOwnership above, which demotes the outgoing owner to admin.
  appointFoundingOwner: (orgId: string, newFoundingOwnerDiscordId: string) =>
    apiFetch<{ ok: boolean; orgId: string; newFoundingOwnerDiscordId: string; previousFoundingOwnerDiscordId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/appoint-founding-owner`,
      { method: 'POST', body: JSON.stringify({ newFoundingOwnerDiscordId }) }
    ),

  banOrgMember: (orgId: string, targetDiscordId: string, reason?: string) =>
    apiFetch<{ ok: boolean }>(
      `/orgs/${encodeURIComponent(orgId)}/bans`,
      { method: 'POST', body: JSON.stringify({ targetDiscordId, ...(reason ? { reason } : {}) }) }
    ),

  unbanOrgMember: (orgId: string, discordId: string) =>
    apiFetch<{ ok: boolean }>(
      `/orgs/${encodeURIComponent(orgId)}/bans/${encodeURIComponent(discordId)}`,
      { method: 'DELETE' }
    ),

  orgBans: (orgId: string) =>
    apiFetch<{ bans: OrgBan[] }>(`/orgs/${encodeURIComponent(orgId)}/bans`),

  // ── Org governance — proposals/voting (PR2 backend) ──────────────────────────
  // REMOVE_OWNER is (currently) the only sanctioned way to remove an ORG_OWNER
  // now that PATCH/DELETE members block unilateral demote/remove on that tier.

  createOrgProposal: (orgId: string, type: OrgProposalType, targetDiscordId: string) =>
    apiFetch<{
      proposalId: string; orgId: string; type: OrgProposalType; proposerDiscordId: string;
      targetDiscordId: string; quorumRule: OrgProposalQuorumRule; status: OrgProposalStatus;
      createdAt: number; expiresAt: number;
    }>(
      `/orgs/${encodeURIComponent(orgId)}/proposals`,
      { method: 'POST', body: JSON.stringify({ type, targetDiscordId }) }
    ),

  orgProposals: (orgId: string) =>
    apiFetch<{ proposals: OrgProposal[] }>(`/orgs/${encodeURIComponent(orgId)}/proposals`),

  voteOnOrgProposal: (orgId: string, proposalId: string, vote: OrgVoteChoice) =>
    apiFetch<{ ok: boolean; proposalId: string; status: OrgProposalStatus; executed: boolean; tally: OrgProposalTally }>(
      `/orgs/${encodeURIComponent(orgId)}/proposals/${encodeURIComponent(proposalId)}/vote`,
      { method: 'POST', body: JSON.stringify({ vote }) }
    ),

  withdrawOrgProposal: (orgId: string, proposalId: string) =>
    apiFetch<{ ok: boolean; proposalId: string; status: OrgProposalStatus }>(
      `/orgs/${encodeURIComponent(orgId)}/proposals/${encodeURIComponent(proposalId)}/withdraw`,
      { method: 'POST' }
    ),

  // Founder-only override — never a board-vote bypass.
  resolveOrgProposal: (orgId: string, proposalId: string, outcome: 'execute' | 'cancel') =>
    apiFetch<{ ok: boolean; proposalId: string; status: OrgProposalStatus; outcome: 'execute' | 'cancel' }>(
      `/orgs/${encodeURIComponent(orgId)}/proposals/${encodeURIComponent(proposalId)}/resolve`,
      { method: 'POST', body: JSON.stringify({ outcome }) }
    ),

  addCompetitionMember: (compId: string, targetDiscordId: string, role: CompetitionRole) =>
    apiFetch<{ ok: boolean; compId: string; discordId: string; role: CompetitionRole }>(
      `/competitions/${encodeURIComponent(compId)}/members`,
      {
        method: 'POST',
        body: JSON.stringify({ targetDiscordId, role }),
      }
    ),

  competitionMembers: (compId: string) =>
    apiFetch<ScopedCompetitionMember[]>(`/competitions/${encodeURIComponent(compId)}/members`),

  updateCompetitionMemberRole: (compId: string, targetDiscordId: string, role: CompetitionRole) =>
    apiFetch<{ ok: boolean; compId: string; discordId: string; role: CompetitionRole }>(
      `/competitions/${encodeURIComponent(compId)}/members/${encodeURIComponent(targetDiscordId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }
    ),

  removeCompetitionMember: (compId: string, targetDiscordId: string) =>
    apiFetch<{ ok: boolean; compId: string; discordId: string }>(
      `/competitions/${encodeURIComponent(compId)}/members/${encodeURIComponent(targetDiscordId)}`,
      { method: 'DELETE' }
    ),

  createCompetitionClan: (compId: string, data: { name: string; ownerDiscordId?: string; description?: string }) =>
    apiFetch<{ id: string; name: string; ownerDiscordId: string; compId: string; orgId?: string }>(
      `/competitions/${encodeURIComponent(compId)}/clans`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  competitionClanList: (compId: string) =>
    // name prefers the enrollment row's denormalized display name — see
    // CompClan's doc comment (same backend route, same fix).
    apiFetch<Array<{ id: string; name: string; status?: string; enrolledAt?: number; clanId?: string }>>(
      `/competitions/${encodeURIComponent(compId)}/clans`
    ),

  clanMembers: (clanName: string) =>
    apiFetch<ScopedClanMember[]>(`/clans/${encodeURIComponent(clanName)}/members`),

  updateClanMemberRole: (clanName: string, targetDiscordId: string, scopedRole: ClanScopedRole) =>
    apiFetch<{ ok: boolean; clanId: string; discordId: string; scopedRole: ClanScopedRole }>(
      `/clans/${encodeURIComponent(clanName)}/members/${encodeURIComponent(targetDiscordId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ scopedRole }),
      }
    ),

  setMemberTeam: (clanName: string, discordId: string, teamNo: number | null) =>
    apiFetch<{ ok: boolean; targetDiscordId: string; teamNo: number | null }>(
      `/clans/${encodeURIComponent(clanName)}/members/${encodeURIComponent(discordId)}/team`,
      {
        method: 'PATCH',
        body: JSON.stringify({ teamNo }),
      }
    ),

  createTeam: (clanName: string, body: { name: string; memberIds: string[]; teamNo?: number }) =>
    apiFetch<{ teamNo: number; name: string; memberIds: string[] }>(
      `/clans/${encodeURIComponent(clanName)}/teams`,
      { method: 'POST', body: JSON.stringify(body) }
    ),

  deleteClan: (clanName: string) =>
    apiFetch<{ ok: boolean; clanId: string; rowsFlagged: number; softDelete: boolean; recoverableUntil: number }>(
      `/clans/${encodeURIComponent(clanName)}`,
      { method: 'DELETE' }
    ),

  restoreClan: (clanName: string) =>
    apiFetch<{ ok: boolean; clanId: string; rowsRestored: number }>(
      `/clans/${encodeURIComponent(clanName)}/restore`,
      { method: 'POST' }
    ),

  removeClanMember: (clanName: string, targetDiscordId: string) =>
    apiFetch<{ ok: boolean; clanId: string; discordId: string }>(
      `/clans/${encodeURIComponent(clanName)}/members/${encodeURIComponent(targetDiscordId)}`,
      { method: 'DELETE' }
    ),

  banClanMember: (clanName: string, discordId: string, reason?: string) =>
    apiFetch<{ ok: boolean; clanId: string; discordId: string; banned: boolean }>(
      `/clans/${encodeURIComponent(clanName)}/bans`,
      { method: 'POST', body: JSON.stringify({ discordId, ...(reason ? { reason } : {}) }) }
    ),

  unbanClanMember: (clanName: string, discordId: string) =>
    apiFetch<{ ok: boolean; clanId: string; discordId: string; banned: boolean }>(
      `/clans/${encodeURIComponent(clanName)}/bans/${encodeURIComponent(discordId)}`,
      { method: 'DELETE' }
    ),

  moveClanMember: (sourceClanName: string, targetDiscordId: string, destinationClanId: string) =>
    apiFetch<{ ok: boolean; sourceClanId: string; destinationClanId: string; discordId: string }>(
      `/clans/${encodeURIComponent(sourceClanName)}/move-member`,
      {
        method: 'POST',
        body: JSON.stringify({ targetDiscordId, destinationClanId }),
      }
    ),

  createGhostSeat: (clanName: string, gamertag: string, claimDiscordId?: string, teamNo?: number) =>
    apiFetch<{ ok: boolean; clanId: string; gamertag: string; status: 'GHOST'; claimDispatched?: boolean }>(
      `/clans/${encodeURIComponent(clanName)}/seats`,
      {
        method: 'POST',
        body: JSON.stringify({ gamertag, claimDiscordId, teamNo }),
      }
    ),

  // ── Handshake redemption ─────────────────────────────────────────────────
  // Public — exchange a single-use handshake blob for a fresh JWT + a
  // Set-Cookie session. credentials:'include' is required so the browser
  // stores the cookie. Spec: docs/planning/anvil-core-spec.md §5.
  //
  // W8: redeem failure has three faces that all mean the same thing to the
  // user ("this link is dead, go get a fresh one") but previously surfaced
  // three different, useless messages:
  //   1. CloudFront masks the API's 404 into the SPA's index.html served
  //      with a 200 — content-type comes back text/html, not JSON.
  //   2. A real {error:'not_found'|'expired', message:'...'} body, but
  //      double-JSON-encoded by portalApi's fail() helper (see
  //      parseApiError's comment above for the same quirk elsewhere) — a
  //      naive `JSON.parse(text).message` read is always undefined for it.
  //   3. Any other unexpected non-JSON body used to throw a raw
  //      "Unexpected token '<'..." SyntaxError straight out of JSON.parse.
  // All three collapse to HANDSHAKE_EXPIRED_MESSAGE below. Anything else
  // (a genuine, unstructured 4xx/5xx) still surfaces its own text so a real
  // outage doesn't get misreported as "your link expired".
  redeemHandshake: (blob: string) =>
    fetch('/portal/auth/handshake/redeem', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blob }),
    }).then(async r => {
      const contentType = r.headers.get('content-type') || '';
      const text = await r.text();

      if (!contentType.includes('application/json')) {
        throw new Error(HANDSHAKE_EXPIRED_MESSAGE);
      }

      let parsed: { ok?: true; token?: string; error?: string; message?: string } = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error(HANDSHAKE_EXPIRED_MESSAGE);
      }

      if (!r.ok || !parsed.ok) {
        // fail()'s body is `{error: <string>}`, where <string> may itself be
        // the JSON.stringify'd {error, message} pair the /handshake/redeem
        // handler builds — unwrap one more layer if so.
        let code = parsed.error;
        let message = parsed.message;
        if (typeof parsed.error === 'string' && parsed.error.trim().startsWith('{')) {
          try {
            const inner = JSON.parse(parsed.error) as { error?: string; message?: string };
            code = inner.error ?? code;
            message = inner.message ?? message;
          } catch { /* not double-encoded — use parsed.error/message as-is */ }
        }
        if (code === 'not_found' || code === 'expired') {
          throw new Error(HANDSHAKE_EXPIRED_MESSAGE);
        }
        throw new Error(message ?? `Could not redeem handshake link (HTTP ${r.status}).`);
      }

      return parsed as {
        ok: true; token: string; role: Role; displayName: string;
        entryOrgId?: string; entryOrgName?: string;
      };
    }),

  // ── DM claim-token redemption ────────────────────────────────────────────
  // Public — no Authorization header. Returns a fresh JWT + the action context
  // baked into the token. Spec: docs/planning/anvil-core-spec.md §5.
  redeemClaimToken: (token: string) =>
    fetch(`/portal/auth/claim/${encodeURIComponent(token)}`, { method: 'POST' })
      .then(async r => {
        const text = await r.text();
        if (!r.ok) {
          let parsed: { error?: string; message?: string } = {};
          try { parsed = JSON.parse(text); } catch { /* */ }
          throw new Error(parsed.message ?? text ?? `Claim failed (${r.status})`);
        }
        return JSON.parse(text) as {
          ok: true; token: string; discordId: string;
          type: 'claim-seat' | 'approve-request' | 'submit-screenshot' | 'comment' | 'decision-point' | 'view-notification' | 'invite-clan-leader';
          payload: Record<string, unknown>; remainingUses: number;
          inviteAccepted?: boolean;
          claimCompleted?: boolean;
          claimError?: string;
        };
      }),

  // Self-claim a ghost seat after redeeming a claim-token. Standard authed call.
  claimSeat: (clanId: string, gamertag: string) =>
    apiFetch<{ ok: boolean; clanId: string; gamertag: string; role: 'MEMBER' }>(
      '/me/claim-seat',
      { method: 'POST', body: JSON.stringify({ clanId, gamertag }) },
    ),

  deleteGhostSeat: (clanName: string, gamertag: string) =>
    apiFetch<{ ok: boolean; clanId: string; gamertag: string }>(
      `/clans/${encodeURIComponent(clanName)}/seats/${encodeURIComponent(gamertag)}`,
      { method: 'DELETE' }
    ),

  // One-click claim link for an existing ghost seat (T08) — DMs the target;
  // their single click claims the seat, no further approval step.
  mintSeatClaimLink: (clanName: string, gamertag: string, targetDiscordId: string) =>
    apiFetch<{ ok: boolean; clanId: string; gamertag: string; url: string; dmSent: boolean; expiresIn: string }>(
      `/clans/${encodeURIComponent(clanName)}/seats/${encodeURIComponent(gamertag)}/claim-link`,
      { method: 'POST', body: JSON.stringify({ targetDiscordId }) }
    ),

  // Shareable (24h) claim link for a ghost seat — no Discord id needed. The
  // leader copies + sends it; whoever opens it (signed in) claims the seat.
  mintShareableSeatLink: (clanName: string, gamertag: string) =>
    apiFetch<{ ok: boolean; clanId: string; gamertag: string; url: string; token: string; shareable: boolean; expiresIn: string }>(
      `/clans/${encodeURIComponent(clanName)}/seats/${encodeURIComponent(gamertag)}/claim-link`,
      { method: 'POST' }
    ),

  // Search the clan's Discord server members — powers the @person invite picker.
  searchDiscordMembers: (clanName: string, q: string) =>
    apiFetch<{ members: Array<{ discordId: string; username: string; displayName: string; avatar: string | null }> }>(
      `/clans/${encodeURIComponent(clanName)}/discord-members?q=${encodeURIComponent(q)}`
    ),

  // Search an org's Discord server members — powers the OrgMembersPanel @person picker.
  searchOrgDiscordMembers: (orgId: string, q: string) =>
    apiFetch<{ members: Array<{ discordId: string; username: string; displayName: string; avatar: string | null }> }>(
      `/orgs/${encodeURIComponent(orgId)}/discord-members?q=${encodeURIComponent(q)}`
    ),

  // Invite a picked Discord member — DMs them an Accept button; DMs closed → returns a link.
  inviteDiscordMember: (clanName: string, discordId: string, displayName: string) =>
    apiFetch<{ ok: boolean; dmed: boolean; link?: string; displayName: string }>(
      `/clans/${encodeURIComponent(clanName)}/invite-discord`,
      { method: 'POST', body: JSON.stringify({ discordId, displayName }) }
    ),

  // Redeem a shareable claim link as the signed-in opener.
  claimSeatViaLink: (token: string) =>
    apiFetch<{ ok: boolean; clanId: string; gamertag: string; clanName: string }>(
      `/me/claim-seat-link`,
      { method: 'POST', body: JSON.stringify({ token }) }
    ),

  renameGhostSeat: (clanName: string, oldGamertag: string, newGamertag: string) =>
    apiFetch<{ ok: boolean; clanId: string; gamertag: string }>(
      `/clans/${encodeURIComponent(clanName)}/seats/${encodeURIComponent(oldGamertag)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ gamertag: newGamertag }),
      }
    ),

  updateClanMeta: (clanName: string, updates: { publicApplicationsOpen?: boolean; description?: string }) =>
    apiFetch<{ ok: boolean; clanId: string }>(
      `/clans/${encodeURIComponent(clanName)}`,
      { method: 'PATCH', body: JSON.stringify(updates) }
    ),

  // teamNo is number | undefined ONLY — bcb86d4 made the backend hard-400 on
  // null/absent/out-of-range teamNo ("Captaincy is per-team — pick a team"),
  // retiring the team-less "clan-wide deputy" grant. MakeCaptainModal already
  // normalises its `number | null` local state to `?? undefined` before
  // calling this, and disables submit entirely while teamNo is null, so no
  // caller ever needs `| null` here.
  // compId is required by the backend when kind: 'COMP_SCOPED' (400 otherwise)
  // — MakeCaptainModal only ever sends it alongside that kind.
  mintDelegation: (clanName: string, body: { granteeDiscordId: string; teamNo?: number; teamName?: string; kind?: 'WINDOWED' | 'PERMANENT' | 'COMP_SCOPED'; durationHours?: number; expiresAt?: number; compId?: string; supersede?: boolean }) => {
    const { supersede, ...rest } = body;
    return apiFetch<{ grant: Delegation }>(
      `/clans/${encodeURIComponent(clanName)}/delegation${supersede ? '?supersede=true' : ''}`,
      { method: 'POST', body: JSON.stringify(rest) }
    );
  },

  revokeDelegation: (clanName: string, granteeId: string, grantId: string) =>
    apiFetch<{ ok: boolean }>(
      `/clans/${encodeURIComponent(clanName)}/delegation/${encodeURIComponent(granteeId)}/${encodeURIComponent(grantId)}`,
      { method: 'DELETE' }
    ),

  // Extend a WINDOWED captain's expiry FROM NOW by durationHours (backend
  // PATCH .../delegation/:granteeId/:grantId, portalApi/index.ts ~22517:
  // accepts { expiresAt } OR { durationHours } — durationHours computes
  // now + hours, which is what "extend by 24h/3d/7d" means to a leader
  // clicking a preset; CLAN_OWNER-only server-side, 30-day cap enforced by
  // resolveGrantWindow, PERMANENT grants 400). No caller used the old
  // expiresAt-only shape (dead export until this UI), so narrowing the
  // param to durationHours is a safe, non-breaking signature change.
  extendDelegation: (clanName: string, granteeId: string, grantId: string, durationHours: number) =>
    apiFetch<{ ok: boolean }>(
      `/clans/${encodeURIComponent(clanName)}/delegation/${encodeURIComponent(granteeId)}/${encodeURIComponent(grantId)}`,
      { method: 'PATCH', body: JSON.stringify({ durationHours }) }
    ),

  browseClans: () =>
    apiFetch<{ clans: Array<{ name: string; description?: string; leaderId: string; memberCount: number; applicationsOpen: boolean }> }>('/clans/browse'),

  applyToClan: (clanName: string, data: { gamertag?: string; message?: string }) =>
    apiFetch<{ ok: boolean; clanId: string; status: 'PENDING' }>(
      `/clans/${encodeURIComponent(clanName)}/applications`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  // ── Comp-admin application ───────────────────────────────────────────────
  applyAsCompAdmin: (compId: string, data: { message?: string }) =>
    apiFetch<{ ok: boolean; compId: string; status: 'PENDING' }>(
      `/competitions/${encodeURIComponent(compId)}/admin-applications`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  myCompAdminApplications: () =>
    apiFetch<{ applications: Array<{ compId: string; compName: string; status: 'PENDING' | 'ACCEPTED' | 'REJECTED'; createdAt: number; decidedAt?: number }> }>(
      '/me/comp-admin-applications'
    ),

  listCompAdminApplications: (compId: string) =>
    apiFetch<{ applications: Array<{ applicantDiscordId: string; message?: string; status: string; createdAt: number; decidedAt?: number; decidedBy?: string }> }>(
      `/competitions/${encodeURIComponent(compId)}/admin-applications`
    ),

  decideCompAdminApplication: (compId: string, applicantId: string, decision: 'ACCEPTED' | 'REJECTED') =>
    apiFetch<{ ok: boolean; compId: string; applicantDiscordId: string; status: string }>(
      `/competitions/${encodeURIComponent(compId)}/admin-applications/${encodeURIComponent(applicantId)}`,
      { method: 'POST', body: JSON.stringify({ decision }) }
    ),

  listClanApplications: (clanName: string) =>
    apiFetch<{ applications: Array<{ applicantDiscordId: string; applicantGamertag?: string; message?: string; status: 'PENDING' | 'ACCEPTED' | 'REJECTED'; createdAt: number; decidedAt?: number; decidedBy?: string }> }>(
      `/clans/${encodeURIComponent(clanName)}/applications`
    ),

  decideClanApplication: (clanName: string, applicantDiscordId: string, decision: 'ACCEPTED' | 'REJECTED') =>
    apiFetch<{ ok: boolean; clanId: string; applicantDiscordId: string; status: 'ACCEPTED' | 'REJECTED' }>(
      `/clans/${encodeURIComponent(clanName)}/applications/${encodeURIComponent(applicantDiscordId)}`,
      { method: 'POST', body: JSON.stringify({ decision }) }
    ),

  myApplications: () =>
    apiFetch<{ applications: Array<{ clanId: string; clanName: string; status: 'PENDING' | 'ACCEPTED' | 'REJECTED'; createdAt: number; decidedAt?: number }> }>('/me/applications'),

  // GATE "Joining → Competition as a clan": register the caller's OWN clan
  // (leader-gated server-side) into a competition. Immediate REGISTERED.
  // 403s with { error: 'terms_not_accepted' } if the clan hasn't accepted the
  // comp's CURRENT rules yet — see acceptCompTerms/getCompTermsAcceptance.
  // M1 of the multi-team epic (Lisa, 2026-07-10): optional teamNo enters a
  // SPECIFIC named team (leader: any team; that team's captain: only their
  // own) — omitted/undefined is the legacy whole-clan entry, unchanged.
  registerMyClanInComp: (compId: string, teamNo?: number) =>
    apiFetch<{ ok: boolean; compId: string; clanId: string; clanName: string; teamNo?: number; teamName?: string }>(
      `/competitions/${encodeURIComponent(compId)}/register-my-clan`,
      teamNo != null ? { method: 'POST', body: JSON.stringify({ teamNo }) } : { method: 'POST' }
    ),

  // Clan leader accepts a comp's rules/terms on behalf of their clan — pass
  // clanId only if the caller leads more than one clan. Idempotent re-accept.
  acceptCompTerms: (compId: string, clanId?: string) =>
    apiFetch<{ ok: boolean; compId: string; clanId: string; rulesVersion: number; acceptedAt: number }>(
      `/competitions/${encodeURIComponent(compId)}/terms-acceptance`,
      { method: 'POST', body: JSON.stringify(clanId ? { clanId } : {}) }
    ),

  // Admin/comp-setup trio → full per-clan status list (`clans`). A leader
  // (non-admin) gets back a single-entry `clans` scoped to their own clan.
  // `rules`/`rulesVersion` are always the comp's current values either way.
  getCompTermsAcceptance: (compId: string) =>
    apiFetch<CompTermsAcceptanceResponse>(`/competitions/${encodeURIComponent(compId)}/terms-acceptance`),

  createMyClan: (data: { name: string; description?: string; gamertag?: string; orgId?: string }) =>
    apiFetch<{ ok: boolean; clanName: string; leaderId: string }>('/me/clan', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  listAlerts: (status?: 'all') =>
    apiFetch<{ alerts: Array<{ id: string; severity: 'LOW' | 'MEDIUM' | 'HIGH'; trigger: string; actorId: string; entity: string; entityType: string; description: string; metadata: Record<string, unknown>; status: 'OPEN' | 'ACKNOWLEDGED'; createdAt: number; acknowledgedAt?: number; acknowledgedBy?: string }> }>(
      `/admin/alerts${status ? `?status=${status}` : ''}`
    ),

  ackAlert: (alertId: string) =>
    apiFetch<{ ok: boolean; alertId: string }>(
      `/admin/alerts/${encodeURIComponent(alertId)}/ack`,
      { method: 'POST' }
    ),

  claimClanLeadership: (clanName: string) =>
    apiFetch<{ ok: boolean; clanId: string; discordId: string; status: string }>(
      `/clans/${encodeURIComponent(clanName)}/claim-leadership`,
      { method: 'POST' }
    ),

  // M1 of the multi-team epic (Lisa, 2026-07-10): optional teamNo enrolls the
  // clan's SPECIFIC named team instead of the whole clan (legacy, unchanged
  // when omitted).
  registerClan: (compId: string, clanName: string, teamNo?: number) =>
    apiFetch<{ ok: boolean; teamNo?: number; teamName?: string }>('/competitions/register-clan', {
      method: 'POST',
      body: JSON.stringify(teamNo != null ? { compId, clanName, teamNo } : { compId, clanName }),
    }),

  // ── POPIA / Data Rights ─────────────────────────────────────────────────
  recordConsent: () =>
    apiFetch<{ ok: boolean }>('/me/consent', { method: 'POST' }),

  dataExport: () =>
    apiFetch<Record<string, unknown>>('/me/data-export'),

  requestDeletion: () =>
    apiFetch<{ ok: boolean; requestedAt: number }>('/me/request-deletion', { method: 'POST' }),

  resolveMatch: (matchId: string, compId: string, action: 'clan1' | 'clan2' | 'void') =>
    apiFetch<{ ok: boolean; status: string; nightWinner: string | null }>(
      `/matches/${encodeURIComponent(matchId)}/resolve`,
      { method: 'POST', body: JSON.stringify({ compId, action }) }
    ),

  // R1 (comp-lifecycle-pipeline, manual result entry) — the no-OCR scoring
  // path. Ordered per-game results, up to the match's resolvedSeries.length;
  // modeKey is optional (server defaults it from resolvedSeries.modeOrder,
  // and 400s on mode_mismatch if a caller-supplied one disagrees). Winner is
  // computed server-side via determineNightWinner; a partial/undecided series
  // comes back as a structured 400 (series_undecided) — see parseApiError.
  enterMatchResult: (
    matchId: string,
    compId: string,
    games: Array<{ modeKey?: string; clan1Score: number; clan2Score: number }>,
  ) =>
    apiFetch<{ matchId: string; compId: string; status: string; nightWinner: string; games: GameEntry[] }>(
      `/match/${encodeURIComponent(matchId)}/result`,
      { method: 'POST', body: JSON.stringify({ compId, games }) },
    ),

  admin: {
    disputes: (compId: string) =>
      apiFetch<MatchSummary[]>(`/admin/disputes?compId=${encodeURIComponent(compId)}`),

    clans: () => apiFetch<AdminClan[]>('/admin/clans'),

    flags: (status?: string) =>
      apiFetch<AdminFlag[]>(`/admin/flags${status ? `?status=${status}` : ''}`),

    resolveFlag: (flagId: string, action: 'approve' | 'reject', note?: string) =>
      apiFetch<{ ok: boolean }>(`/admin/flags/${encodeURIComponent(flagId)}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ action, note }),
      }),

    // ── Discipline flags (v1) — admin severity cards, org-scoped ────
    // Deliberately NOT named `flags` — that key above is the unrelated
    // dispute/moderation flag list (AdminFlag/FlagStatus).
    disciplineFlags: {
      issue: (
        orgId: string,
        body: { targetType: 'player' | 'clan'; targetId: string; severity: FlagSeverity; reason: string; evidence?: string },
      ) =>
        apiFetch<DisciplineFlag>(`/admin/orgs/${encodeURIComponent(orgId)}/flags`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),

      list: (orgId: string, opts?: { targetType?: 'player' | 'clan'; targetId?: string }) => {
        const qs = new URLSearchParams();
        if (opts?.targetType) qs.set('targetType', opts.targetType);
        if (opts?.targetId) qs.set('targetId', opts.targetId);
        const query = qs.toString();
        return apiFetch<{ flags: DisciplineFlag[] }>(
          `/admin/orgs/${encodeURIComponent(orgId)}/flags${query ? `?${query}` : ''}`
        );
      },

      cosign: (orgId: string, sk: string) =>
        apiFetch<DisciplineFlag>(`/admin/orgs/${encodeURIComponent(orgId)}/flags/cosign`, {
          method: 'POST',
          body: JSON.stringify({ sk }),
        }),

      overturn: (orgId: string, sk: string, reason: string) =>
        apiFetch<DisciplineFlag>(`/admin/orgs/${encodeURIComponent(orgId)}/flags/overturn`, {
          method: 'POST',
          body: JSON.stringify({ sk, reason }),
        }),
    },

    // ADMIN and HELPER are both retired platform tiers (2026-07-17 and
    // 2026-07-24, role-spine follow-up Directive 1) — the backend 400s any
    // attempt to write either now, so 'FOUNDER' is the only value left in
    // this WRITE-side union (contrast AdminRecord.tier above, which keeps
    // 'HELPER' for READING a legacy row still worth displaying/revoking).
    setTier: (targetDiscordId: string, tier: 'FOUNDER') =>
      apiFetch<{ ok: boolean; discordId: string; tier: string }>('/admin/set-tier', {
        method: 'POST',
        body: JSON.stringify({ targetDiscordId, tier }),
      }),

    admins: () => apiFetch<AdminRecord[]>('/admin/admins'),

    // Founder-only — un-appoints a HELPER (deletes their ADMIN# tier record).
    // Scoped server-side to the HELPER tier only; not a general tier-removal call.
    revokeTier: (targetDiscordId: string) =>
      apiFetch<{ ok: boolean; discordId: string; revoked: string }>('/admin/revoke-tier', {
        method: 'POST',
        body: JSON.stringify({ targetDiscordId }),
      }),

    bulkImport: (clans: Array<{ name: string; players: string[] }>) =>
      apiFetch<{ clansCreated: number; seatsCreated: number; seatsSkipped: number; errors: string[] }>(
        '/admin/bulk-import',
        { method: 'POST', body: JSON.stringify({ clans }) }
      ),

    transferLeadership: (clanName: string, newLeaderDiscordId: string) =>
      apiFetch<{ ok: boolean; clanName: string; newLeaderDiscordId: string }>('/admin/transfer-leadership', {
        method: 'POST',
        body: JSON.stringify({ clanName, newLeaderDiscordId }),
      }),

    setOrgBilling: (
      orgId: string,
      input: {
        entitlementStatus: EntitlementStatus;
        plan?: BillingPlan;
        interval?: BillingInterval;
        stripeCustomerId?: string;
        stripeSubscriptionId?: string;
        currentPeriodStart?: number;
        currentPeriodEnd?: number;
        cancelAtPeriodEnd?: boolean;
      }
    ) =>
      apiFetch<{ ok: boolean; orgId: string; entitlementStatus: EntitlementStatus }>(
        `/admin/orgs/${encodeURIComponent(orgId)}/billing`,
        {
          method: 'POST',
          body: JSON.stringify(input),
        }
      ),

    rolePolicies: () =>
      apiFetch<RolePolicyBundle>('/admin/role-policies'),

    setRolePolicy: (scope: RolePolicyScope, role: string, disabledCapabilities: string[]) =>
      apiFetch<{ ok: boolean; scope: RolePolicyScope; role: string; disabledCapabilities: string[] }>(
        '/admin/role-policies',
        {
          method: 'POST',
          body: JSON.stringify({ scope, role, disabledCapabilities }),
        }
      ),

    ocrTestUploadUrl: (contentType: string) =>
      apiFetch<{ uploadUrl: string; s3Key: string; matchId: string }>(
        `/admin/textract-test/upload-url?contentType=${encodeURIComponent(contentType)}`
      ),

    ocrTestRun: (data: { s3Key: string; matchId: string; provider: string; model: string; tier: string }) =>
      apiFetch<{ executionId: string; matchId: string; submissionSk: string; pipelineStatus: string }>(
        '/admin/textract-test/run',
        { method: 'POST', body: JSON.stringify(data) }
      ),

    ocrTestPoll: (matchId: string, submissionSk: string, s3Key?: string) =>
      apiFetch<PipelineResult & { pipelineStatus?: string }>('/admin/textract-test/poll', {
        method: 'POST',
        body: JSON.stringify({ matchId, submissionSk, s3Key }),
      }),

    grantCredits: (orgId: string, amount: number, reason?: string) =>
      apiFetch<{ ok: boolean; newBalance: number }>('/admin/credits/grant', {
        method: 'POST',
        body: JSON.stringify({ orgId, amount, reason }),
      }),

    // Founder-only generic-scope token grant. Replaces the legacy org-only path
    // for new callers; the same endpoint also accepts the legacy {orgId,amount}
    // shape so existing OrgCredits UI keeps working unchanged.
    grantTokens: (input: {
      scope: 'user' | 'clan' | 'org' | 'platform';
      targetId?: string;
      tokens: number;
      reason: string;
    }) =>
      apiFetch<{
        ok: boolean;
        scope: string;
        targetId: string | null;
        granted: number;
        balanceAfter: number;
        txSk: string;
      }>('/admin/credits/grant', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    // ── Platform Credits (Founder-only) ────────────────────────────────────
    platformCredits: () =>
      apiFetch<PlatformCreditsResponse>('/admin/platform-credits'),

    platformCreditTransactions: () =>
      apiFetch<PlatformCreditTransaction[]>('/admin/platform-credits/transactions'),

    grantPlatformCredits: (amount: number) =>
      apiFetch<{ ok: boolean; granted: number; newBalance: number }>('/admin/platform-credits/grant', {
        method: 'POST',
        body: JSON.stringify({ amount }),
      }),

    updatePlatformSpendCap: (monthlySpendCap: number) =>
      apiFetch<{ ok: boolean; monthlySpendCap: number }>('/admin/platform-credits/spend-cap', {
        method: 'POST',
        body: JSON.stringify({ monthlySpendCap }),
      }),

    allOrgCredits: () =>
      apiFetch<AllOrgCreditsRow[]>('/admin/all-org-credits'),

    // ── User Management ───────────────────────────────────────────────────
    userLookup: (discordId: string) =>
      apiFetch<UserLookupResponse>(`/admin/user-lookup?discordId=${encodeURIComponent(discordId)}`),

    moveOrgMember: (targetDiscordId: string, sourceOrgId: string, destinationOrgId: string, role?: string) =>
      apiFetch<{ ok: boolean }>('/admin/move-org-member', {
        method: 'POST',
        body: JSON.stringify({ targetDiscordId, sourceOrgId, destinationOrgId, role }),
      }),

    forceClaimSeat: (clanName: string, gamertag: string, discordId: string) =>
      apiFetch<{ ok: boolean }>('/admin/force-claim-seat', {
        method: 'POST',
        body: JSON.stringify({ clanName, gamertag, discordId }),
      }),

    // Reset the per-user per-match submission attempt counter so users can
    // retry after hitting the cap. `submissionSk` optional — resets all
    // submissions for the match if omitted.
    resetMatchAttempts: (matchId: string, submissionSk?: string) =>
      apiFetch<{ ok: boolean; resetCount: number; submissionSk?: string; scope?: string }>(
        `/admin/matches/${encodeURIComponent(matchId)}/reset-attempts`,
        {
          method: 'POST',
          body: JSON.stringify(submissionSk ? { submissionSk } : {}),
        }
      ),

    swapSeat: (clanName: string, gamertag: string, newDiscordId: string) =>
      apiFetch<{ ok: boolean }>('/admin/swap-seat', {
        method: 'POST',
        body: JSON.stringify({ clanName, gamertag, newDiscordId }),
      }),

    // Match-team-autofill (Lisa, 2026-07-13): `entryIds` is an ADDITIVE
    // explicit pointer — pass clan{N}ClanId/clan{N}TeamNo (from the SAME
    // CompClan row clan1Name/clan2Name came from) when a side is a specific
    // entered TEAM, so the backend can stamp clan{N}TeamNo without parsing
    // clan1Name/clan2Name's display string apart. Omit for a legacy
    // whole-clan side — byte-identical to pre-feature.
    createMatch: (
      compId: string, clan1Name: string, clan2Name: string, scheduledAt?: number, streamerUrl?: string,
      entryIds?: { clan1ClanId?: string; clan1TeamNo?: number; clan2ClanId?: string; clan2TeamNo?: number },
    ) =>
      apiFetch<{ matchId: string; clan1: string; clan2: string; dmsSent: boolean }>('/admin/matches', {
        method: 'POST',
        body: JSON.stringify({ compId, clan1Name, clan2Name, scheduledAt, streamerUrl, ...entryIds }),
      }),

    // T15 — edit fixture fields after creation (reschedule / streamer link)
    patchMatch: (compId: string, matchId: string, fields: { scheduledAt?: number; streamerUrl?: string | null }) =>
      apiFetch<{ ok: boolean; matchId: string }>(`/admin/matches/${encodeURIComponent(matchId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ compId, ...fields }),
      }),

    enrichUser: (discordId: string) =>
      apiFetch<{ ok: boolean }>('/admin/enrich-user', {
        method: 'POST',
        body: JSON.stringify({ discordId }),
      }),

    purgeUser: (discordId: string) =>
      apiFetch<{ ok: boolean; targetDiscordId: string; itemsProcessed: number }>(
        `/admin/users/${encodeURIComponent(discordId)}/purge`,
        { method: 'POST' },
      ),

    consentedUsers: () =>
      apiFetch<{ users: Array<{
        discordId: string;
        consentedAt: number;
        consentVersion?: string;
        displayName?: string;
        globalName?: string;
        discordUsername?: string;
        gamertag?: string;
        avatar?: string;
        accentColor?: number;
        discordClanTag?: string;
        membershipCount: number;
      }> }>('/admin/consented-users'),

    // ── One-click client onboarding (founder) ───────────────────────────────
    // Creates an ACTIVE org, appoints the client as ORG_OWNER, activates
    // billing, binds their Discord server and grants starter credits — the
    // four manual gates collapsed into one call. DMs the owner a portal button.
    provisionOrg: (data: {
      name: string;
      ownerDiscordId: string;
      primaryGuildId?: string;
      starterCredits?: number;
      billingEmail?: string;
    }) =>
      apiFetch<{
        ok: boolean; orgId: string; name: string; ownerDiscordId: string;
        primaryGuildId: string | null; entitlementStatus: string;
        creditsGranted: number; dmSent: boolean; commandsRegistered: boolean;
      }>('/admin/orgs/provision', { method: 'POST', body: JSON.stringify(data) }),

    // ── Register the /ggportal slash command into a guild (founder) ──────────
    // Makes the command appear in a server the bot is in (Discord won't add it
    // automatically). Provision does this on its own; this is the manual sync,
    // and works on a server that isn't provisioned yet.
    registerGuildCommands: (guildId: string) =>
      apiFetch<{ ok: boolean; guildId: string; registered: boolean; commands: string[] }>(
        `/admin/guilds/${encodeURIComponent(guildId)}/register-commands`, { method: 'POST' },
      ),

    // ── Disconnect a server from its org (founder) ───────────────────────────
    // Clears the guild binding (frees the server for re-onboarding) and orphans
    // the org with a 90-day grace TTL — data preserved, then auto-expires.
    disconnectOrg: (orgId: string) =>
      apiFetch<{
        ok: boolean; orgId: string; freedGuildId: string | null;
        status: string; orphanedAt: number; orphanExpiresAt: number; graceDays: number;
      }>(`/admin/orgs/${encodeURIComponent(orgId)}/disconnect`, { method: 'POST' }),

    // ── Single-use bot-invite links (founder) ────────────────────────────────
    // Alternative to the static /auth/config botInviteUrl above: a minted,
    // one-time-use shortlink (24h TTL) the founder can hand a specific client
    // and watch flip to "used" once they click it. Redeem itself is the public
    // GET /auth/invite/:token — never called directly from the frontend.
    mintBotInviteLink: (data: { label?: string }) =>
      apiFetch<{ token: string; url: string; label: string; issuedAt: number; expiresAt: number }>(
        '/admin/bot-invite-links', { method: 'POST', body: JSON.stringify(data) },
      ),

    // boundGuildId/boundAt (Phase 2): the exact guild that redeemed the token
    // via the OAuth callback — null until that happens. used/usedAt are kept
    // as derived convenience fields mirroring boundGuildId !== null, for any
    // consumer still keying off the boolean.
    listBotInviteLinks: () =>
      apiFetch<{
        links: Array<{
          token: string; label: string; issuedAt: number; expiresAt: number;
          boundGuildId: string | null; boundAt: number | null;
          used: boolean; usedAt: number | null;
        }>;
      }>('/admin/bot-invite-links'),

    // ── Invite a user (by Discord ID) to lead a clan ────────────────────────
    inviteLeader: (data: { discordId: string; clanName: string; message?: string }) =>
      apiFetch<{ ok: boolean; token: string; dmSent: boolean; dmFailReason: string | null; fallbackUrl: string; expiresIn: string }>(
        '/admin/invite/leader',
        { method: 'POST', body: JSON.stringify(data) },
      ),

    // ── Discord guild member browser (Phase 2) ──────────────────────────────
    // boundOrgId: the org this server is already bound to, or null if it's a
    // server the bot is in that still needs an org (an onboarding candidate).
    listGuilds: () =>
      apiFetch<{ guilds: Array<{ id: string; name: string; icon: string | null; boundOrgId: string | null }> }>('/admin/guilds'),

    listGuildMembers: (guildId: string, after?: string, limit = 100) =>
      apiFetch<{
        members: Array<{
          discordId: string;
          username: string;
          globalName: string | null;
          displayName: string;
          avatar: string | null;
          joinedAt: string;
        }>;
        nextCursor: string | null;
        total: number;
      }>(`/admin/guilds/${encodeURIComponent(guildId)}/members?limit=${limit}${after ? `&after=${encodeURIComponent(after)}` : ''}`),

    searchGuildMembers: (guildId: string, q: string) =>
      apiFetch<{ members: Array<{ discordId: string; username: string; displayName: string; avatar: string | null }> }>(
        `/admin/guilds/${encodeURIComponent(guildId)}/members/search?q=${encodeURIComponent(q)}`,
      ),

    // ── Activity Log ──────────────────────────────────────────────────────
    activityTimeline: (limit = 50, before?: string) =>
      apiFetch<ActivityRecord[]>(`/admin/activity/timeline?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ''}`),

    activityByActor: (discordId: string, limit = 50) =>
      apiFetch<ActivityRecord[]>(`/admin/activity/actor/${encodeURIComponent(discordId)}?limit=${limit}`),

    // ── Observability (org-scoped audit log) ────────────────────────────────
    orgLogs: (orgId: string, opts?: { severity?: LogSeverity; limit?: number }) =>
      apiFetch<OrgLogRecord[]>(`/admin/org-logs?orgId=${encodeURIComponent(orgId)}${opts?.severity ? `&severity=${opts.severity}` : ''}${opts?.limit ? `&limit=${opts.limit}` : ''}`),

    // ── Observatory trace view (ob-A5) — actor timeline / correlation chain /
    // global firehose / cost tiles. Founder-only; see PR description for why
    // these sit alongside the older activityTimeline/activityByActor pair.
    observatoryActor: (actorId: string, opts?: { from?: string; to?: string; limit?: number; nextToken?: string }) => {
      const params = new URLSearchParams();
      if (opts?.from) params.set('from', opts.from);
      if (opts?.to) params.set('to', opts.to);
      if (opts?.limit) params.set('limit', String(opts.limit));
      if (opts?.nextToken) params.set('nextToken', opts.nextToken);
      const qs = params.toString();
      return apiFetch<ObservatoryActorPage>(`/admin/observatory/actor/${encodeURIComponent(actorId)}${qs ? `?${qs}` : ''}`);
    },

    observatoryTrace: (correlationId: string) =>
      apiFetch<ObservatoryTracePage>(`/admin/observatory/trace/${encodeURIComponent(correlationId)}`),

    observatoryTimeline: (opts?: { day?: string; limit?: number }) => {
      const params = new URLSearchParams();
      if (opts?.day) params.set('day', opts.day);
      if (opts?.limit) params.set('limit', String(opts.limit));
      const qs = params.toString();
      return apiFetch<ObservatoryTimelinePage>(`/admin/observatory/timeline${qs ? `?${qs}` : ''}`);
    },

    observatoryCosts: (days = 30) =>
      apiFetch<ObservatoryCostsPage>(`/admin/observatory/costs?days=${days}`),

    // ── Clickstream dashboard ────────────────────────────────────────────────
    // day defaults server-side to today (UTC) when omitted.
    clickstream: (day?: string) =>
      apiFetch<ClickstreamDaySummary>(`/admin/clickstream${day ? `?day=${encodeURIComponent(day)}` : ''}`),

    // ── Founder analytics ─────────────────────────────────────────────────
    // Two halves of one picture: acquisition answers "who came in when" from
    // the consent rows; rollups answer "what did they do" from the permanent
    // daily audit aggregates. Both founder-only, both counts-not-identities
    // (bar topActors, which is purge-covered).
    observatoryAcquisition: (days = 30) =>
      apiFetch<AcquisitionSeries>(`/admin/observatory/acquisition?days=${days}`),

    observatoryRollups: (days = 30) =>
      apiFetch<RollupDay[]>(`/admin/observatory/rollups?days=${days}`),

    // ── Metrics ───────────────────────────────────────────────────────────
    metrics: () => apiFetch<PlatformMetrics>('/admin/metrics'),

    // ── Simulation ────────────────────────────────────────────────────────
    simulateTournament: (name: string, clanCount?: number, playersPerClan?: number) =>
      apiFetch<SimulateTournamentResponse>('/admin/simulate-tournament', {
        method: 'POST',
        body: JSON.stringify({ name, clanCount, playersPerClan }),
      }),

    deleteSimulation: (compId: string) =>
      apiFetch<{ ok: boolean }>(`/admin/simulate-tournament/${encodeURIComponent(compId)}`, { method: 'DELETE' }),
  },

  // ── Credits ──────────────────────────────────────────────────────────────
  orgCredits: (orgId: string) =>
    apiFetch<OrgCreditsResponse>(`/orgs/${encodeURIComponent(orgId)}/credits`),

  orgCreditTransactions: (orgId: string) =>
    apiFetch<CreditTransaction[]>(`/orgs/${encodeURIComponent(orgId)}/credits/transactions`),

  creditCheckout: (orgId: string, pack: CreditPack, email: string) =>
    apiFetch<CreditCheckoutResponse>(`/orgs/${encodeURIComponent(orgId)}/credits/checkout`, {
      method: 'POST',
      body: JSON.stringify({ pack, email }),
    }),

  // ── Assets ──────────────────────────────────────────────────────────────
  listAssets: (scope: AssetOwnerType, ownerId?: string) =>
    apiFetch<{ assets: Asset[] }>(`/assets?scope=${scope}${ownerId ? `&ownerId=${encodeURIComponent(ownerId)}` : ''}`),

  getAssetUploadUrl: (params: { ownerType: AssetOwnerType; ownerId: string; assetType: AssetType; contentType: string; fileSize: number; group?: string }) =>
    apiFetch<{ uploadUrl: string; assetId: string; s3Key: string }>(
      `/assets/upload-url?ownerType=${params.ownerType}&ownerId=${encodeURIComponent(params.ownerId)}&assetType=${params.assetType}&contentType=${encodeURIComponent(params.contentType)}&fileSize=${params.fileSize}${params.group ? `&group=${encodeURIComponent(params.group)}` : ''}`
    ),

  updateAsset: (assetId: string, data: { name?: string; group?: string; objectPosition?: string }) =>
    apiFetch<{ ok: boolean }>(`/assets/${encodeURIComponent(assetId)}`, { method: 'PATCH', body: JSON.stringify(data) }),

  registerAsset: (data: { assetId: string; s3Key: string; ownerType: AssetOwnerType; ownerId: string; assetType: AssetType; contentType: string; sizeBytes: number; name?: string; group?: string }) =>
    apiFetch<{ ok: boolean; assetId: string }>('/assets', { method: 'POST', body: JSON.stringify(data) }),

  deleteAsset: (assetId: string) =>
    apiFetch<{ ok: boolean }>(`/assets/${encodeURIComponent(assetId)}`, { method: 'DELETE' }),

  allocateAsset: (assetId: string, targetType: 'org' | 'clan', targetId: string) =>
    apiFetch<{ ok: boolean }>(`/assets/${encodeURIComponent(assetId)}/allocate`, {
      method: 'POST', body: JSON.stringify({ targetType, targetId }),
    }),

  updateCardSettings: (data: { avatarAssetId?: string; bannerAssetId?: string; cardColor?: string }) =>
    apiFetch<{ ok: boolean }>('/profile/card', { method: 'PATCH', body: JSON.stringify(data) }),

  // ── My Card (Phase 2) ────────────────────────────────────────────────────
  // GET /me/card returns { initialised, ...fields, stats } — `initialised: false`
  // means the user hasn't completed POPIA consent yet (no PROFILE row).
  myCard: () => apiFetch<MyCardResponse>('/me/card'),

  // PUT /me/card — only the keys you submit are written (field-level update).
  // Pass `null` to clear an optional field (e.g. `description: null`).
  updateMyCard: (data: MyCardUpdateInput) =>
    apiFetch<{ ok: boolean }>('/me/card', { method: 'PUT', body: JSON.stringify(data) }),

  // ── My Org (Phase 3) ─────────────────────────────────────────────────────
  // GET /me/orgs — list user's org + comp memberships (already wired Phase 1).
  myOrgs: () =>
    apiFetch<{
      orgMemberships: OrgMembershipSummary[];
      competitionMemberships: CompetitionMembershipSummary[];
    }>('/me/orgs'),

  // POST /me/orgs/<id>/leave — leave an org. Returns 409 if user is the
  // ORG_OWNER (must transfer ownership first).
  leaveOrg: (orgId: string) =>
    apiFetch<{ ok: boolean; orgId: string }>(`/me/orgs/${encodeURIComponent(orgId)}/leave`, {
      method: 'POST',
    }),

  // POST /me/orgs/create — founders get an immediate-create JSON
  // ({ id, name, role }); non-founders get 202 + a request_pending payload
  // (Phase 3.5 — formal OrgCreationRequest row written server-side, founders
  // review via the inbox + Discord DM). Caller branches on `'status' in res`.
  createMyOrg: (data: { name: string; billingEmail?: string; primaryGuildId?: string; slug?: string; reason?: string }) =>
    apiFetch<CreateMyOrgResponse>('/me/orgs/create', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // GET /me/org-creation-requests — own pending + historical (Phase 3.5).
  myOrgCreationRequests: () =>
    apiFetch<{
      requests: MyOrgCreationRequestSummary[];
      pendingCount: number;
      pendingLimit: number;
    }>('/me/org-creation-requests'),

  // GET /orgs/discover — bounded list of orgs flagged publicListed (Phase 3.5).
  discoverOrgs: (cursor?: string) =>
    apiFetch<{
      orgs: DiscoverableOrgSummary[];
      nextCursor: string | null;
      limit: number;
    }>(`/orgs/discover${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),

  // GET /clans/discover — bounded list of clans accepting public applications
  // (Phase 3.5). Cursor-paginated sibling to /clans/browse.
  discoverClans: (cursor?: string) =>
    apiFetch<{
      clans: DiscoverableClanSummary[];
      nextCursor: string | null;
      limit: number;
    }>(`/clans/discover${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),

  // GET /me/role-requests — user's own pending + historical role-requests
  // (Phase 1 read-only; populated by Phase 4's submit/withdraw).
  myRoleRequests: () =>
    apiFetch<{
      requests: MyRoleRequestSummary[];
      pendingCount: number;
      pendingLimit: number;
    }>('/me/role-requests'),

  // POST /orgs/<id>/role-requests — submit a role-request (Phase 4). 429 with
  // a JSON-encoded body when the 10-pending cap or 24h cooldown blocks the
  // submission; the apiFetch error message will contain the JSON which the
  // caller can parse.
  submitRoleRequest: (orgId: string, input: SubmitRoleRequestInput) =>
    apiFetch<SubmitRoleRequestResponse>(`/orgs/${encodeURIComponent(orgId)}/role-requests`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // POST /me/role-requests/<requestId>/withdraw — requester self-resolves a
  // PENDING request (Phase 4).
  withdrawRoleRequest: (requestId: string) =>
    apiFetch<{
      requestId: string;
      orgId: string;
      status: RoleRequestStatus;
      resolvedAt: number;
      resolvedBy: string;
    }>(`/me/role-requests/${encodeURIComponent(requestId)}/withdraw`, {
      method: 'POST',
    }),

  // GET /admin/org-creation-requests — founder inbox.
  listOrgCreationRequests: (status: 'PENDING' | 'APPROVED' | 'DENIED' | 'WITHDRAWN' | 'all' = 'PENDING') =>
    apiFetch<{ requests: OrgCreationRequestInboxRow[]; pendingCount: number; totalReturned: number }>(
      `/admin/org-creation-requests?status=${encodeURIComponent(status)}`,
    ),

  // POST /admin/org-creation-requests/<id>/approve — founder approve.
  approveOrgCreationRequest: (requestId: string, resolverNote?: string) =>
    apiFetch<{
      requestId: string; orgId: string; status: 'APPROVED';
      resolvedAt: number; resolvedBy: string;
      proposedOrgName: string; proposedOrgSlug: string;
    }>(`/admin/org-creation-requests/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ resolverNote }),
    }),

  // POST /admin/org-creation-requests/<id>/deny — founder deny.
  denyOrgCreationRequest: (requestId: string, resolverNote?: string) =>
    apiFetch<{
      requestId: string; status: 'DENIED';
      resolvedAt: number; resolvedBy: string;
    }>(`/admin/org-creation-requests/${encodeURIComponent(requestId)}/deny`, {
      method: 'POST',
      body: JSON.stringify({ resolverNote }),
    }),

  // GET /orgs/<orgId>/role-requests — org-owner inbox.
  listOrgRoleRequests: (orgId: string, status: 'PENDING' | 'APPROVED' | 'DENIED' | 'WITHDRAWN' | 'all' = 'PENDING') =>
    apiFetch<{ requests: RoleRequestInboxRow[]; pendingCount: number; totalReturned: number }>(
      `/orgs/${encodeURIComponent(orgId)}/role-requests?status=${encodeURIComponent(status)}`,
    ),

  // POST /orgs/<orgId>/role-requests/<id>/approve.
  approveRoleRequest: (orgId: string, requestId: string, resolverNote?: string) =>
    apiFetch<{
      requestId: string; orgId: string; status: 'APPROVED';
      grantedRole: string; resolvedAt: number; resolvedBy: string;
    }>(`/orgs/${encodeURIComponent(orgId)}/role-requests/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ resolverNote }),
    }),

  // POST /orgs/<orgId>/role-requests/<id>/deny.
  denyRoleRequest: (orgId: string, requestId: string, resolverNote?: string) =>
    apiFetch<{
      requestId: string; orgId: string; status: 'DENIED';
      resolvedAt: number; resolvedBy: string;
    }>(`/orgs/${encodeURIComponent(orgId)}/role-requests/${encodeURIComponent(requestId)}/deny`, {
      method: 'POST',
      body: JSON.stringify({ resolverNote }),
    }),

  // ── Clan-create requests (T3 — server-is-the-org) ───────────────────────────
  // POST /orgs/<orgId>/clan-create-requests — apply to create a new clan under
  // an org the caller is a member of. 409s on dup-pending (one PENDING request
  // per requester+org) or dup-name (a non-deleted clan already has that name).
  submitClanCreateRequest: (orgId: string, input: SubmitClanCreateRequestInput) =>
    apiFetch<{
      requestId: string; orgId: string; name: string;
      status: 'PENDING'; createdAt: number;
    }>(`/orgs/${encodeURIComponent(orgId)}/clan-create-requests`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // GET /me/clan-create-requests — the caller's own applications, newest first.
  myClanCreateRequests: () =>
    apiFetch<{ requests: MyClanCreateRequestSummary[] }>('/me/clan-create-requests'),

  // GET /orgs/<orgId>/clan-create-requests — org owner/admin inbox
  // (org.manage_clans). `status` defaults to PENDING server-side; pass 'ALL'
  // for the full history.
  listClanCreateRequests: (orgId: string, status: 'PENDING' | 'ALL' = 'PENDING') =>
    apiFetch<{ requests: ClanCreateRequestInboxRow[]; pendingCount: number }>(
      `/orgs/${encodeURIComponent(orgId)}/clan-create-requests?status=${encodeURIComponent(status)}`,
    ),

  // POST /orgs/<orgId>/clan-create-requests/<id>/approve — creates the clan
  // (requester becomes leader) via the same core POST /me/clan uses.
  approveClanCreateRequest: (orgId: string, requestId: string) =>
    apiFetch<{
      requestId: string; orgId: string; status: 'APPROVED';
      clanId: string; resolvedAt: number; resolvedBy: string;
    }>(`/orgs/${encodeURIComponent(orgId)}/clan-create-requests/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
    }),

  // POST /orgs/<orgId>/clan-create-requests/<id>/deny — optional reason, capped
  // 500 chars server-side.
  denyClanCreateRequest: (orgId: string, requestId: string, reason?: string) =>
    apiFetch<{
      requestId: string; orgId: string; status: 'DENIED';
      resolvedAt: number; resolvedBy: string;
    }>(`/orgs/${encodeURIComponent(orgId)}/clan-create-requests/${encodeURIComponent(requestId)}/deny`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // Org branding (white-label hook). accentColor is a 6-digit hex string
  // (e.g. '#ff731b'). null clears.
  getOrgBranding: (orgId: string) =>
    apiFetch<{ orgId: string; accentColor: string | null }>(
      `/orgs/${encodeURIComponent(orgId)}/branding`,
    ),
  updateOrgBranding: (orgId: string, accentColor: string | null) =>
    apiFetch<{ ok: boolean; orgId: string; accentColor: string | null }>(
      `/orgs/${encodeURIComponent(orgId)}/branding`,
      { method: 'PATCH', body: JSON.stringify({ accentColor }) },
    ),

  // Org-configurable /ggveto defaults (series length + per-mode map pools).
  // Owner-config surface — BOTH verbs gated to org.manage_members on the
  // backend (unlike the branding GET above). null clears a field; `defaults`
  // on the GET carries the live league pool so the frontend never hardcodes it.
  getOrgVetoConfig: (orgId: string) =>
    apiFetch<{
      orgId: string;
      vetoConfig: { bestOf?: 3 | 5; pools?: Partial<Record<VetoModeKey, string[]>> } | null;
      defaults: { bestOf: 3; modes: Array<{ key: VetoModeKey; label: string; maps: string[] }> };
    }>(`/orgs/${encodeURIComponent(orgId)}/veto-config`),
  updateOrgVetoConfig: (orgId: string, body: { bestOf?: 3 | 5 | null; pools?: Record<string, string[]> | null }) =>
    apiFetch<{
      ok: boolean;
      orgId: string;
      vetoConfig: { bestOf?: 3 | 5; pools?: Partial<Record<VetoModeKey, string[]>> } | null;
    }>(`/orgs/${encodeURIComponent(orgId)}/veto-config`, { method: 'PATCH', body: JSON.stringify(body) }),

  getOrgAssetConfig: (orgId: string) =>
    apiFetch<AssetConfig>(`/orgs/${encodeURIComponent(orgId)}/asset-config`),

  updateOrgAssetConfig: (orgId: string, storageCap: number) =>
    apiFetch<{ ok: boolean }>(`/orgs/${encodeURIComponent(orgId)}/asset-config`, {
      method: 'PATCH', body: JSON.stringify({ storageCap }),
    }),

  getPlayerCardUrl: (discordId: string) =>
    `/portal/cards/${encodeURIComponent(discordId)}?format=redirect`,

  // ── Match Card v2 ──────────────────────────────────────────────────────────
  matchCardGet: (matchId: string, compId: string) =>
    apiFetch<MatchCardV2Response>(
      `/matchcard/${encodeURIComponent(matchId)}?compId=${encodeURIComponent(compId)}`,
    ),

  /** Sign off (or withdraw) ONE side's own result rows. Server enforces who —
   *  that side's leader, the captain of the team that side fielded, or an
   *  operator on an unresponsive side's behalf (shared/matchSignoff.ts).
   *  Disagreement does NOT come through here: "something's wrong" raises a
   *  score flag via flagScore, the mechanism that already existed. */
  matchSignoff: (matchId: string, compId: string, side: 1 | 2, action: 'SIGN' | 'WITHDRAW' = 'SIGN') =>
    apiFetch<{ ok: true; side: 1 | 2; status: 'SIGNED' | 'WITHDRAWN' }>(
      `/matchcard/${encodeURIComponent(matchId)}/signoff`,
      { method: 'POST', body: JSON.stringify({ compId, side, action }) },
    ),

  matchCardInit: (matchId: string, data: MatchCardInitInput) =>
    apiFetch<{ ok: boolean; matchId: string; slotsCreated: number; cardStatus: MatchCardStatus }>(
      `/matchcard/${encodeURIComponent(matchId)}/init`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  matchCardTransition: (matchId: string, compId: string, to: MatchCardStatus, reason?: string) =>
    apiFetch<{ ok: boolean; from: MatchCardStatus; to: MatchCardStatus }>(
      `/matchcard/${encodeURIComponent(matchId)}/transition`,
      { method: 'POST', body: JSON.stringify({ compId, to, reason }) },
    ),

  matchCardForge: (matchId: string, compId: string, publicCardUrl?: string) =>
    apiFetch<{ ok: boolean; forgedCardUrl: string }>(
      `/matchcard/${encodeURIComponent(matchId)}/forge`,
      { method: 'POST', body: JSON.stringify({ compId, publicCardUrl }) },
    ),

  // Arc 3 (scoring unification) — the missing wire: derives games[]/nightWinner
  // from the OCR'd slots and writes status CONFIRMED, the SAME shape manual
  // result entry uses, so this match finally feeds computeStandings/R2/R4.
  // 409 scores_incomplete (a played game's OCR data can't produce two clean
  // team totals) or 400 series_undecided (not enough confirmed games yet) come
  // back as parseApiError-friendly structured errors — the caller's fallback
  // is manual entry via matchResult-equivalent admin tooling.
  matchCardConfirmScores: (matchId: string, compId: string) =>
    apiFetch<{
      matchId: string;
      cardsCreated: number;
      confirmedBy?: string;
      status: 'CONFIRMED';
      nightWinner: string;
      games: MatchCardGameResult[];
      message: string;
    }>(
      `/matchcard/${encodeURIComponent(matchId)}/confirm-scores`,
      { method: 'POST', body: JSON.stringify({ compId }) },
    ),

  matchCardVeto: (matchId: string, data: VetoMoveInput) =>
    apiFetch<{ ok: boolean; veto: MapVetoState; cardStatus: MatchCardStatus }>(
      `/matchcard/${encodeURIComponent(matchId)}/veto`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  matchCardVetoLock: (matchId: string, compId: string) =>
    apiFetch<{ ok: boolean; veto: MapVetoState }>(
      `/matchcard/${encodeURIComponent(matchId)}/veto/lock`,
      { method: 'POST', body: JSON.stringify({ compId, lockedBy: 'ADMIN' }) },
    ),

  // The caller's own side gets `faction`; the other side auto-inherits the
  // opposite. Locked once set (409 "Faction already locked." on re-set).
  matchCardVetoFaction: (matchId: string, data: { compId: string; faction: 'JSOC' | 'GUILD' }) =>
    apiFetch<{ ok: boolean; veto: MapVetoState }>(
      `/matchcard/${encodeURIComponent(matchId)}/veto/faction`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  matchCardComments: (matchId: string, compId: string) =>
    apiFetch<{ comments: MatchCommentRow[] }>(
      `/matchcard/${encodeURIComponent(matchId)}/comments?compId=${encodeURIComponent(compId)}`,
    ),

  matchCardComment: (matchId: string, compId: string, body: string) =>
    apiFetch<{ ok: boolean; sk: string; ts: number }>(
      `/matchcard/${encodeURIComponent(matchId)}/comment`,
      { method: 'POST', body: JSON.stringify({ compId, body }) },
    ),

  matchCardPinComment: (matchId: string, commentSk: string, compId: string, pinned: boolean) =>
    apiFetch<{ ok: boolean; pinned: boolean }>(
      `/matchcard/${encodeURIComponent(matchId)}/comment/${encodeURIComponent(commentSk)}/pin`,
      { method: 'POST', body: JSON.stringify({ compId, pinned }) },
    ),

  slotUploadUrl: (matchId: string, gameNum: number, playerId: string, compId: string, contentType: string) =>
    apiFetch<{ url: string; s3Key: string; slotSk: string }>(
      `/matchcard/${encodeURIComponent(matchId)}/slot/${gameNum}/${encodeURIComponent(playerId)}/upload-url`,
      { method: 'POST', body: JSON.stringify({ compId, contentType }) },
    ),

  slotConfirm: (matchId: string, gameNum: number, playerId: string, compId: string, s3Key: string) =>
    apiFetch<{
      ok: boolean;
      slotSk: string;
      status: SlotStatus;
      uploadedAt: number;
      proxied?: boolean;
      slotsSubmitted?: number;
      slotsExpected?: number;
      ocrAutoKicked?: boolean;
    }>(
      `/matchcard/${encodeURIComponent(matchId)}/slot/${gameNum}/${encodeURIComponent(playerId)}/confirm`,
      { method: 'POST', body: JSON.stringify({ compId, s3Key }) },
    ),

  // GOD-policy L7b — "nothing to submit" declaration. Self, own-side
  // captain/leader, or admin only (server 403s the opposing side, always).
  // 409s come back structured (parseApiError) with a human "not PENDING /
  // not LIVE" message.
  slotDeclareNothing: (matchId: string, gameNum: number, playerId: string, compId: string, reason?: string) =>
    apiFetch<{ ok: boolean; slotSk: string; status: SlotStatus; missedVia: string; declaredAt: number }>(
      `/matchcard/${encodeURIComponent(matchId)}/slot/${gameNum}/${encodeURIComponent(playerId)}/declare-nothing`,
      { method: 'POST', body: JSON.stringify({ compId, reason }) },
    ),

  // T2 — "the sub's upload IS the swap" (Lisa product ruling, 2026-07-25).
  // A REGISTERED clan member who wasn't fielded for this game may claim an
  // OPEN (PENDING) slot on their own side by uploading their own screenshot.
  // No playerId in the path — ctx.discordId + live clan-membership rows are
  // the server's own authority (subClaimGate/resolveSubSide in
  // matchCardV2.ts), fail-closed. replacedPlayerId is optional: omit it to
  // let the server auto-pick the first open slot (roster order) for this
  // side/game; pass it to target a specific one. Mirrors slotUploadUrl's
  // shape one-for-one, just without the playerId segment.
  subUploadUrl: (matchId: string, gameNum: number, compId: string, contentType: string, replacedPlayerId?: string) =>
    apiFetch<{ url: string; s3Key: string; slotSk: string; replacedPlayerId: string }>(
      `/matchcard/${encodeURIComponent(matchId)}/game/${gameNum}/sub-upload-url`,
      { method: 'POST', body: JSON.stringify({ compId, contentType, ...(replacedPlayerId ? { replacedPlayerId } : {}) }) },
    ),

  // Commits the reassignment: creates the sub's own slot row and retires the
  // replaced original to MISSED (missedVia: 'subbed-out'), one transaction.
  // replacedPlayerId is REQUIRED here — always the value echoed back by
  // subUploadUrl above, never re-derived client-side.
  subConfirm: (matchId: string, gameNum: number, compId: string, s3Key: string, replacedPlayerId: string) =>
    apiFetch<{
      ok: boolean;
      slotSk: string;
      status: SlotStatus;
      uploadedAt: number;
      subbedIn: boolean;
      replacedPlayerId: string;
      subbedBy: string;
      slotsSubmitted?: number;
      slotsExpected?: number;
    }>(
      `/matchcard/${encodeURIComponent(matchId)}/game/${gameNum}/sub-confirm`,
      { method: 'POST', body: JSON.stringify({ compId, s3Key, replacedPlayerId }) },
    ),

  // GOD-policy L7b — nudge the OPPOSING side for outstanding screenshots.
  // targetSide names the side BEING poked (the side short on submissions) —
  // the caller must represent the opposite side or be an admin. Rate-limited
  // server-side to one poke per poking-side per match per 12h; a 429 comes
  // back structured (parseApiError) with `nextAllowedAt`.
  matchCardPoke: (matchId: string, compId: string, targetSide: '1' | '2') =>
    apiFetch<{ ok: boolean; targetSide: '1' | '2'; targetsNotified: number; pendingCount: number; nextAllowedAt: number }>(
      `/matchcard/${encodeURIComponent(matchId)}/poke`,
      { method: 'POST', body: JSON.stringify({ compId, targetSide }) },
    ),

  // ── User-raised score flags ────────────────────────────────────────────────
  submitScoreFlag: (
    matchId: string,
    compId: string,
    data: { gameNum: number; playerKey: string; field: ScoreCellField | '*'; note?: string },
  ) =>
    apiFetch<{ ok: boolean; sk: string; status: 'OPEN'; createdAt: number }>(
      `/matchcard/${encodeURIComponent(matchId)}/score-flags`,
      { method: 'POST', body: JSON.stringify({ compId, ...data }) },
    ),

  listScoreFlags: (matchId: string, compId: string, status?: 'OPEN' | 'RESOLVED' | 'DISMISSED') => {
    const qs = new URLSearchParams({ compId });
    if (status) qs.set('status', status);
    return apiFetch<{ flags: ScoreFlagRow[]; count: number }>(
      `/matchcard/${encodeURIComponent(matchId)}/score-flags?${qs.toString()}`,
    );
  },

  resolveScoreFlag: (
    matchId: string,
    flagSk: string,
    compId: string,
    resolution: 'EDITED' | 'CONFIRMED_OCR' | 'DUPLICATE',
  ) =>
    apiFetch<{ ok: boolean; sk: string; status: 'RESOLVED'; resolvedAt: number }>(
      `/matchcard/${encodeURIComponent(matchId)}/score-flags/${encodeURIComponent(flagSk)}/resolve`,
      { method: 'POST', body: JSON.stringify({ compId, resolution }) },
    ),

  // `entries` = upcoming fixtures, soonest first (unchanged meaning).
  // `past` = matches already played, newest first — scoped to the comp's
  // CURRENTLY LIVE stage: once a stage finishes, its matches stop appearing
  // (Lisa, 2026-07-22). Optional so an older cached response still renders.
  calendar: (days = 14) =>
    apiFetch<{ entries: CalendarEntryRow[]; past?: CalendarEntryRow[] }>(`/calendar?days=${days}`),

  // ── Match Card v2 — manual cleanup (admin/leader only) ────────────────────
  cellOverride: (matchId: string, gameNum: number, playerKey: string, field: ScoreCellField, compId: string, newValue: number, reason?: string) =>
    apiFetch<{ ok: boolean; gameNum: number; playerKey: string; field: ScoreCellField; newValue: number; source: string; recordedAt: number }>(
      `/matchcard/${encodeURIComponent(matchId)}/cell/${gameNum}/${encodeURIComponent(playerKey)}/${encodeURIComponent(field)}/override`,
      { method: 'POST', body: JSON.stringify({ compId, newValue, reason }) },
    ),

  shotFlag: (matchId: string, gameNum: number, playerId: string, compId: string, flag: ShotQualityFlag, opts?: { reason?: string; remove?: boolean }) =>
    apiFetch<{ ok: boolean; slotSk: string; qualityFlags: ShotQualityFlag[]; excludedFromConsensus: boolean }>(
      `/matchcard/${encodeURIComponent(matchId)}/slot/${gameNum}/${encodeURIComponent(playerId)}/flag`,
      { method: 'POST', body: JSON.stringify({ compId, flag, reason: opts?.reason, remove: opts?.remove ?? false }) },
    ),

  matchCardOverrides: (matchId: string, compId: string) =>
    apiFetch<{ overrides: ManualOverrideRow[] }>(
      `/matchcard/${encodeURIComponent(matchId)}/overrides?compId=${encodeURIComponent(compId)}`,
    ),

  matchCardPool: (matchId: string, compId: string) =>
    apiFetch<{ entries: ScorePoolEntryRow[] }>(
      `/matchcard/${encodeURIComponent(matchId)}/pool?compId=${encodeURIComponent(compId)}`,
    ),

  // Founder-only: spawn a demo match for the OCR walkthrough
  demoSpawnMatchCard: (data?: { compName?: string; clan1Name?: string; clan2Name?: string; format?: 'BO3' | 'BO5' | 'BO7' }) =>
    apiFetch<{ ok: boolean; compId: string; matchId: string; clan1: string; clan2: string; clan1Roster: string[]; clan2Roster: string[] }>(
      `/admin/demo/spawn-match-card`,
      { method: 'POST', body: JSON.stringify(data ?? {}) },
    ),

  // Founder-only: kick the v2 OCR pipeline on every uploaded slot of a match.
  // Returns 202 immediately; poll matchCardGet to watch slots fill in.
  runOcrBatch: (matchId: string, compId: string, excludeSlotSks?: string[]) =>
    apiFetch<{ ok: boolean; status: string; shotsQueued?: number; message?: string }>(
      `/matchcard/${encodeURIComponent(matchId)}/run-ocr-batch`,
      { method: 'POST', body: JSON.stringify({ compId, excludeSlotSks }) },
    ),

  // Live activity feed for the OCR Lab (and, as of Wave 6, the real match
  // page). Pass `since` from previous response's latestSk to paginate
  // forward. Server-side TTL is 1h.
  //
  // `compId` is optional because founders bypass the check server-side, but
  // it's REQUIRED for org-owner/org-admin callers (handleOcrFeed 403s them
  // without it) — see canOperateOcrDigestion in matchCardV2.ts. The OCR Lab
  // never needed it because it's founder-only; the real match page always
  // has a compId in scope (it's in the URL), so always pass it there.
  matchCardOcrFeed: (matchId: string, since?: string, compId?: string) => {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    if (compId) params.set('compId', compId);
    const qs = params.toString();
    return apiFetch<{ events: OcrFeedEvent[]; latestSk: string | null }>(
      `/matchcard/${encodeURIComponent(matchId)}/ocrfeed${qs ? `?${qs}` : ''}`,
    );
  },

  // Signed read URLs for a slot's screenshot (original + post-enhancement)
  // plus the slot's own quality signals. Drives the OCR-Lab per-shot drawer
  // AND the real match page's inspect drawer (SlotInspectModal).
  //
  // `compId` is optional because founders/admin bypass the check
  // server-side, but it's REQUIRED for org-manager/comp-manager callers
  // (handleSlotImageUrl 403s them without it) — same shape as
  // matchCardOcrFeed above. The OCR Lab never needed it (founder-only
  // caller); the real match page always has a compId in scope, so always
  // pass it there (see MatchCardLive.tsx's SlotInspectModal).
  slotImageUrl: (matchId: string, gameNum: number, playerId: string, compId?: string) => {
    const qs = compId ? `?compId=${encodeURIComponent(compId)}` : '';
    return apiFetch<SlotImageUrlResponse>(
      `/matchcard/${encodeURIComponent(matchId)}/slot/${gameNum}/${encodeURIComponent(playerId)}/image-url${qs}`,
    );
  },

  // Founder-only: KNOWLEDGE#NAME_SNAP rules — admin-curated corrections
  // that pin OCR'd raw player strings to roster gamertags. Phase 1 of the
  // adapt-without-retraining loop.
  knowledgeListNameSnaps: (compId: string) =>
    apiFetch<{ rules: KnowledgeNameSnapRule[] }>(
      `/matchcard/comp/${encodeURIComponent(compId)}/knowledge/name-snap`,
    ),

  knowledgeAddNameSnap: (compId: string, fromRaw: string, toName: string) =>
    apiFetch<{ ok: true; fromKey: string; sk: string }>(
      `/matchcard/comp/${encodeURIComponent(compId)}/knowledge/name-snap`,
      { method: 'POST', body: JSON.stringify({ fromRaw, toName }) },
    ),

  knowledgeDeleteNameSnap: (compId: string, fromKey: string) =>
    apiFetch<{ ok: true }>(
      `/matchcard/comp/${encodeURIComponent(compId)}/knowledge/name-snap/${encodeURIComponent(fromKey)}`,
      { method: 'DELETE' },
    ),

  // One-click demo: copies pre-named fixture screenshots from S3 into the
  // spawned match's slots. Returns counts; caller still triggers runOcrBatch.
  loadDemoFixture: (matchId: string, compId: string) =>
    apiFetch<{ ok: boolean; copied: number; unmatched: number; totalFixtures: number }>(
      `/admin/demo/load-fixture`,
      { method: 'POST', body: JSON.stringify({ matchId, compId }) },
    ),

  // ── Org Clan Affiliation ──────────────────────────────────────────────────

  /** GET /orgs/:id/clans — member clans + pending requests + pending invites. */
  orgClans: (orgId: string) =>
    apiFetch<OrgClansResponse>(`/orgs/${encodeURIComponent(orgId)}/clans`),

  /** POST /orgs/:id/clan-invites — org owner/admin invites a clan by name or id. */
  inviteClanToOrg: (orgId: string, clanId: string) =>
    apiFetch<{ ok: boolean; orgId: string; clanId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/clan-invites`,
      { method: 'POST', body: JSON.stringify({ clanId }) },
    ),

  /** GET /clans/:name/org-invites — list pending org invites for this clan (leader only). */
  clanOrgInvites: (clanName: string) =>
    apiFetch<{ invites: Array<{ orgId: string; orgName: string; createdBy?: string; createdAt?: string }> }>(
      `/clans/${encodeURIComponent(clanName)}/org-invites`
    ),

  /** POST /clans/:name/org-invites/:orgId/accept — clan leader accepts an org invite. */
  acceptOrgInvite: (clanName: string, orgId: string) =>
    apiFetch<{ ok: boolean; clanId: string; orgId: string }>(
      `/clans/${encodeURIComponent(clanName)}/org-invites/${encodeURIComponent(orgId)}/accept`,
      { method: 'POST' },
    ),

  /** POST /clans/:name/org-invites/:orgId/decline — clan leader declines an org invite. */
  declineOrgInvite: (clanName: string, orgId: string) =>
    apiFetch<{ ok: boolean; clanId: string; orgId: string; declined: boolean }>(
      `/clans/${encodeURIComponent(clanName)}/org-invites/${encodeURIComponent(orgId)}/decline`,
      { method: 'POST' },
    ),

  /** POST /clans/:name/join-org — clan leader requests to join an org. */
  clanRequestJoinOrg: (clanName: string, orgId: string) =>
    apiFetch<{ ok: boolean; orgId: string; clanId: string }>(
      `/clans/${encodeURIComponent(clanName)}/join-org`,
      { method: 'POST', body: JSON.stringify({ orgId }) },
    ),

  /** POST /orgs/:id/clan-requests/:clanId/approve — org admin approves a clan join request. */
  approveClanRequest: (orgId: string, clanId: string) =>
    apiFetch<{ ok: boolean }>(
      `/orgs/${encodeURIComponent(orgId)}/clan-requests/${encodeURIComponent(clanId)}/approve`,
      { method: 'POST' },
    ),

  /** POST /orgs/:id/clan-requests/:clanId/deny — org admin denies a clan join request. */
  denyClanRequest: (orgId: string, clanId: string) =>
    apiFetch<{ ok: boolean }>(
      `/orgs/${encodeURIComponent(orgId)}/clan-requests/${encodeURIComponent(clanId)}/deny`,
      { method: 'POST' },
    ),

  /** POST /orgs/:id/clans/:clanId/remove — org admin removes a clan affiliation. */
  removeClanFromOrg: (orgId: string, clanId: string) =>
    apiFetch<{ ok: boolean; clanId: string; orgId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/clans/${encodeURIComponent(clanId)}/remove`,
      { method: 'POST' },
    ),

  /**
   * POST /presence/heartbeat — combined "who else is here" heartbeat + read.
   * resourceKey travels in the body (never the URL) since comp/org ids are
   * PREFIXED ('COMP#'/'ORG#') and a raw '#' breaks URL paths. One call both
   * upserts the caller's own presence row and returns current co-viewers
   * (server has already applied the read-time freshness filter and excluded
   * the caller). See lib/usePresence.ts for the polling hook.
   */
  presenceHeartbeat: (resourceKey: string) =>
    apiFetch<{ others: PresenceViewer[] }>('/presence/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ resourceKey }),
    }),
};

export interface OcrFeedEvent {
  sk: string;
  ts: number;
  event: OcrProgressEvent;
}

export interface KnowledgeNameSnapRule {
  fromKey: string;
  fromRaw: string;
  toName: string;
  addedBy: string;
  addedAt: number;
  hitCount: number;
}

export interface SlotImageUrlResponse {
  s3Key: string;
  originalUrl: string;
  processedUrl: string;
  expiresAt: number;
  quality: number | null;
  qualityFlags: ShotQualityFlag[];
  ocrConfidence: number | null;
  ocrTier: string | null;
  processingPhase: string | null;
  phaseDetail: string | null;
  parsedScoreboard: unknown;
  uploaderStats: UploaderStats | null;
  flaggedReason: string | null;
  excludedFromConsensus: boolean;
}

export interface UploaderStats {
  gamertag?: string;
  title?: string;
  eliminations?: number;
  deaths?: number;
  elimDeathRatio?: number;
  objectiveScore?: number;
  extras?: Array<{ label: string; value: number }>;
}

export type OcrProgressEvent =
  | { kind: 'BATCH_STARTED'; shotsTotal: number }
  | { kind: 'SHOT_ENHANCED'; shotId: string; sizeBeforeKb: number; sizeAfterKb: number; ms: number }
  | { kind: 'SHOT_QUALITY'; shotId: string; quality: number; flags: string[] }
  | { kind: 'SHOT_STAGE1_START'; shotId: string }
  | { kind: 'SHOT_STAGE1_DONE'; shotId: string; ms: number; tokensIn: number; tokensOut: number; rowsParsed: number; mode?: string; mapName?: string }
  // W-D fix 2: retrying:true marks the interim signal fired before a bounded
  // in-process Stage 1 retry — omitted/falsy means this is the final failure
  // for the shot. Not rendered distinctly today (see formatEvent in
  // OcrActivityFeedPanel.tsx / MatchCardDemo.tsx) — additive/optional only,
  // typed here so the shape stays honest with src/shared/ocrPipelineV2.ts.
  | { kind: 'SHOT_STAGE1_ERROR'; shotId: string; error: string; retrying?: boolean }
  // W-D fix 3: sanityViolations carries deterministic row-sanity findings
  // (rowSanityCheck.ts), always present (empty when clean). Not rendered
  // distinctly today — additive/optional only, see note above.
  | { kind: 'SHOT_NAMES_SNAPPED'; shotId: string; snapsApplied: number; sanityViolations?: unknown[] }
  // W-C: fires once per shot when the batch was run with roster data
  // available — names read off the scoreboard that matched neither clan
  // roster (the "stranger" case) and submitting-roster members who never
  // showed up on this shot's board.
  | { kind: 'SHOT_ROSTER_CHECKED'; shotId: string; unexpectedNames: string[]; missingRosterMembers: string[] }
  | { kind: 'CONSENSUS_GAME_DONE'; gameNum: number; cellsAgreed: number; cellsDisputed: number }
  | { kind: 'STAGE2_QUEUED'; cellsCount: number }
  | { kind: 'STAGE2_CELL_RESOLVED'; gameNum: number; playerKey: string; field: string; before: number; after: number; ms: number }
  // W-A defect 2: reason is set when the cell was kept because Stage 2
  // itself errored (crop failure, Bedrock throttling) rather than because
  // the re-read simply agreed with Stage 1 — undefined for the ordinary keep.
  | { kind: 'STAGE2_CELL_KEPT'; gameNum: number; playerKey: string; field: string; value: number; reason?: string }
  | { kind: 'BATCH_COMPLETE'; totalMs: number; estimatedUsd: number; stage1Calls: number; stage2Calls: number }
  // W11: emitted by ocrBatchRunner instead of any per-shot progress when it
  // routes the whole match to MANUAL (no parent org, or org token pool
  // depleted) — MANUAL never touches a SLOT row, so without this event the
  // feed (and any status line watching it) has no signal the run ended.
  | { kind: 'MANUAL_ROUTED'; reason: 'no_parent_org' | 'insufficient_credits' | 'ocr_gate_unavailable'; message: string }
  // W-A defect 1/4: an uncaught batch exception, the no-actor hard-block, or
  // the daily OCR cap all route here instead of leaving the run silent.
  | { kind: 'BATCH_FAILED'; message: string }
  // W-A defect 6: one or more slot result writes failed to persist — the
  // card is held for review (DISPUTED), not auto-flipped to a clean VERIFIED.
  | { kind: 'SLOT_PERSIST_FAILED'; message: string }
  // AC2: the pipeline itself flagged the extraction as unreliable (a shot
  // disputed against the majority, a Stage-2 reject, a roster mismatch, a
  // row-sanity violation, or a consensus cell Stage 2 never rescued) — the
  // card is routed to DISPUTED instead of a clean VERIFIED even though every
  // slot write landed cleanly (ocrBatchRunner/index.ts BatchTerminalFeedKind).
  // THIS is the kind that was missing a formatEvent case entirely — no
  // default branch either — so the switch fell through and formatEvent
  // implicitly returned undefined, crashing the destructure at the call site
  // with "Cannot destructure property 'line' of 'ue(...)' as it is
  // undefined" (lab-crash-fix root cause).
  | { kind: 'QUALITY_DISPUTED'; message: string }
  // Roster-integrity flag surfacing (Lisa product ruling, 2026-07-21) —
  // OBSERVE-ONLY. Fires once per processed slot whose SLOT row carried
  // notRostered/notRosteredWho at submission time (matchCardV2.ts's
  // handleSlotConfirm/handleDeclareNothing, stamped when the live
  // clan-membership check fails — see shared/types.ts's
  // SubmissionSlot.notRostered doc comment). Not part of the pipeline's own
  // progress stream — ocrBatchRunner/index.ts checks the SLOT row directly
  // and emits this before dispatching to the pipeline. Never changes a
  // card/slot status; purely so an admin adjudicating a DISPUTED card in the
  // OCR lab can see that a submitter had left the clan.
  | { kind: 'SUBMITTER_NOT_ROSTERED'; shotId: string; playerName: string; gameNum: number; notRosteredWho: 'submitter' | 'slotOwner' | 'both'; message: string };
